import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AgentSessionHistoryView } from "./discovery/AgentSessionHistoryView";
import {
  Upload,
  Database,
  ShieldCheck,
  Eye,
  Play,
  Download,
  ChevronDown,
  AlertTriangle,
  CheckCircle2,
  Info,
  ArrowLeft,
  ArrowRight,
  Layers,
  Search,
  EyeOff,
  Shuffle as ShuffleIcon,
  UserX,
  Fingerprint,
  ScanEye,
  CalendarClock,
  Ban,
  Lock,
  TextCursorInput,
  ArrowUpDown,
  ChevronRight,
  FileSpreadsheet,
  RefreshCw,
  Table2,
  Sheet,
  Save,
  FolderOpen,
  Code,
  X,
  Trash2,
  FileCode,
  Cloud,
  Snowflake,
  Server,
  Zap,
  Settings,
  TestTube2,
  FileText,
  Link2,
  Copy,
  Filter,
  Sparkles,
  ClipboardCheck,
  GripVertical,
  Wand2,
  ListChecks,
  SquareCheck,
  Loader2,
} from "lucide-react";
import { api } from "../api";
import type {
  MaskingTechnique,
  MaskingColumnAnalysis,
  MaskingUploadResponse,
  MaskingDbTablesResponse,
  MaskingColumnConfig,
  MaskingPreviewResponse,
  MaskingExecuteResponse,
  MaskingDbConnectParams,
  MaskingSheetInfo,
  MaskingSheetProfile,
  MaskingSheetConfig,
  MaskingTableInfo,
  MaskingTableConfig,
  MaskingRelationshipsResponse,
  SavedMaskingConfig,
  DateLink,
  LLMTokenInfo,
  LLMTokenEstimate,
} from "../api";
import GreenTendrils from "./GreenTendrils";
import {
  ConnectDatabaseDialog,
  type ConnectResult,
  type DbConnectFormValues,
} from "./data-source/ConnectDatabaseDialog";

/* ── Human-readable labels for fake_type choices ── */
const FAKE_TYPE_LABELS: Record<string, string> = {
  auto: "Auto-Detect (from column name)",
  full_name: "Full Name (e.g., John Smith)",
  first_name: "First Name (e.g., James)",
  last_name: "Last Name (e.g., Garcia)",
  email: "Email Address (e.g., john.smith@example.com)",
  phone: "Phone Number (e.g., +1-555-123-4567)",
  ssn: "SSN (e.g., 123-45-6789)",
  address: "Street Address (e.g., 1234 Main St, NYC)",
  city: "City (e.g., New York)",
  state: "US State (e.g., Texas)",
  country: "Country (e.g., United States)",
  zip: "ZIP Code (e.g., 90210)",
  company: "Company Name (e.g., Acme Corp)",
  state_agency: "State/Gov Agency (e.g., TX Dept of Info Resources)",
  department: "Department (e.g., Engineering)",
  job_title: "Job Title (e.g., Software Engineer)",
  account_id: "Account ID (e.g., ACC-123456)",
  project_name: "Project Name (e.g., Project Alpha)",
};

/* ── Dtype → applicable techniques map ── */
const DTYPE_TECHNIQUE_MAP: Record<string, string[]> = {
  // Date/time types → prefer date-specific + universals
  date: [
    "date_shift",
    "redact",
    "nullify",
    "hash",
    "shuffle",
    "randomize",
    "aes_encrypt",
  ],
  datetime: [
    "date_shift",
    "redact",
    "nullify",
    "hash",
    "shuffle",
    "randomize",
    "aes_encrypt",
  ],
  datetime64: [
    "date_shift",
    "redact",
    "nullify",
    "hash",
    "shuffle",
    "randomize",
    "aes_encrypt",
  ],
  timestamp: [
    "date_shift",
    "redact",
    "nullify",
    "hash",
    "shuffle",
    "randomize",
    "aes_encrypt",
  ],
  // Numeric types → numeric-relevant techniques
  int64: [
    "randomize",
    "generalize",
    "shuffle",
    "redact",
    "nullify",
    "hash",
    "aes_encrypt",
  ],
  int32: [
    "randomize",
    "generalize",
    "shuffle",
    "redact",
    "nullify",
    "hash",
    "aes_encrypt",
  ],
  float64: [
    "randomize",
    "generalize",
    "shuffle",
    "redact",
    "nullify",
    "hash",
    "aes_encrypt",
  ],
  float32: [
    "randomize",
    "generalize",
    "shuffle",
    "redact",
    "nullify",
    "hash",
    "aes_encrypt",
  ],
  number: [
    "randomize",
    "generalize",
    "shuffle",
    "redact",
    "nullify",
    "hash",
    "aes_encrypt",
  ],
  // String / object / categorical → all text techniques
  object: [
    "fake_substitute",
    "redact",
    "hash",
    "partial_mask",
    "shuffle",
    "randomize",
    "nullify",
    "generalize",
    "aes_encrypt",
    "format_preserving",
    "date_shift",
  ],
  string: [
    "fake_substitute",
    "redact",
    "hash",
    "partial_mask",
    "shuffle",
    "randomize",
    "nullify",
    "generalize",
    "aes_encrypt",
    "format_preserving",
  ],
  category: [
    "fake_substitute",
    "redact",
    "hash",
    "shuffle",
    "randomize",
    "nullify",
    "generalize",
    "aes_encrypt",
  ],
  // Boolean
  bool: ["shuffle", "randomize", "redact", "nullify"],
  boolean: ["shuffle", "randomize", "redact", "nullify"],
};

/** Get applicable technique IDs for a given dtype, or all if unknown */
function getTechniquesForDtype(dtype: string): string[] | null {
  const d = dtype.toLowerCase().replace(/[^a-z0-9]/g, "");
  // Try exact match first
  for (const [key, techs] of Object.entries(DTYPE_TECHNIQUE_MAP)) {
    if (d === key.replace(/[^a-z0-9]/g, "")) return techs;
  }
  // Try substring match
  for (const [key, techs] of Object.entries(DTYPE_TECHNIQUE_MAP)) {
    if (d.includes(key.replace(/[^a-z0-9]/g, ""))) return techs;
  }
  return null; // unknown dtype → show all
}

/* ── Icon map ─ maps technique icon string to lucide component ── */
const TECHNIQUE_ICONS: Record<string, React.ReactNode> = {
  EyeOff: <EyeOff size={14} />,
  Shuffle: <ShuffleIcon size={14} />,
  ArrowUpDown: <ArrowUpDown size={14} />,
  UserX: <UserX size={14} />,
  Fingerprint: <Fingerprint size={14} />,
  ScanEye: <ScanEye size={14} />,
  CalendarClock: <CalendarClock size={14} />,
  Ban: <Ban size={14} />,
  Layers: <Layers size={14} />,
  Lock: <Lock size={14} />,
  TextCursorInput: <TextCursorInput size={14} />,
};

/* ── Sensitivity badge colors ── */
function sensitivityBadge(level: string) {
  const map: Record<string, { bg: string; text: string; label: string }> = {
    high: {
      bg: "bg-red-500/15 border-red-500/30",
      text: "text-red-400",
      label: "HIGH",
    },
    medium: {
      bg: "bg-yellow-500/15 border-yellow-500/30",
      text: "text-yellow-400",
      label: "MEDIUM",
    },
    low: {
      bg: "bg-blue-500/15 border-blue-500/30",
      text: "text-blue-400",
      label: "LOW",
    },
    none: {
      bg: "bg-white/5 border-white/10",
      text: "text-white/50",
      label: "NONE",
    },
  };
  const s = map[level] || map.none;
  return (
    <span
      data-testid="masking-sensitivity-badge"
      data-sensitivity={s.label.toLowerCase()}
      className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-bold border ${s.bg} ${s.text}`}
    >
      {s.label}
    </span>
  );
}

/* ── Step indicator (dynamic) ── */
function StepIndicator({
  steps,
  current,
}: {
  steps: { id: number; label: string; icon: React.ReactNode }[];
  current: number;
}) {
  return (
    <div className="flex items-center gap-1">
      {steps.map((step, i) => (
        <div key={step.id} className="flex items-center">
          <div
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
              step.id === current
                ? "bg-brand-500/20 text-brand-500 border border-brand-500/30"
                : step.id < current
                  ? "bg-green-500/10 text-green-400"
                  : "bg-surface-200 text-white/50"
            }`}
          >
            {step.id < current ? <CheckCircle2 size={12} /> : step.icon}
            <span className="hidden sm:inline">{step.label}</span>
          </div>
          {i < steps.length - 1 && (
            <ChevronRight size={12} className="text-white/50 mx-1" />
          )}
        </div>
      ))}
    </div>
  );
}

/* ── Main Component ── */
interface Props {
  onBack: () => void;
  projectId?: string;
  resumeSessionId?: string;
}

export default function DataMaskingView({
  onBack,
  projectId,
  resumeSessionId: _resumeSessionId,
}: Props) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Source
  const [sourceType, setSourceType] = useState<"file" | "db">("file");
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [uploadResponse, setUploadResponse] =
    useState<MaskingUploadResponse | null>(null);

  // Multi-sheet state
  const [isMultiSheet, setIsMultiSheet] = useState(false);
  const [sheetInfoList, setSheetInfoList] = useState<MaskingSheetInfo[]>([]);
  const [selectedSheets, setSelectedSheets] = useState<Set<string>>(new Set());
  const [sheetProfiles, setSheetProfiles] = useState<MaskingSheetProfile[]>([]);
  const [activeSheetTab, setActiveSheetTab] = useState<string>("");

  // Per-sheet column configs: Map<sheetName, Map<column, config>>
  const [sheetColumnConfigs, setSheetColumnConfigs] = useState<
    Map<string, Map<string, MaskingColumnConfig>>
  >(new Map());

  // Pass-through sheets: included in output without masking
  const [passThroughSheets, setPassThroughSheets] = useState<Set<string>>(
    new Set(),
  );

  // Single-sheet column configs (non-Excel / single-sheet Excel / DB)
  const [columnConfigs, setColumnConfigs] = useState<
    Map<string, MaskingColumnConfig>
  >(new Map());

  // ── Multi-table state (#603) — the DB counterpart to multi-sheet ──
  const [isMultiTable, setIsMultiTable] = useState(false);
  const [tableProfiles, setTableProfiles] = useState<MaskingTableInfo[]>([]);
  const [activeTableTab, setActiveTableTab] = useState<string>("");
  // Per-table column configs: Map<tableName, Map<column, config>>
  const [tableColumnConfigs, setTableColumnConfigs] = useState<
    Map<string, Map<string, MaskingColumnConfig>>
  >(new Map());
  const [relationships, setRelationships] =
    useState<MaskingRelationshipsResponse | null>(null);
  const [relationshipsLoading, setRelationshipsLoading] = useState(false);

  // DB connect state
  const [dbParams, setDbParams] = useState<MaskingDbConnectParams>({
    db_type: "postgresql",
    host: "",
    port: 5432,
    database: "",
    schema_name: "public",
    username: "",
    password: "",
    table: undefined,
  });
  const [dbTables, setDbTables] = useState<string[]>([]);
  const [dbTableSearch, setDbTableSearch] = useState("");
  // Multi-select for the manual-connect table list (single table_name still
  // goes through the pre-existing single-select flow below).
  const [selectedDbTables, setSelectedDbTables] = useState<Set<string>>(
    new Set(),
  );

  // Project DB connection state
  const [projectDbConfig, setProjectDbConfig] = useState<{
    id: string;
    db_type: string;
    host?: string;
    port?: number;
    database?: string;
    schema_name?: string;
    username?: string;
    has_password: boolean;
    account?: string;
    warehouse?: string;
    role?: string;
  } | null>(null);
  const [projectDbMode, setProjectDbMode] = useState<"project" | "manual">(
    "project",
  );
  const [projectDbTables, setProjectDbTables] = useState<string[]>([]);
  const [projectDbViews, setProjectDbViews] = useState<string[]>([]);
  const [projectDbLoading, setProjectDbLoading] = useState(false);
  const [selectedProjectTables, setSelectedProjectTables] = useState<
    Set<string>
  >(new Set());
  const [projectTableSearch, setProjectTableSearch] = useState("");

  // Techniques
  const [techniques, setTechniques] = useState<MaskingTechnique[]>([]);
  const [columnSearch, setColumnSearch] = useState("");

  // Preview
  const [previewData, setPreviewData] = useState<MaskingPreviewResponse | null>(
    null,
  );

  // Execute & output
  const [targetFormat, setTargetFormat] = useState("csv");
  const [targetType, setTargetType] = useState<"file" | "db">("file");
  const [tableStrategy, setTableStrategy] = useState("create_if_not_exists");
  const [targetDbParams, setTargetDbParams] = useState<MaskingDbConnectParams>({
    db_type: "postgresql",
    host: "",
    port: 5432,
    database: "",
    schema_name: "public",
    username: "",
    password: "",
    table: "masked_data",
  });
  // True once the target form has been pre-filled from the project's saved
  // (usually read-only) source connection — used to warn that writes may
  // fail on a permissions error rather than let that surface as a raw
  // "insufficient privileges" driver exception with no context.
  const [
    targetPrefilledFromProjectSource,
    setTargetPrefilledFromProjectSource,
  ] = useState(false);
  const [executeResult, setExecuteResult] =
    useState<MaskingExecuteResponse | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Save config state
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [saveConfigName, setSaveConfigName] = useState("");
  const [saveConfigDesc, setSaveConfigDesc] = useState("");
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null);
  const [saveMode, setSaveMode] = useState<"new" | "update">("new");
  const [selectedParentConfig, setSelectedParentConfig] =
    useState<SavedMaskingConfig | null>(null);
  const [existingConfigsForSave, setExistingConfigsForSave] = useState<
    SavedMaskingConfig[]
  >([]);

  // Load config state
  const [showLoadModal, setShowLoadModal] = useState(false);
  const [savedConfigs, setSavedConfigs] = useState<SavedMaskingConfig[]>([]);
  const [loadingConfigs, setLoadingConfigs] = useState(false);

  // Pending config (loaded before file upload)
  const [pendingConfig, setPendingConfig] = useState<SavedMaskingConfig | null>(
    null,
  );
  const pendingConfigRef = useRef<SavedMaskingConfig | null>(null);
  useEffect(() => {
    pendingConfigRef.current = pendingConfig;
  }, [pendingConfig]);

  // Script generation state
  const [showScriptModal, setShowScriptModal] = useState(false);
  const [scriptLanguage, setScriptLanguage] = useState<
    "pandas" | "pyspark" | "snowpark"
  >("pandas");
  const [scriptTarget, setScriptTarget] = useState<
    "ec2" | "lambda" | "snowflake"
  >("ec2");
  const [scriptGenerating, setScriptGenerating] = useState(false);
  const [scriptError, setScriptError] = useState<string | null>(null);
  // Script gen — Error handling
  const [scriptErrorStrategy, setScriptErrorStrategy] = useState<
    "fail_fast" | "log_continue"
  >("fail_fast");
  const [scriptMaxErrors, setScriptMaxErrors] = useState(0);
  // Script gen — Logging
  const [scriptLogLevel, setScriptLogLevel] = useState<
    "DEBUG" | "INFO" | "WARNING"
  >("INFO");
  const [scriptLogOutput, setScriptLogOutput] = useState<
    "console" | "file" | "both"
  >("console");
  const [scriptIncludeSummary, setScriptIncludeSummary] = useState(true);
  // Script gen — Validation
  const [scriptPreValidation, setScriptPreValidation] = useState(true);
  const [scriptPostValidation, setScriptPostValidation] = useState(true);
  const [scriptNullHandling, setScriptNullHandling] = useState<
    "skip" | "placeholder" | "error"
  >("skip");
  // Script gen — Testing
  const [scriptIncludeTests, setScriptIncludeTests] = useState(false);
  const [scriptIncludeSampleData, setScriptIncludeSampleData] = useState(false);
  const [scriptIncludeTestRunner, setScriptIncludeTestRunner] = useState(false);
  // Script gen — Documentation
  const [scriptIncludeCodeReview, setScriptIncludeCodeReview] = useState(false);
  const [scriptIncludeChangelog, setScriptIncludeChangelog] = useState(false);
  // Script gen — Section visibility
  const [scriptShowAdvanced, setScriptShowAdvanced] = useState(false);

  // ── Sheet reorder (drag-and-drop) ──
  const [dragSheetIdx, setDragSheetIdx] = useState<number | null>(null);
  const [dragOverSheetIdx, setDragOverSheetIdx] = useState<number | null>(null);

  // ── Bulk selection mode ──
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkSelected, setBulkSelected] = useState<Set<string>>(new Set());
  const [showPresetsPanel, setShowPresetsPanel] = useState(false);

  // ── Lineage: deterministic consistent mapping ──
  const [lineageEnabled, setLineageEnabled] = useState(false);
  const [lineageGroups, setLineageGroups] = useState<string[][]>([]);

  // ── Date relationship preservation ──
  const [dateLinks, setDateLinks] = useState<DateLink[]>([]);
  const [showDateLinkBuilder, setShowDateLinkBuilder] = useState(false);
  const [dateLinkPrimary, setDateLinkPrimary] = useState("");
  const [dateLinkLinked, setDateLinkLinked] = useState<string[]>([]);

  // ── Script gen — production controls ──
  const [scriptLineageEnabled, setScriptLineageEnabled] = useState(false);
  const [scriptIncludeConditionalMasking, setScriptIncludeConditionalMasking] =
    useState(false);
  const [scriptIncludeUniqueness, setScriptIncludeUniqueness] = useState(false);
  const [scriptIncludeFormatValidation, setScriptIncludeFormatValidation] =
    useState(false);
  const [scriptIncludeAuditLog, setScriptIncludeAuditLog] = useState(false);
  const [scriptUserInstructions, setScriptUserInstructions] = useState("");
  const [scriptUseLLM, setScriptUseLLM] = useState(false);
  const [tokenEstimate, setTokenEstimate] = useState<LLMTokenEstimate | null>(
    null,
  );
  const [tokenEstimateLoading, setTokenEstimateLoading] = useState(false);
  const [lastTokenUsage, setLastTokenUsage] = useState<LLMTokenInfo | null>(
    null,
  );
  // Load techniques on mount
  useEffect(() => {
    api
      .maskingTechniques()
      .then((r) => setTechniques(r.techniques))
      .catch(() => {});
  }, []);

  // Dynamic steps
  const steps = isMultiSheet
    ? [
        { id: 1, label: "Source", icon: <Upload size={14} /> },
        { id: 2, label: "Sheets", icon: <Sheet size={14} /> },
        { id: 3, label: "Configure", icon: <ShieldCheck size={14} /> },
        { id: 4, label: "Review", icon: <ClipboardCheck size={14} /> },
        { id: 5, label: "Preview", icon: <Eye size={14} /> },
        { id: 6, label: "Execute", icon: <Play size={14} /> },
      ]
    : isMultiTable
      ? [
          { id: 1, label: "Source", icon: <Upload size={14} /> },
          { id: 2, label: "Relationships", icon: <Link2 size={14} /> },
          { id: 3, label: "Configure", icon: <ShieldCheck size={14} /> },
          { id: 4, label: "Review", icon: <ClipboardCheck size={14} /> },
          { id: 5, label: "Preview", icon: <Eye size={14} /> },
          { id: 6, label: "Execute", icon: <Play size={14} /> },
        ]
      : [
          { id: 1, label: "Source", icon: <Upload size={14} /> },
          { id: 2, label: "Configure", icon: <ShieldCheck size={14} /> },
          { id: 3, label: "Review", icon: <ClipboardCheck size={14} /> },
          { id: 4, label: "Preview", icon: <Eye size={14} /> },
          { id: 5, label: "Execute", icon: <Play size={14} /> },
        ];

  // Step numbers for multi-sheet vs multi-table vs single
  const isMultiEntity = isMultiSheet || isMultiTable;
  const STEP_SHEETS = isMultiSheet ? 2 : -1;
  const STEP_RELATIONSHIPS = isMultiTable ? 2 : -1;
  const STEP_CONFIGURE = isMultiEntity ? 3 : 2;
  const STEP_REVIEW = isMultiEntity ? 4 : 3;
  const STEP_PREVIEW = isMultiEntity ? 5 : 4;
  const STEP_EXECUTE = isMultiEntity ? 6 : 5;

  /* ── Helper: auto-configure columns from analysis ──
   * High-risk only by default — mixing in medium-risk columns made the
   * default selection look arbitrary across risk tiers. Users can still
   * pull in medium/low via the "PII Shield" preset or "select by
   * sensitivity" bulk action. */
  const autoConfigureColumns = (
    columns: MaskingColumnAnalysis[],
  ): Map<string, MaskingColumnConfig> => {
    const configs = new Map<string, MaskingColumnConfig>();
    columns.forEach((col) => {
      if (col.sensitivity === "high" && col.suggested_technique) {
        configs.set(col.column, {
          column: col.column,
          technique: col.suggested_technique,
          options: {},
        });
      }
    });
    return configs;
  };

  /* ── Step 1: File Upload ── */
  const handleFileUpload = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      setLoading(true);
      setError(null);
      try {
        const res = await api.maskingUpload(file, projectId);
        setSessionId(res.session_id);
        setUploadResponse(res);

        if (res.is_multi_sheet && res.sheets && res.sheets.length > 1) {
          // Multi-sheet Excel: go to sheet selection step
          setIsMultiSheet(true);
          setSheetInfoList(res.sheets);

          // If there's a pending config with sheet info, pre-select only those sheets
          const pending = pendingConfigRef.current;
          const hasSheetConfigs =
            pending?.sheet_configs && pending.sheet_configs.length > 0;
          const hasPassThrough =
            pending?.pass_through_sheets &&
            pending.pass_through_sheets.length > 0;
          if (hasSheetConfigs || hasPassThrough) {
            const savedSheetNames = new Set(
              (pending!.sheet_configs || []).map((sc) => sc.sheet),
            );
            // Also include pass-through sheets in the set of sheets to select
            if (pending!.pass_through_sheets) {
              pending!.pass_through_sheets.forEach((s) =>
                savedSheetNames.add(s),
              );
            }
            const availableSheets = new Set(res.sheets.map((s) => s.name));
            const matchedSheets = new Set<string>();
            savedSheetNames.forEach((name) => {
              if (availableSheets.has(name)) matchedSheets.add(name);
            });
            setSelectedSheets(
              matchedSheets.size > 0
                ? matchedSheets
                : new Set(res.sheets.map((s) => s.name)),
            );
            if (matchedSheets.size > 0) {
              setSaveSuccess(
                `Config "${pending!.name}" — ${matchedSheets.size} of ${savedSheetNames.size} sheets matched. Review selection and continue.`,
              );
              setTimeout(() => setSaveSuccess(null), 5000);
            }
            // Keep pending config so sheet column configs are applied after sheet loading
          } else {
            setSelectedSheets(new Set(res.sheets.map((s) => s.name)));
            setPendingConfig(null);
          }
          setStep(2); // Sheet selection step
        } else {
          // Single sheet or non-Excel
          setIsMultiSheet(false);
          if (res.columns) {
            // If there's a pending config from pre-upload load, apply it
            const pending = pendingConfigRef.current;
            if (pending) {
              const availCols = new Set(res.columns.map((c) => c.column));
              const newConfigs = new Map<string, MaskingColumnConfig>();
              let matched = 0;
              pending.column_configs.forEach((cc) => {
                if (availCols.has(cc.column)) {
                  newConfigs.set(cc.column, {
                    column: cc.column,
                    technique: cc.technique,
                    options: cc.options || {},
                  });
                  matched++;
                }
              });
              setColumnConfigs(newConfigs);
              // Restore lineage & pass-through from pending config
              setLineageEnabled(!!pending.lineage_enabled);
              setLineageGroups(pending.lineage_groups || []);
              setDateLinks(pending.date_links || []);
              if (
                pending.pass_through_sheets &&
                pending.pass_through_sheets.length > 0
              ) {
                setPassThroughSheets(new Set(pending.pass_through_sheets));
              }
              setPendingConfig(null);
              if (matched > 0) {
                setSaveSuccess(
                  `Applied config "${pending.name}" — ${matched} of ${pending.column_configs.length} columns matched`,
                );
                setTimeout(() => setSaveSuccess(null), 4000);
              }
            } else {
              setColumnConfigs(autoConfigureColumns(res.columns));
            }
          }
          setStep(2); // Configure step (step 2 in single mode)
        }
      } catch (err: any) {
        setError(err.message || "Upload failed");
      } finally {
        setLoading(false);
      }
    },
    [projectId],
  );

  /* ── Fetch project DB config if available ──
   * Reads the project's actual saved source connection (the same store
   * the Profiler agent's db_connect now keeps in sync on every connect,
   * and the one `handleFetchProjectDbTables` below already knows how to
   * use) -- NOT `apiv2.getProject()`, whose `source_db_config` field is a
   * permanently-null stub on the v2 project endpoint. */
  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    import("../apiv2").then(({ apiv2 }) => {
      apiv2
        .getSourceDB(projectId)
        .then((config) => {
          if (cancelled) return;
          if (config) {
            setProjectDbConfig({ ...config, port: config.port ?? undefined });
            setProjectDbMode("project");
          } else {
            setProjectDbMode("manual");
          }
        })
        .catch(() => {
          if (!cancelled) setProjectDbMode("manual");
        });
    });
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  /* ── Auto-connect + jump to table selection ──
   * Once the project's saved connection is confirmed, switch to the DB tab
   * and fetch its tables automatically -- so handing off from another
   * agent (e.g. the Profiler) lands the user directly on the table list
   * instead of requiring them to re-enter connection details and click
   * through "Connect Database" -> "Load Tables & Views" by hand. */
  useEffect(() => {
    if (projectId && projectDbConfig && projectDbMode === "project") {
      setSourceType("db");
      handleFetchProjectDbTables();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, projectDbConfig, projectDbMode]);

  /* ── Fetch tables/views from project DB connection (for masking) ── */
  const handleFetchProjectDbTables = useCallback(async () => {
    if (!projectId) return;
    setProjectDbLoading(true);
    setError(null);
    try {
      const res = await api.maskingProjectDbTables(projectId, "source");
      setSessionId(res.session_id);
      setProjectDbTables(res.tables || []);
      setProjectDbViews(res.views || []);
    } catch (err: unknown) {
      setError(
        err instanceof Error
          ? err.message
          : "Failed to fetch tables from project database",
      );
    } finally {
      setProjectDbLoading(false);
    }
  }, [projectId]);

  /* ── Select one or more tables from a listed DB session and load them
   * for masking (#603 item 1) — a single table keeps the pre-existing
   * single-table flow; more than one switches the session into
   * is_multi_table, the DB counterpart to is_multi_sheet. Shared by the
   * project-connection card and the manual-connect form below. ── */
  const handleTablesSelected = useCallback(
    async (tables: string[]) => {
      if (!sessionId || tables.length === 0) return;
      setLoading(true);
      setError(null);
      try {
        if (tables.length === 1) {
          const res = await api.maskingDbSelectTable(sessionId, tables[0]);
          setUploadResponse(res);
          setIsMultiTable(false);
          if (res.columns) {
            setColumnConfigs(autoConfigureColumns(res.columns));
          }
          setStep(2); // Configure step
        } else {
          const res = await api.maskingDbSelectTables(sessionId, tables);
          setIsMultiTable(true);
          setTableProfiles(res.tables);
          const newTableConfigs = new Map<
            string,
            Map<string, MaskingColumnConfig>
          >();
          res.tables.forEach((t) => {
            newTableConfigs.set(t.table_name, autoConfigureColumns(t.columns));
          });
          setTableColumnConfigs(newTableConfigs);
          setActiveTableTab(res.tables[0]?.table_name || "");
          setRelationships(null);
          setStep(STEP_RELATIONSHIPS > 0 ? STEP_RELATIONSHIPS : 2);
        }
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : "Failed to load tables");
      } finally {
        setLoading(false);
      }
    },
    [sessionId, STEP_RELATIONSHIPS],
  );

  /* ── Table selection for the project-connection card — multi-select;
   * a single selected table still goes through the existing single-table
   * flow inside handleTablesSelected. ── */
  const toggleProjectTableSelection = useCallback((table: string) => {
    setSelectedProjectTables((prev) => {
      const next = new Set(prev);
      if (next.has(table)) next.delete(table);
      else next.add(table);
      return next;
    });
  }, []);

  /* ── Step 1: DB Connect ── */
  const applyDbConnectResult = useCallback(
    (res: MaskingUploadResponse | MaskingDbTablesResponse) => {
      setSessionId(res.session_id);
      setIsMultiSheet(false);
      setIsMultiTable(false);
      setSelectedDbTables(new Set());
      if ("tables" in res && res.tables) {
        setDbTables(res.tables);
      } else {
        setUploadResponse(res as MaskingUploadResponse);
        const ur = res as MaskingUploadResponse;
        if (ur.columns) {
          setColumnConfigs(autoConfigureColumns(ur.columns));
        }
        setStep(2); // Configure
      }
    },
    [],
  );

  const handleDbConnect = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.maskingDbConnect(dbParams);
      applyDbConnectResult(res);
    } catch (err: any) {
      setError(err.message || "Connection failed");
    } finally {
      setLoading(false);
    }
  }, [dbParams, applyDbConnectResult]);

  // Additive entry point: reuse a connection ANY agent saved for this
  // project (not just the single one auto-loaded above as `projectDbConfig`
  // — see admin/project_db_config.list_connections_for_project). Kept
  // alongside the project-connection card + manual form above rather than
  // replacing them: those already implement the deliberate seamless-handoff
  // continuity (e.g. Profiler → Masking) this application is building toward
  // for all agents, plus a richer table/view browser the generic dialog
  // doesn't replicate — losing either would be a real regression, not a
  // simplification.
  const [showSharedConnectDialog, setShowSharedConnectDialog] = useState(false);

  const handleSharedDialogConnectNew = useCallback(
    async (values: DbConnectFormValues): Promise<ConnectResult> => {
      try {
        const res = await api.maskingDbConnect({
          db_type: values.db_type,
          host: values.host,
          port: parseInt(values.port, 10),
          database: values.database,
          schema_name: values.schema_name,
          username: values.username,
          password: values.password,
          account: values.account,
          warehouse: values.warehouse,
          role: values.role,
          project_id: projectId,
        });
        applyDbConnectResult(res);
        return { ok: true };
      } catch (err: any) {
        return { ok: false, error: err.message || "Connection failed" };
      }
    },
    [projectId, applyDbConnectResult],
  );

  const handleSharedDialogConnectExisting = useCallback(
    async (connectionId: string): Promise<ConnectResult> => {
      try {
        const res = await api.maskingDbConnect({
          connection_id: connectionId,
          project_id: projectId,
        });
        applyDbConnectResult(res);
        return { ok: true };
      } catch (err: any) {
        return { ok: false, error: err.message || "Connection failed" };
      }
    },
    [projectId, applyDbConnectResult],
  );

  /* ── Manual-connect table list: multi-select toggle (a single selected
   * table still goes through the pre-existing single-table flow inside
   * handleTablesSelected). ── */
  const toggleDbTableSelection = useCallback((table: string) => {
    setSelectedDbTables((prev) => {
      const next = new Set(prev);
      if (next.has(table)) next.delete(table);
      else next.add(table);
      return next;
    });
  }, []);

  /* ── Step 2 (multi-sheet): Select & load sheets ── */
  const toggleSheetSelection = (name: string) => {
    setSelectedSheets((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const handleLoadSelectedSheets = useCallback(async () => {
    if (!sessionId || selectedSheets.size === 0) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.maskingSelectSheets(
        sessionId,
        Array.from(selectedSheets),
      );
      setSheetProfiles(res.sheets);

      // Check for pending config with sheet-level column configs
      const pending = pendingConfigRef.current;
      const pendingSheetMap = new Map<string, MaskingColumnConfig[]>();
      if (pending?.sheet_configs) {
        pending.sheet_configs.forEach((sc) =>
          pendingSheetMap.set(sc.sheet, sc.column_configs),
        );
      }

      // Apply per-sheet column configs: use pending config if available, else auto-configure
      const newSheetConfigs = new Map<
        string,
        Map<string, MaskingColumnConfig>
      >();
      let totalMatched = 0;
      let totalSaved = 0;
      res.sheets.forEach((sp) => {
        const savedCols = pendingSheetMap.get(sp.sheet_name);
        if (savedCols && savedCols.length > 0) {
          const availCols = new Set(sp.columns.map((c) => c.column));
          const cfgMap = new Map<string, MaskingColumnConfig>();
          savedCols.forEach((cc) => {
            if (availCols.has(cc.column)) {
              cfgMap.set(cc.column, {
                column: cc.column,
                technique: cc.technique,
                options: cc.options || {},
              });
              totalMatched++;
            }
            totalSaved++;
          });
          newSheetConfigs.set(sp.sheet_name, cfgMap);
        } else {
          newSheetConfigs.set(sp.sheet_name, autoConfigureColumns(sp.columns));
        }
      });

      if (pending?.sheet_configs && totalMatched > 0) {
        setSaveSuccess(
          `Applied config "${pending.name}" — ${totalMatched} of ${totalSaved} column mappings matched across sheets`,
        );
        setTimeout(() => setSaveSuccess(null), 4000);
      }
      // Restore lineage & pass-through from pending config
      if (pending) {
        setLineageEnabled(!!pending.lineage_enabled);
        setLineageGroups(pending.lineage_groups || []);
        setDateLinks(pending.date_links || []);
        if (
          pending.pass_through_sheets &&
          pending.pass_through_sheets.length > 0
        ) {
          setPassThroughSheets(new Set(pending.pass_through_sheets));
        }
      }
      setPendingConfig(null);

      setSheetColumnConfigs(newSheetConfigs);
      setActiveSheetTab(res.sheets[0]?.sheet_name || "");
      setStep(STEP_CONFIGURE);
    } catch (err: any) {
      setError(err.message || "Failed to load sheets");
    } finally {
      setLoading(false);
    }
  }, [sessionId, selectedSheets, STEP_CONFIGURE]);

  /* ── Step 2 (multi-table, #603 item 2): detect PK/FK relationships
   * across the selected tables. Schema + aggregate stats only — no cell
   * values, no LLM (see relationships.py). Never blocks progressing to
   * Configure; it's evidence for the human, not a gate. ── */
  const handleDetectRelationships = useCallback(async () => {
    if (!sessionId) return;
    setRelationshipsLoading(true);
    setError(null);
    try {
      const res = await api.maskingDetectRelationships(sessionId);
      setRelationships(res);
    } catch (err: unknown) {
      setError(
        err instanceof Error ? err.message : "Failed to detect relationships",
      );
    } finally {
      setRelationshipsLoading(false);
    }
  }, [sessionId]);

  /* ── Configure: column toggle / technique / option setters ── */
  const getActiveColumns = (): MaskingColumnAnalysis[] => {
    if (isMultiSheet) {
      const sp = sheetProfiles.find((s) => s.sheet_name === activeSheetTab);
      return sp?.columns || [];
    }
    if (isMultiTable) {
      const tp = tableProfiles.find((t) => t.table_name === activeTableTab);
      return tp?.columns || [];
    }
    return uploadResponse?.columns || [];
  };

  const getActiveConfigs = (): Map<string, MaskingColumnConfig> => {
    if (isMultiSheet) {
      return sheetColumnConfigs.get(activeSheetTab) || new Map();
    }
    if (isMultiTable) {
      return tableColumnConfigs.get(activeTableTab) || new Map();
    }
    return columnConfigs;
  };

  const setActiveConfigs = (
    updater: (
      prev: Map<string, MaskingColumnConfig>,
    ) => Map<string, MaskingColumnConfig>,
  ) => {
    if (isMultiSheet) {
      setSheetColumnConfigs((prev) => {
        const next = new Map(prev);
        const current = next.get(activeSheetTab) || new Map();
        next.set(activeSheetTab, updater(current));
        return next;
      });
    } else if (isMultiTable) {
      setTableColumnConfigs((prev) => {
        const next = new Map(prev);
        const current = next.get(activeTableTab) || new Map();
        next.set(activeTableTab, updater(current));
        return next;
      });
    } else {
      setColumnConfigs(updater);
    }
  };

  const toggleColumn = useCallback(
    (col: MaskingColumnAnalysis) => {
      setActiveConfigs((prev) => {
        const next = new Map(prev);
        if (next.has(col.column)) {
          next.delete(col.column);
        } else {
          // Pick best default technique based on dtype
          const dtypeTechs = getTechniquesForDtype(col.dtype);
          const defaultTechnique =
            col.suggested_technique || (dtypeTechs ? dtypeTechs[0] : "redact");
          next.set(col.column, {
            column: col.column,
            technique: defaultTechnique,
            options: {},
          });
        }
        return next;
      });
    },
    [isMultiSheet, activeSheetTab, isMultiTable, activeTableTab],
  );

  const setTechniqueForCol = useCallback(
    (column: string, technique: string) => {
      setActiveConfigs((prev) => {
        const next = new Map(prev);
        const existing = next.get(column);
        if (existing) next.set(column, { ...existing, technique, options: {} });
        return next;
      });
      // Cross-sheet rule propagation: automatically apply to same-name columns in other sheets
      if (isMultiSheet) {
        const targetSheets: string[] = [];
        sheetProfiles.forEach((sp) => {
          if (sp.sheet_name !== activeSheetTab) {
            const hasCol = sp.columns.some((c) => c.column === column);
            if (hasCol) targetSheets.push(sp.sheet_name);
          }
        });
        if (targetSheets.length > 0) {
          // Auto-apply immediately instead of showing suggestion banner
          setSheetColumnConfigs((prev) => {
            const next = new Map(prev);
            targetSheets.forEach((sheetName) => {
              const existing = new Map(next.get(sheetName) || new Map());
              existing.set(column, { column, technique, options: {} });
              next.set(sheetName, existing);
            });
            return next;
          });
          setSaveSuccess(
            `Auto-applied "${technique}" to "${column}" across ${targetSheets.length + 1} sheets`,
          );
          setTimeout(() => setSaveSuccess(null), 3000);
        }
      }
    },
    [isMultiSheet, activeSheetTab, sheetProfiles, isMultiTable, activeTableTab],
  );

  const setOptionForCol = useCallback(
    (column: string, optName: string, optValue: any) => {
      setActiveConfigs((prev) => {
        const next = new Map(prev);
        const existing = next.get(column);
        if (existing)
          next.set(column, {
            ...existing,
            options: { ...existing.options, [optName]: optValue },
          });
        return next;
      });
      // Also propagate option changes to matching columns in other sheets
      if (isMultiSheet) {
        sheetProfiles.forEach((sp) => {
          if (sp.sheet_name !== activeSheetTab) {
            const hasCol = sp.columns.some((c) => c.column === column);
            if (hasCol) {
              setSheetColumnConfigs((prev) => {
                const next = new Map(prev);
                const sheetMap = new Map(next.get(sp.sheet_name) || new Map());
                const existing = sheetMap.get(column);
                if (existing) {
                  sheetMap.set(column, {
                    ...existing,
                    options: { ...existing.options, [optName]: optValue },
                  });
                  next.set(sp.sheet_name, sheetMap);
                }
                return next;
              });
            }
          }
        });
      }
    },
    [isMultiSheet, activeSheetTab, sheetProfiles, isMultiTable, activeTableTab],
  );

  // Total selected columns across all sheets/tables
  const totalSelectedColumns = isMultiSheet
    ? Array.from(sheetColumnConfigs.values()).reduce(
        (sum, m) => sum + m.size,
        0,
      )
    : isMultiTable
      ? Array.from(tableColumnConfigs.values()).reduce(
          (sum, m) => sum + m.size,
          0,
        )
      : columnConfigs.size;

  /* ── Sheet drag-to-reorder ── */
  const handleSheetDragStart = (idx: number) => setDragSheetIdx(idx);
  const handleSheetDragOver = (e: React.DragEvent, idx: number) => {
    e.preventDefault();
    setDragOverSheetIdx(idx);
  };
  const handleSheetDrop = (dropIdx: number, list: "info" | "profile") => {
    if (dragSheetIdx === null || dragSheetIdx === dropIdx) {
      setDragSheetIdx(null);
      setDragOverSheetIdx(null);
      return;
    }
    if (list === "info") {
      setSheetInfoList((prev) => {
        const next = [...prev];
        const [moved] = next.splice(dragSheetIdx, 1);
        next.splice(dropIdx, 0, moved);
        return next;
      });
    } else {
      setSheetProfiles((prev) => {
        const next = [...prev];
        const [moved] = next.splice(dragSheetIdx, 1);
        next.splice(dropIdx, 0, moved);
        return next;
      });
    }
    setDragSheetIdx(null);
    setDragOverSheetIdx(null);
  };
  const handleSheetDragEnd = () => {
    setDragSheetIdx(null);
    setDragOverSheetIdx(null);
  };

  /* ── Bulk column selection helpers ── */
  const toggleBulkSelect = (col: string) => {
    setBulkSelected((prev) => {
      const next = new Set(prev);
      if (next.has(col)) next.delete(col);
      else next.add(col);
      return next;
    });
  };

  const bulkSelectBySensitivity = (level: string) => {
    const cols = activeColumns
      .filter((c) => c.sensitivity === level)
      .map((c) => c.column);
    setBulkSelected(new Set(cols));
  };

  const bulkApplyTechnique = (technique: string) => {
    setActiveConfigs((prev) => {
      const next = new Map(prev);
      bulkSelected.forEach((col) => {
        const existing = next.get(col);
        if (existing) {
          next.set(col, { ...existing, technique, options: {} });
        } else {
          next.set(col, { column: col, technique, options: {} });
        }
      });
      return next;
    });
    setBulkSelected(new Set());
    setBulkMode(false);
  };

  const bulkRemoveColumns = () => {
    setActiveConfigs((prev) => {
      const next = new Map(prev);
      bulkSelected.forEach((col) => next.delete(col));
      return next;
    });
    setBulkSelected(new Set());
    setBulkMode(false);
  };

  const bulkSelectAll = () =>
    setBulkSelected(new Set(filteredColumns.map((c) => c.column)));
  const bulkClearSelection = () => setBulkSelected(new Set());

  /* ── Quick masking presets ── */
  const MASKING_PRESETS = [
    {
      id: "pii_shield",
      label: "PII Shield",
      desc: "Masks all high + medium sensitivity columns with recommended techniques",
      icon: <ShieldCheck size={14} className="text-red-400" />,
      color: "red",
      apply: () => {
        setActiveConfigs(() => {
          const next = new Map<string, MaskingColumnConfig>();
          activeColumns.forEach((col) => {
            if (col.sensitivity === "high" || col.sensitivity === "medium") {
              next.set(col.column, {
                column: col.column,
                technique: col.suggested_technique || "redact",
                options: {},
              });
            }
          });
          return next;
        });
      },
    },
    {
      id: "full_masking",
      label: "Full Masking",
      desc: "Apply masking to ALL columns (high→redact, medium→fake, low→shuffle)",
      icon: <Lock size={14} className="text-purple-400" />,
      color: "purple",
      apply: () => {
        setActiveConfigs(() => {
          const next = new Map<string, MaskingColumnConfig>();
          activeColumns.forEach((col) => {
            const technique =
              col.sensitivity === "high"
                ? "redact"
                : col.sensitivity === "medium"
                  ? col.suggested_technique || "fake_substitute"
                  : col.sensitivity === "low"
                    ? "shuffle"
                    : "generalize";
            next.set(col.column, {
              column: col.column,
              technique,
              options: {},
            });
          });
          return next;
        });
      },
    },
    {
      id: "gdpr_compliant",
      label: "GDPR Mode",
      desc: "Focus on personal data: fake substitution for names, emails, phones, addresses",
      icon: <Fingerprint size={14} className="text-blue-400" />,
      color: "blue",
      apply: () => {
        const piiKeywords = [
          "name",
          "email",
          "phone",
          "address",
          "ssn",
          "dob",
          "birth",
          "salary",
          "income",
        ];
        setActiveConfigs(() => {
          const next = new Map<string, MaskingColumnConfig>();
          activeColumns.forEach((col) => {
            const lower = col.column.toLowerCase();
            const isPII =
              piiKeywords.some((kw) => lower.includes(kw)) ||
              col.sensitivity === "high";
            if (isPII) {
              next.set(col.column, {
                column: col.column,
                technique: col.suggested_technique || "fake_substitute",
                options: {},
              });
            }
          });
          return next;
        });
      },
    },
    {
      id: "minimal",
      label: "Minimal",
      desc: "Only mask high-sensitivity columns, leave everything else untouched",
      icon: <EyeOff size={14} className="text-amber-400" />,
      color: "amber",
      apply: () => {
        setActiveConfigs(() => {
          const next = new Map<string, MaskingColumnConfig>();
          activeColumns.forEach((col) => {
            if (col.sensitivity === "high") {
              next.set(col.column, {
                column: col.column,
                technique: col.suggested_technique || "redact",
                options: {},
              });
            }
          });
          return next;
        });
      },
    },
  ];

  /* ── Copy masking rule from another configured column ── */
  const handleCopyRuleFrom = useCallback(
    (targetColumn: string, sourceColumn: string, sourceSheet?: string) => {
      let srcConfig: MaskingColumnConfig | undefined;
      if (isMultiSheet && sourceSheet) {
        srcConfig = sheetColumnConfigs.get(sourceSheet)?.get(sourceColumn);
      } else if (isMultiSheet) {
        // Check current sheet first, then others
        srcConfig = sheetColumnConfigs.get(activeSheetTab)?.get(sourceColumn);
        if (!srcConfig) {
          for (const [, configs] of sheetColumnConfigs) {
            srcConfig = configs.get(sourceColumn);
            if (srcConfig) break;
          }
        }
      } else {
        srcConfig = columnConfigs.get(sourceColumn);
      }

      // If source isn't configured yet, configure it with the target's current technique,
      // then copy that config to the target (so both end up with the same rule)
      if (!srcConfig) {
        const targetConfig = isMultiSheet
          ? sheetColumnConfigs.get(activeSheetTab)?.get(targetColumn)
          : columnConfigs.get(targetColumn);
        if (targetConfig) {
          // Apply the target's technique to the source column first
          setActiveConfigs((prev) => {
            const next = new Map(prev);
            next.set(sourceColumn, {
              column: sourceColumn,
              technique: targetConfig.technique,
              options: { ...targetConfig.options },
            });
            return next;
          });
        }
        return;
      }

      setActiveConfigs((prev) => {
        const next = new Map(prev);
        next.set(targetColumn, {
          column: targetColumn,
          technique: srcConfig!.technique,
          options: { ...srcConfig!.options },
          lineage_group: srcConfig!.lineage_group,
        });
        return next;
      });
    },
    [isMultiSheet, activeSheetTab, sheetColumnConfigs, columnConfigs],
  );

  /* ── Get all configured columns across all sheets (for "Copy from" dropdown) ── */
  const getAllConfiguredColumns = useCallback((): {
    column: string;
    sheet?: string;
    technique: string;
  }[] => {
    const result: { column: string; sheet?: string; technique: string }[] = [];
    if (isMultiSheet) {
      sheetColumnConfigs.forEach((configs, sheetName) => {
        configs.forEach((cfg, col) => {
          result.push({
            column: col,
            sheet: sheetName,
            technique: cfg.technique,
          });
        });
      });
    } else {
      columnConfigs.forEach((cfg, col) => {
        result.push({ column: col, technique: cfg.technique });
      });
    }
    return result;
  }, [isMultiSheet, sheetColumnConfigs, columnConfigs]);

  /* ── Get ALL available columns for the current view (configured + unconfigured) ── */
  const getAllAvailableColumns = useCallback((): {
    column: string;
    sheet?: string;
    technique?: string;
    dtype: string;
  }[] => {
    const result: {
      column: string;
      sheet?: string;
      technique?: string;
      dtype: string;
    }[] = [];
    if (isMultiSheet) {
      sheetProfiles.forEach((sp) => {
        sp.columns.forEach((c) => {
          const cfg = sheetColumnConfigs.get(sp.sheet_name)?.get(c.column);
          result.push({
            column: c.column,
            sheet: sp.sheet_name,
            technique: cfg?.technique,
            dtype: c.dtype,
          });
        });
      });
    } else {
      (uploadResponse?.columns || []).forEach((c) => {
        const cfg = columnConfigs.get(c.column);
        result.push({
          column: c.column,
          technique: cfg?.technique,
          dtype: c.dtype,
        });
      });
    }
    return result;
  }, [
    isMultiSheet,
    sheetProfiles,
    sheetColumnConfigs,
    uploadResponse,
    columnConfigs,
  ]);

  /* ── Date link helpers ── */
  const getDateColumns = useCallback((): {
    column: string;
    sheet?: string;
  }[] => {
    const result: { column: string; sheet?: string }[] = [];
    const isDateCol = (col: MaskingColumnAnalysis) => {
      const t = col.dtype.toLowerCase();
      const n = col.column.toUpperCase();
      // Check dtype OR column name contains date-related keywords
      return (
        t.includes("date") ||
        t.includes("time") ||
        t.includes("timestamp") ||
        n.includes("DATE") ||
        n.includes("_DT") ||
        n.endsWith("_TS") ||
        n.includes("TIMESTAMP") ||
        n.includes("_TIME")
      );
    };
    if (isMultiSheet) {
      sheetProfiles.forEach((sp) => {
        sp.columns.filter(isDateCol).forEach((c) => {
          result.push({ column: c.column, sheet: sp.sheet_name });
        });
      });
    } else {
      (uploadResponse?.columns || []).filter(isDateCol).forEach((c) => {
        result.push({ column: c.column });
      });
    }
    return result;
  }, [isMultiSheet, sheetProfiles, uploadResponse]);

  const addDateLink = useCallback(() => {
    if (!dateLinkPrimary || dateLinkLinked.length === 0) return;
    setDateLinks((prev) => [
      ...prev,
      { primary: dateLinkPrimary, linked: [...dateLinkLinked] },
    ]);
    setDateLinkPrimary("");
    setDateLinkLinked([]);
    setShowDateLinkBuilder(false);
  }, [dateLinkPrimary, dateLinkLinked]);

  const removeDateLink = useCallback((idx: number) => {
    setDateLinks((prev) => prev.filter((_, i) => i !== idx));
  }, []);

  /* ── Smart date pair detection using token-based matching ── */
  /* Only links columns that are SELECTED for masking */
  const autoDetectDateLinks = useCallback(() => {
    // Get ALL date-named columns from profile
    const allDateCols = getDateColumns().map((dc) => dc.column);
    if (allDateCols.length < 2) return 0;

    // Only consider columns that are currently configured for masking
    const configuredCols = new Set<string>();
    if (isMultiSheet) {
      sheetColumnConfigs.forEach((configs) =>
        configs.forEach((_, col) => configuredCols.add(col)),
      );
    } else {
      columnConfigs.forEach((_, col) => configuredCols.add(col));
    }
    const dateCols = allDateCols.filter((c) => configuredCols.has(c));
    if (dateCols.length < 2) return 0;

    // Token-based grouping: split column names into tokens, find matching START↔END pairs
    const tokenPairs: [string, string][] = [
      ["START", "END"],
      ["BEGIN", "END"],
      ["FROM", "TO"],
      ["OPEN", "CLOSE"],
      ["EFFECTIVE", "EXPIRATION"],
      ["HIRE", "TERMINATION"],
      ["CREATED", "MODIFIED"],
      ["CREATED", "UPDATED"],
      ["ISSUED", "EXPIRY"],
    ];

    const newLinks: DateLink[] = [];
    const alreadyLinked = new Set<string>();

    // Collect already linked columns
    dateLinks.forEach((link) => {
      alreadyLinked.add(link.primary);
      link.linked.forEach((l) => alreadyLinked.add(l));
    });

    // Tokenize: split by _ and normalize
    const tokenize = (name: string) => name.toUpperCase().split(/[_\s]+/);

    for (const col of dateCols) {
      if (alreadyLinked.has(col)) continue;
      const tokens = tokenize(col);

      for (const [fromToken, toToken] of tokenPairs) {
        const fromIdx = tokens.indexOf(fromToken);
        if (fromIdx === -1) continue;

        // Build expected paired column by replacing the token
        const pairedTokens = [...tokens];
        pairedTokens[fromIdx] = toToken;
        const expectedPaired = pairedTokens.join("_");

        // Find matching column (case-insensitive)
        const pairedCol = dateCols.find(
          (c) =>
            !alreadyLinked.has(c) &&
            c !== col &&
            c.toUpperCase() === expectedPaired,
        );

        if (pairedCol) {
          newLinks.push({ primary: col, linked: [pairedCol] });
          alreadyLinked.add(col);
          alreadyLinked.add(pairedCol);
          break;
        }
      }
    }

    if (newLinks.length > 0) {
      setDateLinks((prev) => [...prev, ...newLinks]);
      setSaveSuccess(
        `Auto-linked ${newLinks.length} date pair${newLinks.length > 1 ? "s" : ""} to preserve ordering`,
      );
      setTimeout(() => setSaveSuccess(null), 3000);
    }
    return newLinks.length;
  }, [
    getDateColumns,
    dateLinks,
    isMultiSheet,
    sheetColumnConfigs,
    columnConfigs,
  ]);

  // Auto-detect date links when date_shift columns exist and no date links are configured
  useEffect(() => {
    const configs = isMultiSheet
      ? Array.from(sheetColumnConfigs.values()).flatMap((m) =>
          Array.from(m.values()),
        )
      : Array.from(columnConfigs.values());
    const hasDateShift = configs.some((c) => c.technique === "date_shift");
    if (hasDateShift && dateLinks.length === 0) {
      // Defer to avoid state update during render
      const timer = setTimeout(() => autoDetectDateLinks(), 100);
      return () => clearTimeout(timer);
    }
  }, [
    isMultiSheet,
    sheetColumnConfigs,
    columnConfigs,
    dateLinks.length,
    autoDetectDateLinks,
  ]);

  /* ── Lineage group helpers ── */
  const addToLineageGroup = useCallback((column: string, groupIdx: number) => {
    setLineageGroups((prev) => {
      const next = prev.map((g) => g.filter((c) => c !== column)); // remove from any existing group
      if (groupIdx >= 0 && groupIdx < next.length) {
        next[groupIdx] = [...next[groupIdx], column];
      }
      return next.filter((g) => g.length > 0);
    });
  }, []);

  const createLineageGroup = useCallback((columns: string[]) => {
    if (columns.length < 2) return;
    setLineageGroups((prev) => [...prev, columns]);
  }, []);

  /* ── Preview ── */
  const handlePreview = useCallback(async () => {
    if (!sessionId) return;
    setLoading(true);
    setError(null);
    try {
      if (isMultiSheet) {
        // Check all sheets (not just active tab) for configured columns
        const allConfigs = _collectAllColumnConfigs();
        if (allConfigs.length === 0 && passThroughSheets.size === 0) {
          setError(
            "Configure at least one column to mask or mark sheets as pass-through",
          );
          setLoading(false);
          return;
        }
        // Preview the active sheet if it has configs, otherwise the first sheet with configs
        let previewSheet = activeSheetTab;
        let previewConfigs = Array.from(getActiveConfigs().values());
        if (previewConfigs.length === 0) {
          for (const [sheetName, configs] of sheetColumnConfigs.entries()) {
            if (configs.size > 0) {
              previewSheet = sheetName;
              previewConfigs = Array.from(configs.values());
              break;
            }
          }
        }
        if (previewConfigs.length === 0) {
          // All selected sheets are pass-through only — skip preview, go to execute
          setStep(STEP_PREVIEW);
          setPreviewData({
            session_id: sessionId,
            original: [],
            masked: [],
            columns_affected: [],
            total_rows: 0,
            preview_rows: 0,
          });
          setLoading(false);
          return;
        }
        const res = await api.maskingPreview(
          sessionId,
          previewConfigs,
          previewSheet,
          lineageEnabled,
          lineageGroups,
          dateLinks,
        );
        setPreviewData(res);
      } else if (isMultiTable) {
        // Check all tables (not just active tab) for configured columns
        const allConfigs = _collectAllColumnConfigs();
        if (allConfigs.length === 0) {
          setError(
            "Configure at least one column to mask on at least one table",
          );
          setLoading(false);
          return;
        }
        // Preview the active table if it has configs, otherwise the first table with configs
        let previewTable = activeTableTab;
        let previewConfigs = Array.from(getActiveConfigs().values());
        if (previewConfigs.length === 0) {
          for (const [tableName, configs] of tableColumnConfigs.entries()) {
            if (configs.size > 0) {
              previewTable = tableName;
              previewConfigs = Array.from(configs.values());
              break;
            }
          }
        }
        const res = await api.maskingPreview(
          sessionId,
          previewConfigs,
          undefined,
          lineageEnabled,
          lineageGroups,
          dateLinks,
          previewTable,
        );
        setPreviewData(res);
      } else {
        const configs = Array.from(columnConfigs.values());
        if (configs.length === 0) {
          setError("Select at least one column to mask");
          setLoading(false);
          return;
        }
        const res = await api.maskingPreview(
          sessionId,
          configs,
          undefined,
          lineageEnabled,
          lineageGroups,
          dateLinks,
        );
        setPreviewData(res);
      }
      setStep(STEP_PREVIEW);
    } catch (err: any) {
      setError(err.message || "Preview failed");
    } finally {
      setLoading(false);
    }
  }, [
    sessionId,
    columnConfigs,
    isMultiSheet,
    activeSheetTab,
    sheetColumnConfigs,
    passThroughSheets,
    isMultiTable,
    activeTableTab,
    tableColumnConfigs,
    STEP_PREVIEW,
    lineageEnabled,
    lineageGroups,
    dateLinks,
  ]);

  /* ── Execute ── */
  const handleExecute = useCallback(async () => {
    if (!sessionId) return;
    setLoading(true);
    setError(null);
    try {
      let params: any;
      if (isMultiSheet) {
        const sheetConfigs: MaskingSheetConfig[] = [];
        sheetColumnConfigs.forEach((configs, sheetName) => {
          if (configs.size > 0) {
            sheetConfigs.push({
              sheet: sheetName,
              column_configs: Array.from(configs.values()),
            });
          }
        });
        params = {
          session_id: sessionId,
          sheet_configs: sheetConfigs,
          pass_through_sheets:
            passThroughSheets.size > 0
              ? Array.from(passThroughSheets)
              : undefined,
          target_format: targetFormat,
          target_type: targetType,
          db_config: targetType === "db" ? targetDbParams : undefined,
          table_strategy: targetType === "db" ? tableStrategy : undefined,
          lineage_enabled: lineageEnabled || undefined,
          lineage_groups: lineageGroups.length > 0 ? lineageGroups : undefined,
          date_links: dateLinks.length > 0 ? dateLinks : undefined,
        };
      } else if (isMultiTable) {
        const tableConfigs = _collectTableConfigs() || [];
        params = {
          session_id: sessionId,
          table_configs: tableConfigs,
          target_format: targetFormat,
          target_type: targetType,
          db_config: targetType === "db" ? targetDbParams : undefined,
          table_strategy: targetType === "db" ? tableStrategy : undefined,
          lineage_enabled: lineageEnabled || undefined,
          lineage_groups: lineageGroups.length > 0 ? lineageGroups : undefined,
          date_links: dateLinks.length > 0 ? dateLinks : undefined,
        };
      } else {
        params = {
          session_id: sessionId,
          column_configs: Array.from(columnConfigs.values()),
          target_format: targetFormat,
          target_type: targetType,
          db_config: targetType === "db" ? targetDbParams : undefined,
          table_strategy: targetType === "db" ? tableStrategy : undefined,
          lineage_enabled: lineageEnabled || undefined,
          lineage_groups: lineageGroups.length > 0 ? lineageGroups : undefined,
          date_links: dateLinks.length > 0 ? dateLinks : undefined,
        };
      }
      await api.maskingExecute(params);
      // execute starts the work in the background and returns immediately
      // (avoids a long-held connection getting killed by CloudFront's
      // origin timeout under load) — poll for the real outcome instead of
      // awaiting one long request.
      const POLL_INTERVAL_MS = 800;
      const MAX_WAIT_MS = 5 * 60 * 1000;
      const deadline = Date.now() + MAX_WAIT_MS;
      for (;;) {
        const statusRes = await api.maskingExecuteStatus(sessionId);
        if (statusRes.status === "completed") {
          setExecuteResult(statusRes.result);
          setStep(STEP_EXECUTE);
          break;
        }
        if (statusRes.status === "failed") {
          throw new Error(statusRes.error || "Execution failed");
        }
        if (Date.now() > deadline) {
          throw new Error(
            "Execution is taking longer than expected — check back shortly.",
          );
        }
        await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
      }
    } catch (err: any) {
      setError(err.message || "Execution failed");
    } finally {
      setLoading(false);
    }
  }, [
    sessionId,
    columnConfigs,
    sheetColumnConfigs,
    isMultiSheet,
    isMultiTable,
    tableColumnConfigs,
    targetFormat,
    targetType,
    targetDbParams,
    tableStrategy,
    lineageEnabled,
    lineageGroups,
    dateLinks,
    STEP_EXECUTE,
  ]);

  /* ── Filtered columns for search ── */
  const activeColumns = getActiveColumns();
  const filteredColumns = activeColumns.filter((c) =>
    c.column.toLowerCase().includes(columnSearch.toLowerCase()),
  );
  const activeConfigs = getActiveConfigs();

  /* ── Reset all state ── */
  /* ── Save config ── */
  const handleSaveConfig = useCallback(async () => {
    if (!saveConfigName.trim()) return;
    const allConfigs = _collectAllColumnConfigs();
    const schemaColumns = (uploadResponse?.columns || []).map((c) => c.column);

    // Build sheet_configs for multi-sheet files
    let sheetConfigsPayload:
      | { sheet: string; column_configs: MaskingColumnConfig[] }[]
      | undefined;
    if (isMultiSheet) {
      sheetConfigsPayload = [];
      sheetColumnConfigs.forEach((configs, sheetName) => {
        if (configs.size > 0) {
          sheetConfigsPayload!.push({
            sheet: sheetName,
            column_configs: Array.from(configs.values()),
          });
        }
      });
    }

    setLoading(true);
    try {
      const res = await api.maskingConfigSave({
        name: saveConfigName.trim(),
        description: saveConfigDesc.trim(),
        column_configs: allConfigs,
        schema_columns: schemaColumns,
        sheet_configs: sheetConfigsPayload,
        is_multi_sheet: isMultiSheet || undefined,
        // Multi-table (#603 item 6) — same shape/precedent as sheet_configs
        // above, so a multi-table plan can be saved and reloaded like its
        // multi-sheet sibling.
        table_configs: _collectTableConfigs(),
        is_multi_table: isMultiTable || undefined,
        pass_through_sheets:
          passThroughSheets.size > 0
            ? Array.from(passThroughSheets)
            : undefined,
        parent_id:
          saveMode === "update" && selectedParentConfig
            ? selectedParentConfig.id
            : undefined,
        lineage_enabled: lineageEnabled || undefined,
        lineage_groups: lineageGroups.length > 0 ? lineageGroups : undefined,
        date_links: dateLinks.length > 0 ? dateLinks : undefined,
      });
      const versionLabel = (res as any).version
        ? ` (v${(res as any).version})`
        : "";
      setSaveSuccess(`Configuration "${saveConfigName}"${versionLabel} saved!`);
      setTimeout(() => setSaveSuccess(null), 3000);
      setShowSaveModal(false);
      setSaveConfigName("");
      setSaveConfigDesc("");
      setSaveMode("new");
      setSelectedParentConfig(null);
    } catch (err: any) {
      setError(err.message || "Failed to save config");
    } finally {
      setLoading(false);
    }
  }, [
    saveConfigName,
    saveConfigDesc,
    uploadResponse,
    isMultiSheet,
    sheetColumnConfigs,
    isMultiTable,
    tableColumnConfigs,
    saveMode,
    selectedParentConfig,
  ]);

  /* ── Load config ── */
  const handleLoadConfigs = useCallback(async () => {
    setLoadingConfigs(true);
    try {
      const res = await api.maskingConfigList();
      setSavedConfigs(res.configs);
    } catch (err: any) {
      setError(err.message || "Failed to load configs");
    } finally {
      setLoadingConfigs(false);
    }
  }, []);

  const handleApplyConfig = (cfg: SavedMaskingConfig) => {
    // Multi-table (#603 item 6): tables are already loaded by the time this
    // can be reached (unlike the file-upload path, table selection happens
    // before any config can be applied), so apply directly by table name
    // rather than going through the "pending" mechanism below.
    if (isMultiTable && tableProfiles.length > 0) {
      const savedByTable = new Map(
        (cfg.is_multi_table && cfg.table_configs ? cfg.table_configs : []).map(
          (tc) => [tc.table, tc.column_configs] as const,
        ),
      );
      const newTableConfigs = new Map<
        string,
        Map<string, MaskingColumnConfig>
      >();
      let matchedTables = 0;
      tableProfiles.forEach((tp) => {
        const availableColumns = new Set(tp.columns.map((c) => c.column));
        // A saved multi-table config matches by table name; a flat
        // (single-table) saved config is applied to every selected table
        // whose columns match, so a config saved before this feature
        // existed is still reusable.
        const sourceConfigs =
          savedByTable.get(tp.table_name) ?? cfg.column_configs;
        const m = new Map<string, MaskingColumnConfig>();
        sourceConfigs.forEach((cc) => {
          if (availableColumns.has(cc.column)) {
            m.set(cc.column, {
              column: cc.column,
              technique: cc.technique,
              options: cc.options || {},
            });
          }
        });
        if (m.size > 0) matchedTables++;
        newTableConfigs.set(tp.table_name, m);
      });
      setTableColumnConfigs(newTableConfigs);
      setLineageEnabled(!!cfg.lineage_enabled);
      setLineageGroups(cfg.lineage_groups || []);
      setDateLinks(cfg.date_links || []);
      setShowLoadModal(false);
      if (matchedTables > 0) {
        setSaveSuccess(
          `Applied config "${cfg.name}" — matched columns on ${matchedTables} of ${tableProfiles.length} tables`,
        );
        setTimeout(() => setSaveSuccess(null), 4000);
      }
      return;
    }
    if (!uploadResponse) {
      // No file uploaded yet — store as pending; user still needs to upload
      setPendingConfig(cfg);
      setShowLoadModal(false);
      return;
    }
    const newConfigs = new Map<string, MaskingColumnConfig>();
    const availableColumns = new Set(
      (uploadResponse?.columns || []).map((c) => c.column),
    );
    cfg.column_configs.forEach((cc) => {
      if (availableColumns.has(cc.column)) {
        newConfigs.set(cc.column, {
          column: cc.column,
          technique: cc.technique,
          options: cc.options || {},
        });
      }
    });
    setColumnConfigs(newConfigs);
    // Restore pass-through sheets if saved
    if (cfg.pass_through_sheets && cfg.pass_through_sheets.length > 0) {
      setPassThroughSheets(new Set(cfg.pass_through_sheets));
    } else {
      setPassThroughSheets(new Set());
    }
    // Restore lineage settings
    setLineageEnabled(!!cfg.lineage_enabled);
    setLineageGroups(cfg.lineage_groups || []);
    setDateLinks(cfg.date_links || []);
    setShowLoadModal(false);
  };

  /* ── Apply pending config after upload ── */
  const applyPendingConfig = useCallback(
    (columns: MaskingColumnAnalysis[]) => {
      if (!pendingConfig) return null;
      const availableColumns = new Set(columns.map((c) => c.column));
      const newConfigs = new Map<string, MaskingColumnConfig>();
      let matched = 0;
      pendingConfig.column_configs.forEach((cc) => {
        if (availableColumns.has(cc.column)) {
          newConfigs.set(cc.column, {
            column: cc.column,
            technique: cc.technique,
            options: cc.options || {},
          });
          matched++;
        }
      });
      setPendingConfig(null);
      if (matched > 0) {
        setSaveSuccess(
          `Loaded config "${pendingConfig.name}" — ${matched} of ${pendingConfig.column_configs.length} columns matched`,
        );
        setTimeout(() => setSaveSuccess(null), 4000);
      }
      return newConfigs;
    },
    [pendingConfig],
  );

  const handleDeleteConfig = async (id: string) => {
    try {
      await api.maskingConfigDelete(id);
      setSavedConfigs((prev) => prev.filter((c) => c.id !== id));
    } catch (err: any) {
      setError(err.message || "Failed to delete config");
    }
  };

  /* ── Collect all column configs (single + multi-sheet + multi-table) ── */
  const _collectAllColumnConfigs = (): MaskingColumnConfig[] => {
    if (isMultiSheet) {
      const all: MaskingColumnConfig[] = [];
      sheetColumnConfigs.forEach((map) => map.forEach((v) => all.push(v)));
      return all;
    }
    if (isMultiTable) {
      const all: MaskingColumnConfig[] = [];
      tableColumnConfigs.forEach((map) => map.forEach((v) => all.push(v)));
      return all;
    }
    return Array.from(columnConfigs.values());
  };

  /* ── Collect per-sheet configs for script gen ── */
  const _collectSheetConfigs = ():
    | { sheet: string; column_configs: MaskingColumnConfig[] }[]
    | undefined => {
    if (!isMultiSheet) return undefined;
    const result: { sheet: string; column_configs: MaskingColumnConfig[] }[] =
      [];
    sheetColumnConfigs.forEach((map, sheetName) => {
      const configs = Array.from(map.values());
      if (configs.length > 0)
        result.push({ sheet: sheetName, column_configs: configs });
    });
    return result.length > 0 ? result : undefined;
  };

  /* ── Collect per-table configs (#603 item 1) — same shape/purpose as
   * _collectSheetConfigs above, for the execute() table_configs body and
   * for saving/loading a multi-table config. ── */
  const _collectTableConfigs = (): MaskingTableConfig[] | undefined => {
    if (!isMultiTable) return undefined;
    const result: MaskingTableConfig[] = [];
    tableColumnConfigs.forEach((map, tableName) => {
      const configs = Array.from(map.values());
      if (configs.length > 0)
        result.push({ table: tableName, column_configs: configs });
    });
    return result.length > 0 ? result : undefined;
  };

  /* ── Fetch token estimate for LLM generation ── */
  const fetchTokenEstimate = useCallback(async () => {
    const allConfigs = _collectAllColumnConfigs();
    if (allConfigs.length === 0) {
      setTokenEstimate(null);
      return;
    }
    setTokenEstimateLoading(true);
    try {
      const est = await api.maskingEstimateTokens({
        column_configs: allConfigs,
        language: scriptLanguage,
        deployment_target: scriptTarget,
        is_multi_sheet: isMultiSheet,
        sheet_configs: _collectSheetConfigs(),
        pass_through_sheets:
          passThroughSheets.size > 0
            ? Array.from(passThroughSheets)
            : undefined,
        user_instructions: scriptUserInstructions.trim() || undefined,
        lineage_enabled: scriptLineageEnabled || lineageEnabled,
        lineage_groups: lineageGroups.length > 0 ? lineageGroups : undefined,
        date_links: dateLinks.length > 0 ? dateLinks : undefined,
      });
      setTokenEstimate(est);
    } catch {
      setTokenEstimate(null);
    } finally {
      setTokenEstimateLoading(false);
    }
  }, [
    scriptLanguage,
    scriptTarget,
    isMultiSheet,
    scriptUserInstructions,
    scriptLineageEnabled,
    lineageEnabled,
    lineageGroups,
    dateLinks,
  ]);

  /* ── Generate Script ── */
  const handleGenerateScript = useCallback(async () => {
    const allConfigs = _collectAllColumnConfigs();
    if (allConfigs.length === 0 && passThroughSheets.size === 0) {
      setScriptError("No columns configured for masking.");
      return;
    }
    setScriptGenerating(true);
    setScriptError(null);
    setLastTokenUsage(null);
    try {
      const { blob, tokenInfo } = await api.maskingGenerateScript({
        config_name:
          uploadResponse?.file_name?.replace(/\.[^.]+$/, "") ||
          "masking_script",
        column_configs: allConfigs,
        language: scriptLanguage,
        deployment_target: scriptTarget,
        error_strategy: scriptErrorStrategy,
        max_errors: scriptMaxErrors,
        log_level: scriptLogLevel,
        log_output: scriptLogOutput,
        include_summary: scriptIncludeSummary,
        pre_validation: scriptPreValidation,
        post_validation: scriptPostValidation,
        null_handling: scriptNullHandling,
        include_tests: scriptIncludeTests,
        include_sample_data: scriptIncludeSampleData,
        include_test_runner: scriptIncludeTestRunner,
        include_code_review: scriptIncludeCodeReview,
        include_changelog: scriptIncludeChangelog,
        // New production-ready controls
        lineage_enabled: scriptLineageEnabled || lineageEnabled,
        lineage_groups: lineageGroups.length > 0 ? lineageGroups : undefined,
        date_links: dateLinks.length > 0 ? dateLinks : undefined,
        include_conditional_masking: scriptIncludeConditionalMasking,
        include_uniqueness: scriptIncludeUniqueness,
        include_format_validation: scriptIncludeFormatValidation,
        include_audit_log: scriptIncludeAuditLog,
        // Multi-sheet & user instructions
        is_multi_sheet: isMultiSheet,
        sheet_configs: _collectSheetConfigs(),
        pass_through_sheets:
          passThroughSheets.size > 0
            ? Array.from(passThroughSheets)
            : undefined,
        user_instructions: scriptUserInstructions.trim() || undefined,
        use_llm: scriptUseLLM && !!scriptUserInstructions.trim(),
      });
      // Trigger download
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `masking_script_${scriptLanguage}_${scriptTarget}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      if (tokenInfo) setLastTokenUsage(tokenInfo);
      setShowScriptModal(false);
    } catch (err: any) {
      setScriptError(err.message || "Script generation failed");
    } finally {
      setScriptGenerating(false);
    }
  }, [
    scriptLanguage,
    scriptTarget,
    uploadResponse,
    scriptErrorStrategy,
    scriptMaxErrors,
    scriptLogLevel,
    scriptLogOutput,
    scriptIncludeSummary,
    scriptPreValidation,
    scriptPostValidation,
    scriptNullHandling,
    scriptIncludeTests,
    scriptIncludeSampleData,
    scriptIncludeTestRunner,
    scriptIncludeCodeReview,
    scriptIncludeChangelog,
    scriptLineageEnabled,
    lineageEnabled,
    lineageGroups,
    dateLinks,
    scriptIncludeConditionalMasking,
    scriptIncludeUniqueness,
    scriptIncludeFormatValidation,
    scriptIncludeAuditLog,
    isMultiSheet,
    scriptUserInstructions,
    scriptUseLLM,
    // The masked column selection must be in deps — otherwise this
    // callback closes over a stale (empty) columnConfigs/sheetColumnConfigs
    // captured before any column was selected, and Generate Script wrongly
    // reports "No columns configured for masking."
    columnConfigs,
    sheetColumnConfigs,
    passThroughSheets,
  ]);

  const resetAll = () => {
    setStep(1);
    setSessionId(null);
    setUploadResponse(null);
    setIsMultiSheet(false);
    setSheetInfoList([]);
    setSelectedSheets(new Set());
    setSheetProfiles([]);
    setActiveSheetTab("");
    setSheetColumnConfigs(new Map());
    setColumnConfigs(new Map());
    setPassThroughSheets(new Set());
    setPreviewData(null);
    setExecuteResult(null);
    setError(null);
    setPendingConfig(null);
    setLineageEnabled(false);
    setLineageGroups([]);
    setDateLinks([]);
    setShowDateLinkBuilder(false);
    setBulkMode(false);
    setBulkSelected(new Set());
    setShowPresetsPanel(false);
    setTokenEstimate(null);
    setLastTokenUsage(null);
    setSelectedProjectTables(new Set());
    setDbTables([]);
    setSelectedDbTables(new Set());
    setIsMultiTable(false);
    setTableProfiles([]);
    setActiveTableTab("");
    setTableColumnConfigs(new Map());
    setRelationships(null);
    // sessionId is reset above, but the auto-connect effect that would
    // normally repopulate it only re-fires when projectId/projectDbConfig/
    // projectDbMode change — none of which do here — so without this call
    // it stays null and handleTablesSelected silently no-ops on the next
    // "Mask <table>" click (its very first line is `if (!sessionId)
    // return`), making the table list look reloaded when it's actually
    // stale and unusable.
    if (projectDbMode === "project" && projectDbConfig) {
      handleFetchProjectDbTables();
    }
  };

  if (searchParams.get("tab") === "history" && projectId) {
    return (
      <AgentSessionHistoryView
        projectId={projectId}
        sessionType="masking"
        agentLabel="Data Guardian (Data Masking)"
        onResume={(id) => navigate(`?session=${id}`)}
        onNewSession={() => navigate("?")}
      />
    );
  }

  return (
    <div className="relative min-h-full">
      <GreenTendrils />
      <div className="relative z-10 max-w-6xl mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="p-2 rounded-lg hover:bg-surface-200 text-white/55 hover:text-white transition"
            >
              <ArrowLeft size={18} />
            </button>
            <div>
              <h1 className="text-2xl font-bold">
                <span className="gradient-text-green">Data Guardian</span>
              </h1>
              <p className="text-xs text-white/50 mt-0.5">
                Protect sensitive data with 11 masking techniques
              </p>
            </div>
          </div>
          <StepIndicator steps={steps} current={step} />
        </div>

        {/* Error banner */}
        {error && (
          <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-xl text-sm text-red-300 flex items-center justify-between">
            <span className="flex items-center gap-2">
              <AlertTriangle size={14} /> {error}
            </span>
            <button
              onClick={() => setError(null)}
              className="text-red-500 hover:text-red-300 text-xs"
            >
              dismiss
            </button>
          </div>
        )}

        {/* Loading overlay */}
        {loading && (
          <div className="flex items-center justify-center py-16">
            <div className="text-center space-y-3">
              <div className="w-10 h-10 border-2 border-brand-500 border-t-transparent rounded-full animate-spin mx-auto" />
              <p className="text-sm text-white/50">Processing...</p>
            </div>
          </div>
        )}

        {/* ════════════════════ STEP 1: Source ════════════════════ */}
        {step === 1 && !loading && (
          <div className="space-y-4">
            {/* Source type toggle */}
            <div className="glass rounded-xl p-4">
              <div className="flex gap-3">
                <button
                  onClick={() => setSourceType("file")}
                  className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-medium transition-all border ${
                    sourceType === "file"
                      ? "bg-brand-500/10 border-brand-500/30 text-brand-500"
                      : "bg-surface-200 border-white/5 text-white/55 hover:text-white/60"
                  }`}
                >
                  <Upload size={16} /> Upload File
                </button>
                <button
                  data-testid="masking-source-db"
                  onClick={() => setSourceType("db")}
                  className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-medium transition-all border ${
                    sourceType === "db"
                      ? "bg-brand-500/10 border-brand-500/30 text-brand-500"
                      : "bg-surface-200 border-white/5 text-white/55 hover:text-white/60"
                  }`}
                >
                  <Database size={16} /> Connect Database
                </button>
              </div>
            </div>

            {sourceType === "file" ? (
              <div className="glass rounded-xl p-8">
                <div
                  className="border-2 border-dashed border-white/10 hover:border-brand-500/40 rounded-xl p-10 text-center cursor-pointer transition-all hover:bg-brand-500/5"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <FileSpreadsheet
                    size={40}
                    className="mx-auto mb-4 text-brand-500/60"
                  />
                  <p className="text-sm text-white/60 mb-1">
                    Click to upload or drag & drop
                  </p>
                  <p className="text-[11px] text-white/50">
                    CSV, Excel (multi-sheet supported), JSON, Parquet
                  </p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv,.tsv,.txt,.xlsx,.xls,.json,.jsonl,.parquet"
                    className="hidden"
                    onChange={handleFileUpload}
                  />
                </div>
                {/* Load saved config */}
                <div className="mt-4 flex items-center justify-center">
                  <button
                    onClick={() => {
                      handleLoadConfigs();
                      setShowLoadModal(true);
                    }}
                    className="px-4 py-2 bg-surface-200 hover:bg-surface-300 rounded-xl text-xs text-white/55 hover:text-white transition flex items-center gap-2 border border-white/5 hover:border-white/10"
                    title="Load a previously saved masking configuration"
                  >
                    <FolderOpen size={14} /> Load Saved Configuration
                  </button>
                </div>
                {/* Pending config indicator */}
                {pendingConfig && (
                  <div className="mt-3 p-3 bg-brand-500/10 border border-brand-500/20 rounded-xl flex items-center justify-between">
                    <div className="flex items-center gap-2 text-xs text-brand-400">
                      <CheckCircle2 size={14} />
                      <span>
                        Config{" "}
                        <span className="font-semibold">
                          &quot;{pendingConfig.name}&quot;
                        </span>{" "}
                        loaded (
                        {pendingConfig.is_multi_sheet &&
                        pendingConfig.sheet_configs
                          ? `${pendingConfig.sheet_configs.length} sheets, `
                          : ""}
                        {pendingConfig.column_configs.length} columns). Upload a
                        file to apply.
                      </span>
                    </div>
                    <button
                      onClick={() => setPendingConfig(null)}
                      className="text-white/50 hover:text-white/60 transition"
                    >
                      <X size={14} />
                    </button>
                  </div>
                )}
              </div>
            ) : (
              /* Database connection — project or manual */
              <div className="space-y-4">
                {/* ── Project Connection Card ── */}
                {projectId && projectDbConfig && (
                  <div className="glass rounded-xl p-6 space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-semibold text-brand-400 flex items-center gap-2">
                        <Server size={16} /> Project Database Connection
                      </h3>
                      <span className="text-[11px] px-2 py-0.5 rounded-full bg-brand-500/15 text-brand-400 border border-brand-500/20">
                        {projectDbConfig.db_type?.toUpperCase()}
                      </span>
                    </div>

                    {/* Connection summary */}
                    <div className="grid grid-cols-2 gap-3">
                      {projectDbConfig.database && (
                        <div className="text-xs">
                          <span className="text-white/50">Database:</span>{" "}
                          <span className="text-white/70">
                            {projectDbConfig.database}
                          </span>
                        </div>
                      )}
                      {projectDbConfig.schema_name && (
                        <div className="text-xs">
                          <span className="text-white/50">Schema:</span>{" "}
                          <span className="text-white/70">
                            {projectDbConfig.schema_name}
                          </span>
                        </div>
                      )}
                      {projectDbConfig.host && (
                        <div className="text-xs">
                          <span className="text-white/50">Host:</span>{" "}
                          <span className="text-white/70">
                            {projectDbConfig.host}
                          </span>
                        </div>
                      )}
                      {projectDbConfig.account && (
                        <div className="text-xs">
                          <span className="text-white/50">Account:</span>{" "}
                          <span className="text-white/70">
                            {projectDbConfig.account}
                          </span>
                        </div>
                      )}
                      {projectDbConfig.warehouse && (
                        <div className="text-xs">
                          <span className="text-white/50">Warehouse:</span>{" "}
                          <span className="text-white/70">
                            {projectDbConfig.warehouse}
                          </span>
                        </div>
                      )}
                      {projectDbConfig.username && (
                        <div className="text-xs">
                          <span className="text-white/50">User:</span>{" "}
                          <span className="text-white/70">
                            {projectDbConfig.username}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Fetch tables button or table/view list */}
                    {projectDbTables.length === 0 &&
                    projectDbViews.length === 0 ? (
                      <button
                        onClick={handleFetchProjectDbTables}
                        disabled={projectDbLoading}
                        className="w-full py-3 bg-brand-600/20 hover:bg-brand-600/30 border border-brand-500/30 disabled:opacity-50 text-brand-400 rounded-xl font-medium transition-colors flex items-center justify-center gap-2 text-sm"
                      >
                        {projectDbLoading ? (
                          <>
                            <Loader2 size={16} className="animate-spin" />{" "}
                            Fetching tables...
                          </>
                        ) : (
                          <>
                            <Table2 size={16} /> Load Tables & Views
                          </>
                        )}
                      </button>
                    ) : (
                      <div className="space-y-3">
                        {/* Search */}
                        <div className="relative">
                          <Search
                            size={14}
                            className="absolute left-3 top-1/2 -translate-y-1/2 text-white/50"
                          />
                          <input
                            type="text"
                            value={projectTableSearch}
                            onChange={(e) =>
                              setProjectTableSearch(e.target.value)
                            }
                            placeholder="Search tables and views..."
                            className="w-full pl-9 pr-3 py-2 bg-surface-200 border border-white/10 rounded-lg text-sm text-white/80 placeholder:text-white/50 focus:outline-none focus:border-brand-500/40"
                          />
                        </div>

                        {/* Selection count */}
                        {selectedProjectTables.size > 0 && (
                          <div className="text-xs text-brand-400 flex items-center gap-1.5">
                            <CheckCircle2 size={12} />
                            {selectedProjectTables.size} table
                            {selectedProjectTables.size > 1 ? "s" : ""} selected
                          </div>
                        )}

                        {/* Tables */}
                        {projectDbTables.length > 0 && (
                          <div>
                            <p className="text-[11px] uppercase tracking-wider text-white/50 mb-1.5 flex items-center gap-1">
                              <Table2 size={10} /> Tables (
                              {projectDbTables.length})
                            </p>
                            <div className="max-h-48 overflow-y-auto space-y-1 pr-1 custom-scrollbar">
                              {projectDbTables
                                .filter(
                                  (t) =>
                                    !projectTableSearch ||
                                    t
                                      .toLowerCase()
                                      .includes(
                                        projectTableSearch.toLowerCase(),
                                      ),
                                )
                                .map((table) => (
                                  <button
                                    key={`t-${table}`}
                                    onClick={() =>
                                      toggleProjectTableSelection(table)
                                    }
                                    className={`w-full text-left px-3 py-2 rounded-lg text-xs transition-all flex items-center gap-2 ${
                                      selectedProjectTables.has(table)
                                        ? "bg-brand-500/15 border border-brand-500/30 text-brand-400"
                                        : "bg-surface-200/50 border border-transparent text-white/50 hover:text-white/70 hover:bg-surface-200"
                                    }`}
                                  >
                                    <div
                                      className={`w-4 h-4 rounded-full border flex items-center justify-center flex-shrink-0 ${
                                        selectedProjectTables.has(table)
                                          ? "border-brand-500"
                                          : "border-white/20"
                                      }`}
                                    >
                                      {selectedProjectTables.has(table) && (
                                        <div className="w-2 h-2 rounded-full bg-brand-500" />
                                      )}
                                    </div>
                                    <Table2
                                      size={12}
                                      className="opacity-40 flex-shrink-0"
                                    />
                                    {table}
                                  </button>
                                ))}
                            </div>
                          </div>
                        )}

                        {/* Views */}
                        {projectDbViews.length > 0 && (
                          <div>
                            <p className="text-[11px] uppercase tracking-wider text-white/50 mb-1.5 flex items-center gap-1">
                              <Eye size={10} /> Views ({projectDbViews.length})
                            </p>
                            <div className="max-h-48 overflow-y-auto space-y-1 pr-1 custom-scrollbar">
                              {projectDbViews
                                .filter(
                                  (v) =>
                                    !projectTableSearch ||
                                    v
                                      .toLowerCase()
                                      .includes(
                                        projectTableSearch.toLowerCase(),
                                      ),
                                )
                                .map((view) => (
                                  <button
                                    key={`v-${view}`}
                                    onClick={() =>
                                      toggleProjectTableSelection(view)
                                    }
                                    className={`w-full text-left px-3 py-2 rounded-lg text-xs transition-all flex items-center gap-2 ${
                                      selectedProjectTables.has(view)
                                        ? "bg-brand-500/15 border border-brand-500/30 text-brand-400"
                                        : "bg-surface-200/50 border border-transparent text-white/50 hover:text-white/70 hover:bg-surface-200"
                                    }`}
                                  >
                                    <div
                                      className={`w-4 h-4 rounded-full border flex items-center justify-center flex-shrink-0 ${
                                        selectedProjectTables.has(view)
                                          ? "border-brand-500"
                                          : "border-white/20"
                                      }`}
                                    >
                                      {selectedProjectTables.has(view) && (
                                        <div className="w-2 h-2 rounded-full bg-brand-500" />
                                      )}
                                    </div>
                                    <Eye
                                      size={12}
                                      className="opacity-40 flex-shrink-0"
                                    />
                                    {view}
                                  </button>
                                ))}
                            </div>
                          </div>
                        )}

                        {/* Connect with selected table(s) — one table keeps
                         * the existing single-table flow; more than one
                         * switches into multi-table masking (#603 item 1). */}
                        <button
                          onClick={() =>
                            handleTablesSelected(
                              Array.from(selectedProjectTables),
                            )
                          }
                          disabled={selectedProjectTables.size === 0}
                          className="w-full py-3 bg-brand-600 hover:bg-brand-700 disabled:bg-gray-700 disabled:text-white/50 text-white rounded-xl font-medium transition-colors flex items-center justify-center gap-2 text-sm"
                        >
                          <Database size={16} />
                          {selectedProjectTables.size === 0
                            ? "Select tables to continue"
                            : selectedProjectTables.size === 1
                              ? `Mask "${Array.from(selectedProjectTables)[0]}"`
                              : `Mask ${selectedProjectTables.size} tables`}
                        </button>
                      </div>
                    )}

                    {/* Divider to manual form */}
                    <div className="flex items-center gap-3 pt-2">
                      <div className="flex-1 h-px bg-white/5" />
                      <button
                        data-testid="masking-toggle-manual-mode"
                        onClick={() =>
                          setProjectDbMode(
                            projectDbMode === "manual" ? "project" : "manual",
                          )
                        }
                        className="text-[11px] text-white/50 hover:text-white/55 transition-colors"
                      >
                        {projectDbMode === "manual"
                          ? "Use project connection"
                          : "Or connect manually"}
                      </button>
                      <div className="flex-1 h-px bg-white/5" />
                    </div>

                    <button
                      type="button"
                      data-testid="masking-open-saved-connections"
                      onClick={() => setShowSharedConnectDialog(true)}
                      className="text-[11px] text-white/50 hover:text-white/55 transition-colors underline"
                    >
                      Or pick a connection saved by another agent…
                    </button>
                  </div>
                )}

                {/* ── Manual DB form (shown when no project config or mode is manual) ── */}
                {(!projectDbConfig || projectDbMode === "manual") && (
                  <div
                    className="glass rounded-xl p-6 space-y-4"
                    data-testid="masking-db-form"
                  >
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs text-white/55 mb-1.5">
                          Database Type
                        </label>
                        <select
                          aria-label="Database Type"
                          data-testid="masking-db-type"
                          value={dbParams.db_type}
                          onChange={(e) =>
                            setDbParams((p) => ({
                              ...p,
                              db_type: e.target.value,
                            }))
                          }
                          className="w-full bg-surface-200 border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
                        >
                          <option value="postgresql">PostgreSQL</option>
                          <option value="mysql">MySQL</option>
                          <option value="mssql">SQL Server</option>
                          <option value="oracle">Oracle</option>
                          <option value="snowflake">Snowflake</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs text-white/55 mb-1.5">
                          Host
                        </label>
                        <input
                          aria-label="Host"
                          data-testid="masking-db-host"
                          value={dbParams.host}
                          onChange={(e) =>
                            setDbParams((p) => ({ ...p, host: e.target.value }))
                          }
                          placeholder="localhost"
                          className="w-full bg-surface-200 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-white/20"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-white/55 mb-1.5">
                          Port
                        </label>
                        <input
                          aria-label="Port"
                          data-testid="masking-db-port"
                          type="number"
                          value={dbParams.port}
                          onChange={(e) =>
                            setDbParams((p) => ({
                              ...p,
                              port: parseInt(e.target.value) || 0,
                            }))
                          }
                          className="w-full bg-surface-200 border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-white/55 mb-1.5">
                          Database
                        </label>
                        <input
                          aria-label="Database"
                          data-testid="masking-db-database"
                          value={dbParams.database}
                          onChange={(e) =>
                            setDbParams((p) => ({
                              ...p,
                              database: e.target.value,
                            }))
                          }
                          className="w-full bg-surface-200 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-white/20"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-white/55 mb-1.5">
                          Schema
                        </label>
                        <input
                          aria-label="Schema"
                          data-testid="masking-db-schema"
                          value={dbParams.schema_name}
                          onChange={(e) =>
                            setDbParams((p) => ({
                              ...p,
                              schema_name: e.target.value,
                            }))
                          }
                          className="w-full bg-surface-200 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-white/20"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-white/55 mb-1.5">
                          Username
                        </label>
                        <input
                          aria-label="Username"
                          data-testid="masking-db-username"
                          value={dbParams.username}
                          onChange={(e) =>
                            setDbParams((p) => ({
                              ...p,
                              username: e.target.value,
                            }))
                          }
                          className="w-full bg-surface-200 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-white/20"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-white/55 mb-1.5">
                          Password
                        </label>
                        <input
                          aria-label="Password"
                          data-testid="masking-db-password"
                          type="password"
                          value={dbParams.password}
                          onChange={(e) =>
                            setDbParams((p) => ({
                              ...p,
                              password: e.target.value,
                            }))
                          }
                          className="w-full bg-surface-200 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-white/20"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-white/55 mb-1.5">
                          Table (optional)
                        </label>
                        <input
                          aria-label="Table"
                          data-testid="masking-db-table"
                          value={dbParams.table || ""}
                          onChange={(e) =>
                            setDbParams((p) => ({
                              ...p,
                              table: e.target.value || undefined,
                            }))
                          }
                          placeholder="Leave blank to list all"
                          className="w-full bg-surface-200 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-white/20"
                        />
                      </div>
                    </div>
                    <button
                      data-testid="masking-db-connect"
                      onClick={handleDbConnect}
                      className="w-full py-3 bg-brand-500 hover:bg-brand-600 text-white rounded-xl font-medium transition-colors flex items-center justify-center gap-2"
                    >
                      <Database size={16} /> Connect
                    </button>
                    {dbTables.length > 0 && (
                      <div className="space-y-2 mt-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Table2 size={14} className="text-brand-500" />
                          <span className="text-xs font-semibold text-white/60">
                            Select tables ({dbTables.length} found)
                          </span>
                        </div>
                        <div className="relative mb-2">
                          <Search
                            size={14}
                            className="absolute left-3 top-1/2 -translate-y-1/2 text-white/50"
                          />
                          <input
                            value={dbTableSearch}
                            onChange={(e) => setDbTableSearch(e.target.value)}
                            placeholder="Search tables..."
                            className="w-full bg-surface-200 border border-white/10 rounded-lg pl-9 pr-3 py-2 text-xs text-white placeholder-white/20"
                          />
                        </div>
                        {selectedDbTables.size > 0 && (
                          <div className="text-xs text-brand-400 flex items-center gap-1.5">
                            <CheckCircle2 size={12} />
                            {selectedDbTables.size} table
                            {selectedDbTables.size > 1 ? "s" : ""} selected —
                            selecting more than one masks them together with
                            shared relationship detection
                          </div>
                        )}
                        <div className="max-h-[300px] overflow-y-auto custom-scrollbar space-y-1">
                          {dbTables
                            .filter((t) =>
                              t
                                .toLowerCase()
                                .includes(dbTableSearch.toLowerCase()),
                            )
                            .map((t) => (
                              <button
                                key={t}
                                onClick={() => toggleDbTableSelection(t)}
                                className={`w-full text-left px-4 py-2.5 rounded-lg text-xs transition-all flex items-center gap-2 ${
                                  selectedDbTables.has(t)
                                    ? "bg-brand-500/15 border border-brand-500/30 text-brand-400"
                                    : "bg-surface-200 border border-transparent text-white/60 hover:text-white hover:bg-surface-300"
                                }`}
                              >
                                <div
                                  className={`w-4 h-4 rounded-full border flex items-center justify-center flex-shrink-0 ${
                                    selectedDbTables.has(t)
                                      ? "border-brand-500"
                                      : "border-white/20"
                                  }`}
                                >
                                  {selectedDbTables.has(t) && (
                                    <div className="w-2 h-2 rounded-full bg-brand-500" />
                                  )}
                                </div>
                                {t}
                              </button>
                            ))}
                        </div>
                        <button
                          onClick={() =>
                            handleTablesSelected(Array.from(selectedDbTables))
                          }
                          disabled={selectedDbTables.size === 0}
                          className="w-full py-2.5 bg-brand-600 hover:bg-brand-700 disabled:bg-gray-700 disabled:text-white/50 text-white rounded-xl font-medium transition-colors flex items-center justify-center gap-2 text-sm"
                        >
                          <Database size={14} />
                          {selectedDbTables.size === 0
                            ? "Select tables to continue"
                            : selectedDbTables.size === 1
                              ? `Mask "${Array.from(selectedDbTables)[0]}"`
                              : `Mask ${selectedDbTables.size} tables`}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ════════════════════ STEP 2 (multi-table only): Relationship Detection ════════════════════ */}
        {step === STEP_RELATIONSHIPS && isMultiTable && !loading && (
          <div className="space-y-4">
            <div className="glass rounded-xl p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <Link2 size={18} className="text-brand-500" />
                  <div>
                    <h2 className="text-sm font-semibold text-white">
                      Detect Table Relationships
                    </h2>
                    <p className="text-[11px] text-white/50">
                      {tableProfiles.length} tables selected — identify
                      primary/foreign keys before configuring masking, so linked
                      columns can be kept consistent.
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    handleLoadConfigs();
                    setShowLoadModal(true);
                  }}
                  className="px-3 py-1.5 bg-surface-200 hover:bg-surface-300 rounded-lg text-xs text-white/55 hover:text-white transition flex items-center gap-1.5"
                  title="Load a previously saved masking configuration"
                >
                  <FolderOpen size={12} /> Load Saved Configuration
                </button>
              </div>

              {/* Selected tables summary */}
              <div className="flex flex-wrap gap-2 mb-4">
                {tableProfiles.map((tp) => (
                  <span
                    key={tp.table_name}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-surface-200 border border-white/5 rounded-lg text-[11px] text-white/60"
                  >
                    <Table2 size={10} className="text-brand-500" />
                    {tp.table_name}
                    <span className="text-white/50">
                      · {tp.row_count.toLocaleString()} rows
                    </span>
                  </span>
                ))}
              </div>

              <button
                onClick={handleDetectRelationships}
                disabled={relationshipsLoading}
                className="px-4 py-2.5 bg-brand-500 hover:bg-brand-600 disabled:opacity-50 rounded-xl text-sm font-medium text-white transition flex items-center gap-2"
              >
                {relationshipsLoading ? (
                  <>
                    <Loader2 size={14} className="animate-spin" /> Detecting…
                  </>
                ) : (
                  <>
                    <Search size={14} />{" "}
                    {relationships
                      ? "Re-run Detection"
                      : "Detect Relationships"}
                  </>
                )}
              </button>

              {relationships && (
                <div className="mt-4 space-y-3">
                  {/* PK candidates */}
                  <div>
                    <p className="text-[11px] uppercase tracking-wider text-white/50 mb-1.5">
                      Likely primary keys
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(relationships.pk_candidates).map(
                        ([table, cands]) => {
                          const top = cands[0];
                          return (
                            <span
                              key={table}
                              className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-surface-200 rounded-lg text-[11px]"
                            >
                              <Table2 size={10} className="text-brand-500" />
                              <span className="text-white/60">{table}</span>
                              {top ? (
                                <span className="text-brand-400 font-mono">
                                  {top.columns.join(", ")}
                                </span>
                              ) : (
                                <span className="text-white/50">
                                  no clear key
                                </span>
                              )}
                            </span>
                          );
                        },
                      )}
                    </div>
                  </div>

                  {/* FK candidates */}
                  <div>
                    <p className="text-[11px] uppercase tracking-wider text-white/50 mb-1.5">
                      Detected relationships (FK-style links)
                    </p>
                    {relationships.fk_candidates.length === 0 ? (
                      <p className="text-[11px] text-white/50">
                        No cross-table relationships detected between the
                        selected tables.
                      </p>
                    ) : (
                      <div className="space-y-1.5">
                        {relationships.fk_candidates.map((fk, idx) => (
                          <div
                            key={idx}
                            className="flex items-center gap-2 bg-surface-200 rounded-lg px-3 py-2 text-[11px]"
                          >
                            <span className="font-mono text-white/70">
                              {fk.source_table}.{fk.source_columns.join(", ")}
                            </span>
                            <Link2
                              size={10}
                              className="text-brand-500 flex-shrink-0"
                            />
                            <span className="font-mono text-white/70">
                              {fk.target_table}.{fk.target_columns.join(", ")}
                            </span>
                            <span
                              className={`px-1.5 py-0.5 rounded text-[11px] ml-auto ${
                                fk.type === "declared"
                                  ? "bg-green-500/10 text-green-400"
                                  : "bg-amber-500/10 text-amber-400"
                              }`}
                            >
                              {fk.type} · {Math.round(fk.confidence * 100)}%
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <p className="text-[11px] text-white/50">
                    Configuring the same masking technique — and, for encryption
                    techniques, the same key — on both sides of a link keeps
                    masked values matching across tables. If a linked pair ends
                    up configured inconsistently, Execute will warn you about it
                    after the run rather than block you.
                  </p>
                </div>
              )}
            </div>

            <div className="glass rounded-xl p-4 flex items-center justify-between sticky bottom-4">
              <button
                onClick={() => setStep(1)}
                className="px-4 py-2 bg-surface-200 hover:bg-surface-300 rounded-lg text-xs text-white/55 hover:text-white transition"
              >
                Back
              </button>
              <button
                onClick={() => setStep(STEP_CONFIGURE)}
                className="px-6 py-2.5 bg-brand-500 hover:bg-brand-600 rounded-xl text-sm font-medium text-white transition flex items-center gap-2"
              >
                Continue to Configure <ArrowRight size={14} />
              </button>
            </div>
          </div>
        )}

        {/* ════════════════════ STEP 2 (multi-sheet only): Sheet Selection ════════════════════ */}
        {step === STEP_SHEETS && isMultiSheet && !loading && (
          <div className="space-y-4">
            <div className="glass rounded-xl p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <Sheet size={18} className="text-brand-500" />
                  <div>
                    <h2 className="text-sm font-semibold text-white">
                      Select Sheets to Mask
                    </h2>
                    <p className="text-[11px] text-white/50 flex items-center gap-1">
                      {uploadResponse?.file_name} — {sheetInfoList.length}{" "}
                      sheets found
                      <span className="text-white/50 ml-2">
                        · Drag to reorder
                      </span>
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() =>
                      setSelectedSheets(
                        new Set(sheetInfoList.map((s) => s.name)),
                      )
                    }
                    className="px-3 py-1.5 bg-surface-200 hover:bg-surface-300 rounded-lg text-[11px] text-white/55 hover:text-white transition"
                  >
                    Select All
                  </button>
                  <button
                    onClick={() => setSelectedSheets(new Set())}
                    className="px-3 py-1.5 bg-surface-200 hover:bg-surface-300 rounded-lg text-[11px] text-white/55 hover:text-white transition"
                  >
                    Clear
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                {sheetInfoList.map((sheet, idx) => {
                  const checked = selectedSheets.has(sheet.name);
                  return (
                    <div
                      key={sheet.name}
                      draggable
                      onDragStart={() => handleSheetDragStart(idx)}
                      onDragOver={(e) => handleSheetDragOver(e, idx)}
                      onDrop={() => handleSheetDrop(idx, "info")}
                      onDragEnd={handleSheetDragEnd}
                      className={`flex items-center gap-3 px-4 py-3 rounded-xl cursor-pointer transition-all border ${
                        dragOverSheetIdx === idx
                          ? "border-brand-500/60 bg-brand-500/10"
                          : checked
                            ? "bg-brand-500/5 border-brand-500/30 ring-1 ring-brand-500/20"
                            : "bg-surface-200/50 border-white/5 hover:border-white/10"
                      } ${dragSheetIdx === idx ? "opacity-40" : ""}`}
                    >
                      <GripVertical
                        size={14}
                        className="text-white/50 hover:text-white/55 cursor-grab flex-shrink-0"
                      />
                      <div
                        onClick={() => toggleSheetSelection(sheet.name)}
                        className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                          checked
                            ? "bg-brand-500 border-brand-500"
                            : "border-white/20"
                        }`}
                      >
                        {checked && (
                          <CheckCircle2 size={12} className="text-white" />
                        )}
                      </div>
                      <div
                        className="flex-1 min-w-0"
                        onClick={() => toggleSheetSelection(sheet.name)}
                      >
                        <p className="text-sm font-medium text-white truncate">
                          {sheet.name}
                        </p>
                        <p className="text-[11px] text-white/50">
                          {sheet.col_count} columns
                          {sheet.columns.length > 0 && (
                            <span className="ml-2 text-white/50">
                              ({sheet.columns.slice(0, 5).join(", ")}
                              {sheet.columns.length > 5
                                ? `, +${sheet.columns.length - 5} more`
                                : ""}
                              )
                            </span>
                          )}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-[11px] text-white/50 font-mono">
                          #{idx + 1}
                        </span>
                        <span className="text-[11px] text-white/50 px-2 py-1 bg-surface-300 rounded font-mono">
                          {sheet.col_count} cols
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Bottom action bar */}
            <div className="glass rounded-xl p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setStep(1)}
                  className="px-3 py-1.5 bg-surface-200 hover:bg-surface-300 rounded-lg text-xs text-white/55 hover:text-white transition"
                >
                  Back
                </button>
                <span className="text-xs text-white/50">
                  {selectedSheets.size} of {sheetInfoList.length} sheets
                  selected
                </span>
              </div>
              <button
                onClick={handleLoadSelectedSheets}
                disabled={selectedSheets.size === 0}
                className="px-6 py-2.5 bg-brand-500 hover:bg-brand-600 disabled:opacity-30 disabled:cursor-not-allowed rounded-xl text-sm font-medium text-white transition flex items-center gap-2"
              >
                <ShieldCheck size={14} /> Continue to Configure
              </button>
            </div>
          </div>
        )}

        {/* ════════════════════ CONFIGURE STEP ════════════════════ */}
        {step === STEP_CONFIGURE && !loading && (
          <div className="space-y-4">
            {/* Summary bar */}
            <div className="glass rounded-xl p-4 flex items-center justify-between">
              <div className="flex items-center gap-4">
                {isMultiSheet ? (
                  <div className="text-xs text-white/55">
                    <span className="text-white font-bold">
                      {sheetProfiles.length}
                    </span>{" "}
                    sheets ·{" "}
                    <span className="text-brand-500 font-bold">
                      {totalSelectedColumns}
                    </span>{" "}
                    columns selected for masking
                  </div>
                ) : isMultiTable ? (
                  <div className="text-xs text-white/55">
                    <span className="text-white font-bold">
                      {tableProfiles.length}
                    </span>{" "}
                    tables ·{" "}
                    <span className="text-brand-500 font-bold">
                      {totalSelectedColumns}
                    </span>{" "}
                    columns selected for masking
                  </div>
                ) : (
                  <div className="text-xs text-white/55">
                    <span className="text-white font-bold">
                      {uploadResponse?.row_count?.toLocaleString()}
                    </span>{" "}
                    rows ×{" "}
                    <span className="text-white font-bold">
                      {uploadResponse?.col_count}
                    </span>{" "}
                    columns ·{" "}
                    <span className="text-brand-500 font-bold">
                      {columnConfigs.size}
                    </span>{" "}
                    selected for masking
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() =>
                    setStep(
                      isMultiSheet
                        ? STEP_SHEETS
                        : isMultiTable
                          ? STEP_RELATIONSHIPS
                          : 1,
                    )
                  }
                  className="px-3 py-1.5 bg-surface-200 hover:bg-surface-300 rounded-lg text-xs text-white/55 hover:text-white transition"
                >
                  Back
                </button>
                <button
                  onClick={() => setStep(STEP_REVIEW)}
                  disabled={totalSelectedColumns === 0}
                  className="px-4 py-1.5 bg-brand-500 hover:bg-brand-600 disabled:opacity-30 disabled:cursor-not-allowed rounded-lg text-xs font-medium text-white transition flex items-center gap-1.5"
                >
                  <ClipboardCheck size={12} /> Review & Preview
                </button>
              </div>
            </div>

            {/* Sheet tabs (multi-sheet only) — draggable for reorder */}
            {isMultiSheet && sheetProfiles.length > 0 && (
              <div className="flex items-center gap-1 overflow-x-auto custom-scrollbar pb-1">
                {sheetProfiles.map((sp, idx) => {
                  const count =
                    sheetColumnConfigs.get(sp.sheet_name)?.size || 0;
                  const isPassThrough = passThroughSheets.has(sp.sheet_name);
                  return (
                    <div
                      key={sp.sheet_name}
                      draggable
                      onDragStart={() => handleSheetDragStart(idx)}
                      onDragOver={(e) => handleSheetDragOver(e, idx)}
                      onDrop={() => handleSheetDrop(idx, "profile")}
                      onDragEnd={handleSheetDragEnd}
                      onClick={() => {
                        setActiveSheetTab(sp.sheet_name);
                        setColumnSearch("");
                        setBulkMode(false);
                        setBulkSelected(new Set());
                      }}
                      className={`flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-medium whitespace-nowrap transition-all border cursor-pointer ${
                        dragOverSheetIdx === idx
                          ? "border-brand-500/60 bg-brand-500/15"
                          : isPassThrough
                            ? activeSheetTab === sp.sheet_name
                              ? "bg-amber-500/15 border-amber-500/40 text-amber-400"
                              : "bg-amber-500/5 border-amber-500/15 text-amber-400/60 hover:text-amber-400/80"
                            : activeSheetTab === sp.sheet_name
                              ? "bg-brand-500/10 border-brand-500/30 text-brand-500"
                              : "bg-surface-200 border-white/5 text-white/55 hover:text-white/60"
                      } ${dragSheetIdx === idx ? "opacity-40" : ""}`}
                    >
                      <GripVertical
                        size={10}
                        className="text-white/50 hover:text-white/50 cursor-grab flex-shrink-0"
                      />
                      <FileSpreadsheet size={12} />
                      {sp.sheet_name}
                      {isPassThrough ? (
                        <span className="ml-1 px-1.5 py-0.5 bg-amber-500/20 text-amber-400 rounded text-[11px] font-bold">
                          pass-through
                        </span>
                      ) : count > 0 ? (
                        <span className="ml-1 px-1.5 py-0.5 bg-brand-500/20 text-brand-500 rounded text-[11px] font-bold">
                          {count}
                        </span>
                      ) : null}
                      <span className="text-[11px] text-white/50">
                        {sp.row_count.toLocaleString()} rows
                      </span>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Table tabs (multi-table only, #603 item 3) — mirrors the
             * sheet tabs above minus drag-reorder and pass-through, neither
             * of which has a multi-table backend counterpart. */}
            {isMultiTable && tableProfiles.length > 0 && (
              <div className="flex items-center gap-1 overflow-x-auto custom-scrollbar pb-1">
                {tableProfiles.map((tp) => {
                  const count =
                    tableColumnConfigs.get(tp.table_name)?.size || 0;
                  return (
                    <div
                      key={tp.table_name}
                      onClick={() => {
                        setActiveTableTab(tp.table_name);
                        setColumnSearch("");
                        setBulkMode(false);
                        setBulkSelected(new Set());
                      }}
                      className={`flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-medium whitespace-nowrap transition-all border cursor-pointer ${
                        activeTableTab === tp.table_name
                          ? "bg-brand-500/10 border-brand-500/30 text-brand-500"
                          : "bg-surface-200 border-white/5 text-white/55 hover:text-white/60"
                      }`}
                    >
                      <Table2 size={12} />
                      {tp.table_name}
                      {count > 0 && (
                        <span className="ml-1 px-1.5 py-0.5 bg-brand-500/20 text-brand-500 rounded text-[11px] font-bold">
                          {count}
                        </span>
                      )}
                      <span className="text-[11px] text-white/50">
                        {tp.row_count.toLocaleString()} rows
                      </span>
                    </div>
                  );
                })}
              </div>
            )}

            {/* ── Lineage & Date Links control panel ── */}
            <div className="glass rounded-xl p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  {/* Lineage toggle */}
                  <label className="flex items-center gap-2 cursor-pointer">
                    <div
                      onClick={() => setLineageEnabled(!lineageEnabled)}
                      className={`w-9 h-5 rounded-full transition-all cursor-pointer flex items-center ${
                        lineageEnabled
                          ? "bg-brand-500 justify-end"
                          : "bg-surface-300 justify-start"
                      }`}
                    >
                      <div
                        className={`w-4 h-4 rounded-full bg-white mx-0.5 transition-all shadow-sm`}
                      />
                    </div>
                    <span className="text-xs text-white/50 flex items-center gap-1.5">
                      <Link2
                        size={12}
                        className={
                          lineageEnabled ? "text-brand-500" : "text-white/50"
                        }
                      />
                      Maintain Lineage
                    </span>
                  </label>
                  {lineageEnabled && (
                    <span className="text-[11px] text-brand-500/60">
                      Same original values will map to same masked values across
                      columns
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {/* Date link button */}
                  <button
                    onClick={() => setShowDateLinkBuilder(!showDateLinkBuilder)}
                    className={`px-3 py-1.5 rounded-lg text-[11px] flex items-center gap-1.5 transition border ${
                      dateLinks.length > 0
                        ? "bg-amber-500/10 border-amber-500/30 text-amber-400"
                        : "bg-surface-200 border-white/5 text-white/50 hover:text-white/50"
                    }`}
                  >
                    <CalendarClock size={12} /> Date Links
                    {dateLinks.length > 0 && (
                      <span className="px-1.5 py-0.5 bg-amber-500/20 rounded text-[11px] font-bold">
                        {dateLinks.length}
                      </span>
                    )}
                  </button>
                  <button
                    onClick={() => autoDetectDateLinks()}
                    className="px-3 py-1.5 rounded-lg text-[11px] flex items-center gap-1.5 transition border bg-surface-200 border-white/5 text-white/50 hover:text-amber-400 hover:border-amber-500/30"
                    title="Auto-detect date pairs (START/END, BEGIN/END, FROM/TO)"
                  >
                    <Wand2 size={12} /> Auto-detect
                  </button>
                </div>
              </div>

              {/* Lineage groups display */}
              {lineageEnabled && lineageGroups.length > 0 && (
                <div className="space-y-1.5">
                  <p className="text-[11px] text-white/50 uppercase tracking-wider">
                    Lineage Groups
                  </p>
                  {lineageGroups.map((group, idx) => (
                    <div
                      key={idx}
                      className="flex items-center gap-2 bg-surface-200 rounded-lg px-3 py-2"
                    >
                      <Link2
                        size={10}
                        className="text-brand-500 flex-shrink-0"
                      />
                      <div className="flex flex-wrap gap-1 flex-1">
                        {group.map((col) => (
                          <span
                            key={col}
                            className="px-1.5 py-0.5 bg-brand-500/10 text-brand-500 rounded text-[11px] font-mono"
                          >
                            {col}
                          </span>
                        ))}
                      </div>
                      <button
                        onClick={() =>
                          setLineageGroups((prev) =>
                            prev.filter((_, i) => i !== idx),
                          )
                        }
                        className="text-white/50 hover:text-red-400 transition"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Date link builder */}
              {showDateLinkBuilder && (
                <div className="bg-surface-200 rounded-xl p-3 space-y-2 border border-amber-500/10">
                  <p className="text-[11px] font-medium text-amber-400 flex items-center gap-1.5">
                    <CalendarClock size={12} /> Link Date Columns (same shift
                    offset preserves ordering)
                  </p>
                  {dateLinks.map((link, idx) => (
                    <div
                      key={idx}
                      className="flex items-center gap-2 bg-surface-300 rounded-lg px-3 py-2"
                    >
                      <span className="text-[11px] text-amber-400 font-mono">
                        {link.primary}
                      </span>
                      <span className="text-[11px] text-white/50">→</span>
                      {link.linked.map((l) => (
                        <span
                          key={l}
                          className="px-1.5 py-0.5 bg-amber-500/10 text-amber-400 rounded text-[11px] font-mono"
                        >
                          {l}
                        </span>
                      ))}
                      <button
                        onClick={() => removeDateLink(idx)}
                        className="ml-auto text-white/50 hover:text-red-400"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                  <div className="grid grid-cols-12 gap-2 items-end">
                    <div className="col-span-4">
                      <label className="text-[11px] text-white/50 block mb-1">
                        Primary Date
                      </label>
                      <select
                        value={dateLinkPrimary}
                        onChange={(e) => setDateLinkPrimary(e.target.value)}
                        className="w-full bg-surface-300 border border-white/10 rounded px-2 py-1.5 text-[11px] text-white"
                      >
                        <option value="">Select...</option>
                        {getDateColumns().map((dc) => (
                          <option
                            key={`${dc.sheet || ""}-${dc.column}`}
                            value={dc.column}
                          >
                            {dc.sheet ? `[${dc.sheet}] ` : ""}
                            {dc.column}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-span-6">
                      <label className="text-[11px] text-white/50 block mb-1">
                        Linked Dates
                      </label>
                      <div className="flex flex-wrap gap-1">
                        {getDateColumns()
                          .filter((dc) => dc.column !== dateLinkPrimary)
                          .map((dc) => {
                            const isLinked = dateLinkLinked.includes(dc.column);
                            return (
                              <button
                                key={`${dc.sheet || ""}-${dc.column}`}
                                onClick={() =>
                                  setDateLinkLinked((prev) =>
                                    isLinked
                                      ? prev.filter((c) => c !== dc.column)
                                      : [...prev, dc.column],
                                  )
                                }
                                className={`px-2 py-1 rounded text-[11px] border transition ${
                                  isLinked
                                    ? "bg-amber-500/15 border-amber-500/30 text-amber-400"
                                    : "bg-surface-300 border-white/5 text-white/50 hover:text-white/55"
                                }`}
                              >
                                {dc.sheet ? `[${dc.sheet}] ` : ""}
                                {dc.column}
                              </button>
                            );
                          })}
                      </div>
                    </div>
                    <div className="col-span-2">
                      <button
                        onClick={addDateLink}
                        disabled={
                          !dateLinkPrimary || dateLinkLinked.length === 0
                        }
                        className="w-full px-3 py-1.5 bg-amber-500/20 hover:bg-amber-500/30 disabled:opacity-30 rounded-lg text-[11px] text-amber-400 transition border border-amber-500/20"
                      >
                        + Link
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Pass-through toggle for active sheet (multi-sheet only) */}
            {isMultiSheet && (
              <div
                className={`rounded-xl p-3 flex items-center justify-between transition-all border ${
                  passThroughSheets.has(activeSheetTab)
                    ? "bg-amber-500/10 border-amber-500/25"
                    : "bg-surface-200 border-white/5"
                }`}
              >
                <div className="flex items-center gap-2">
                  <ArrowRight
                    size={14}
                    className={
                      passThroughSheets.has(activeSheetTab)
                        ? "text-amber-400"
                        : "text-white/50"
                    }
                  />
                  <div>
                    <p
                      className={`text-xs font-medium ${passThroughSheets.has(activeSheetTab) ? "text-amber-400" : "text-white/55"}`}
                    >
                      Pass-Through Sheet
                    </p>
                    <p className="text-[11px] text-white/50">
                      {passThroughSheets.has(activeSheetTab)
                        ? "This sheet will be included in output without any masking applied"
                        : "Mark this sheet to include in output without masking (for migration/reference data)"}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setPassThroughSheets((prev) => {
                      const next = new Set(prev);
                      if (next.has(activeSheetTab)) {
                        next.delete(activeSheetTab);
                      } else {
                        next.add(activeSheetTab);
                        // Clear any masking configs for this sheet when marking pass-through
                        setSheetColumnConfigs((prev) => {
                          const m = new Map(prev);
                          m.set(activeSheetTab, new Map());
                          return m;
                        });
                      }
                      return next;
                    });
                  }}
                  className={`px-3 py-1.5 rounded-lg text-[11px] font-medium transition border ${
                    passThroughSheets.has(activeSheetTab)
                      ? "bg-amber-500/20 border-amber-500/40 text-amber-400 hover:bg-amber-500/30"
                      : "bg-surface-300 border-white/10 text-white/50 hover:text-white/50 hover:border-white/20"
                  }`}
                >
                  {passThroughSheets.has(activeSheetTab)
                    ? "✓ Pass-Through"
                    : "Mark as Pass-Through"}
                </button>
              </div>
            )}

            {/* Column search + smart actions */}
            {!isMultiSheet || !passThroughSheets.has(activeSheetTab) ? (
              <>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <div className="relative flex-1">
                      <Search
                        size={14}
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-white/50"
                      />
                      <input
                        value={columnSearch}
                        onChange={(e) => setColumnSearch(e.target.value)}
                        placeholder={`Search columns${
                          isMultiSheet
                            ? ` in ${activeSheetTab}`
                            : isMultiTable
                              ? ` in ${activeTableTab}`
                              : ""
                        }...`}
                        className="w-full bg-surface-200 border border-white/10 rounded-xl pl-9 pr-3 py-2.5 text-sm text-white placeholder-white/20"
                      />
                    </div>
                    {/* Quick preset button */}
                    <button
                      onClick={() => setShowPresetsPanel(!showPresetsPanel)}
                      className={`px-3 py-2.5 rounded-xl text-xs flex items-center gap-1.5 transition border ${
                        showPresetsPanel
                          ? "bg-purple-500/15 border-purple-500/30 text-purple-300"
                          : "bg-surface-200 border-white/10 text-white/50 hover:text-white/50"
                      }`}
                    >
                      <Wand2 size={13} /> Presets
                    </button>
                    {/* Bulk mode toggle */}
                    <button
                      onClick={() => {
                        setBulkMode(!bulkMode);
                        setBulkSelected(new Set());
                      }}
                      className={`px-3 py-2.5 rounded-xl text-xs flex items-center gap-1.5 transition border ${
                        bulkMode
                          ? "bg-brand-500/15 border-brand-500/30 text-brand-500"
                          : "bg-surface-200 border-white/10 text-white/50 hover:text-white/50"
                      }`}
                    >
                      <ListChecks size={13} /> Bulk
                    </button>
                  </div>

                  {/* ── Quick Masking Presets Panel ── */}
                  {showPresetsPanel && (
                    <div className="glass rounded-xl p-3 space-y-2 animate-in slide-in-from-top-2">
                      <div className="flex items-center justify-between">
                        <p className="text-[11px] font-medium text-purple-400 flex items-center gap-1.5">
                          <Wand2 size={12} /> Quick Masking Presets
                        </p>
                        <button
                          onClick={() => setShowPresetsPanel(false)}
                          className="text-white/50 hover:text-white/50"
                        >
                          <X size={14} />
                        </button>
                      </div>
                      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
                        {MASKING_PRESETS.map((preset) => (
                          <button
                            key={preset.id}
                            onClick={() => {
                              preset.apply();
                              setShowPresetsPanel(false);
                            }}
                            className={`p-3 rounded-xl border bg-surface-200 border-white/5 hover:border-${preset.color}-500/30 hover:bg-${preset.color}-500/5 text-left transition-all group`}
                          >
                            <div className="flex items-center gap-2 mb-1.5">
                              {preset.icon}
                              <span className="text-xs font-medium text-white/70 group-hover:text-white">
                                {preset.label}
                              </span>
                            </div>
                            <p className="text-[11px] text-white/50 group-hover:text-white/55 leading-relaxed">
                              {preset.desc}
                            </p>
                          </button>
                        ))}
                      </div>
                      <p className="text-[11px] text-white/50 text-center">
                        Presets auto-configure columns based on sensitivity
                        analysis · Can be customized after applying
                      </p>
                    </div>
                  )}

                  {/* ── Bulk Actions Bar ── */}
                  {bulkMode && (
                    <div className="glass rounded-xl p-3 space-y-2 animate-in slide-in-from-top-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <p className="text-[11px] font-medium text-brand-500 flex items-center gap-1.5">
                            <SquareCheck size={12} /> Bulk Select
                          </p>
                          <span className="text-[11px] text-white/50">
                            {bulkSelected.size} selected
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <button
                            onClick={bulkSelectAll}
                            className="px-2 py-1 bg-surface-200 rounded text-[11px] text-white/50 hover:text-white/50 transition"
                          >
                            All
                          </button>
                          <button
                            onClick={bulkClearSelection}
                            className="px-2 py-1 bg-surface-200 rounded text-[11px] text-white/50 hover:text-white/50 transition"
                          >
                            None
                          </button>
                          <button
                            onClick={() => {
                              setBulkMode(false);
                              setBulkSelected(new Set());
                            }}
                            className="text-white/50 hover:text-white/50 ml-1"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      </div>
                      {/* Select by sensitivity */}
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[11px] text-white/50">
                          Select by:
                        </span>
                        {["high", "medium", "low", "none"].map((level) => {
                          const count = filteredColumns.filter(
                            (c) => c.sensitivity === level,
                          ).length;
                          if (count === 0) return null;
                          const colors: Record<string, string> = {
                            high: "bg-red-500/15 border-red-500/30 text-red-400",
                            medium:
                              "bg-amber-500/15 border-amber-500/30 text-amber-400",
                            low: "bg-blue-500/15 border-blue-500/30 text-blue-400",
                            none: "bg-surface-300 border-white/10 text-white/50",
                          };
                          return (
                            <button
                              key={level}
                              onClick={() => bulkSelectBySensitivity(level)}
                              className={`px-2 py-1 rounded-lg text-[11px] border transition ${colors[level]}`}
                            >
                              {level} ({count})
                            </button>
                          );
                        })}
                      </div>
                      {/* Bulk apply technique */}
                      {bulkSelected.size > 0 && (
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-[11px] text-white/50">
                            Apply:
                          </span>
                          {techniques.slice(0, 6).map((t) => (
                            <button
                              key={t.id}
                              onClick={() => bulkApplyTechnique(t.id)}
                              className="px-2 py-1 bg-surface-200 hover:bg-brand-500/10 border border-white/5 hover:border-brand-500/30 rounded text-[11px] text-white/50 hover:text-brand-400 transition"
                            >
                              {t.label}
                            </button>
                          ))}
                          <button
                            onClick={bulkRemoveColumns}
                            className="px-2 py-1 bg-red-500/10 hover:bg-red-500/20 border border-red-500/20 rounded text-[11px] text-red-400 transition flex items-center gap-1"
                          >
                            <Trash2 size={9} /> Remove
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Column cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {filteredColumns.map((col) => {
                    const isSelected = activeConfigs.has(col.column);
                    const config = activeConfigs.get(col.column);
                    const selectedTechnique = techniques.find(
                      (t) => t.id === config?.technique,
                    );
                    const isBulkChecked = bulkSelected.has(col.column);

                    return (
                      <div
                        key={col.column}
                        data-testid="masking-column-row"
                        data-column={col.column}
                        className={`glass rounded-xl overflow-hidden transition-all ${
                          isBulkChecked
                            ? "ring-1 ring-brand-500/60 bg-brand-500/5"
                            : isSelected
                              ? "ring-1 ring-brand-500/40"
                              : ""
                        }`}
                      >
                        <div
                          className="px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-surface-200/30 transition"
                          onClick={() =>
                            bulkMode
                              ? toggleBulkSelect(col.column)
                              : toggleColumn(col)
                          }
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            {bulkMode ? (
                              <div
                                className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all flex-shrink-0 ${
                                  isBulkChecked
                                    ? "bg-brand-500 border-brand-500"
                                    : "border-white/20 hover:border-white/40"
                                }`}
                              >
                                {isBulkChecked && (
                                  <CheckCircle2
                                    size={12}
                                    className="text-white"
                                  />
                                )}
                              </div>
                            ) : (
                              <div
                                className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all flex-shrink-0 ${
                                  isSelected
                                    ? "bg-brand-500 border-brand-500"
                                    : "border-white/20 hover:border-white/40"
                                }`}
                              >
                                {isSelected && (
                                  <CheckCircle2
                                    size={12}
                                    className="text-white"
                                  />
                                )}
                              </div>
                            )}
                            <div className="min-w-0">
                              <span className="text-sm font-mono text-white truncate block">
                                {col.column}
                              </span>
                              <span className="text-[11px] text-white/50">
                                {col.dtype}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            {sensitivityBadge(col.sensitivity)}
                          </div>
                        </div>

                        {isSelected && config && (
                          <div className="px-4 pb-3 space-y-3 border-t border-white/5 pt-3">
                            <div>
                              <label className="block text-[11px] text-white/50 mb-1 uppercase tracking-wider">
                                Masking Technique
                              </label>
                              {(() => {
                                const recommendedIds = getTechniquesForDtype(
                                  col.dtype,
                                );
                                const recommended = recommendedIds
                                  ? techniques.filter((t) =>
                                      recommendedIds.includes(t.id),
                                    )
                                  : techniques;
                                const others = recommendedIds
                                  ? techniques.filter(
                                      (t) => !recommendedIds.includes(t.id),
                                    )
                                  : [];
                                return (
                                  <select
                                    aria-label={`Masking technique for ${col.column}`}
                                    data-testid="masking-technique-select"
                                    data-column={col.column}
                                    value={config.technique}
                                    onChange={(e) =>
                                      setTechniqueForCol(
                                        col.column,
                                        e.target.value,
                                      )
                                    }
                                    className="w-full bg-surface-300 border border-white/10 rounded-lg px-3 py-2 text-xs text-white"
                                  >
                                    <optgroup
                                      label={`Recommended for ${col.dtype}`}
                                    >
                                      {recommended.map((t) => (
                                        <option key={t.id} value={t.id}>
                                          {t.label} — {t.description}
                                        </option>
                                      ))}
                                    </optgroup>
                                    {others.length > 0 && (
                                      <optgroup label="Other techniques">
                                        {others.map((t) => (
                                          <option key={t.id} value={t.id}>
                                            {t.label} — {t.description}
                                          </option>
                                        ))}
                                      </optgroup>
                                    )}
                                  </select>
                                );
                              })()}
                            </div>
                            {selectedTechnique &&
                              selectedTechnique.options.length > 0 && (
                                <div className="grid grid-cols-2 gap-2">
                                  {selectedTechnique.options.map((opt) => (
                                    <div
                                      key={opt.name}
                                      className={
                                        opt.name === "fake_type"
                                          ? "col-span-2"
                                          : ""
                                      }
                                    >
                                      <label className="block text-[11px] text-white/50 mb-1">
                                        {opt.label}
                                      </label>
                                      {opt.type === "select" ? (
                                        <select
                                          value={
                                            config.options[opt.name] ??
                                            opt.default
                                          }
                                          onChange={(e) =>
                                            setOptionForCol(
                                              col.column,
                                              opt.name,
                                              e.target.value,
                                            )
                                          }
                                          className="w-full bg-surface-200 border border-white/10 rounded px-2 py-1.5 text-xs text-white"
                                        >
                                          {opt.choices?.map((c) => (
                                            <option key={c} value={c}>
                                              {opt.name === "fake_type"
                                                ? (FAKE_TYPE_LABELS[c] ?? c)
                                                : c}
                                            </option>
                                          ))}
                                        </select>
                                      ) : opt.type === "number" ? (
                                        <input
                                          type="number"
                                          value={
                                            config.options[opt.name] ??
                                            opt.default
                                          }
                                          onChange={(e) =>
                                            setOptionForCol(
                                              col.column,
                                              opt.name,
                                              parseInt(e.target.value) || 0,
                                            )
                                          }
                                          className="w-full bg-surface-200 border border-white/10 rounded px-2 py-1.5 text-xs text-white"
                                        />
                                      ) : (
                                        <input
                                          type="text"
                                          value={
                                            config.options[opt.name] ??
                                            opt.default
                                          }
                                          onChange={(e) =>
                                            setOptionForCol(
                                              col.column,
                                              opt.name,
                                              e.target.value,
                                            )
                                          }
                                          className="w-full bg-surface-200 border border-white/10 rounded px-2 py-1.5 text-xs text-white"
                                        />
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}
                            {/* ── Copy from another column ── */}
                            <div>
                              <label className="block text-[11px] text-white/50 mb-1 flex items-center gap-1">
                                <Copy size={9} /> Copy rule from
                              </label>
                              <select
                                value=""
                                onChange={(e) => {
                                  if (e.target.value)
                                    handleCopyRuleFrom(
                                      col.column,
                                      e.target.value,
                                    );
                                }}
                                className="w-full bg-surface-200 border border-white/10 rounded px-2 py-1.5 text-xs text-white/55"
                              >
                                <option value="">
                                  Select column to copy from...
                                </option>
                                {(() => {
                                  const configured =
                                    getAllAvailableColumns().filter(
                                      (c) =>
                                        c.column !== col.column && c.technique,
                                    );
                                  const unconfigured =
                                    getAllAvailableColumns().filter(
                                      (c) =>
                                        c.column !== col.column && !c.technique,
                                    );
                                  return (
                                    <>
                                      {configured.length > 0 && (
                                        <optgroup label="Configured columns">
                                          {configured.map((c) => (
                                            <option
                                              key={`cfg-${c.sheet || ""}-${c.column}`}
                                              value={c.column}
                                            >
                                              {c.sheet ? `[${c.sheet}] ` : ""}
                                              {c.column} ({c.technique})
                                            </option>
                                          ))}
                                        </optgroup>
                                      )}
                                      {unconfigured.length > 0 && (
                                        <optgroup label="All columns">
                                          {unconfigured.map((c) => (
                                            <option
                                              key={`all-${c.sheet || ""}-${c.column}`}
                                              value={c.column}
                                            >
                                              {c.sheet ? `[${c.sheet}] ` : ""}
                                              {c.column} ({c.dtype})
                                            </option>
                                          ))}
                                        </optgroup>
                                      )}
                                    </>
                                  );
                                })()}
                              </select>
                            </div>

                            {/* ── Conditional Masking ── */}
                            <div>
                              <label className="block text-[11px] text-white/50 mb-1 flex items-center gap-1">
                                <Filter size={9} /> Conditional Filter{" "}
                                <span className="text-white/50">
                                  (optional)
                                </span>
                              </label>
                              <input
                                type="text"
                                placeholder='e.g. status == "active"'
                                value={config.filter_expression || ""}
                                onChange={(e) => {
                                  const cfg = activeConfigs.get(col.column);
                                  if (cfg) {
                                    const updated = new Map(activeConfigs);
                                    updated.set(col.column, {
                                      ...cfg,
                                      filter_expression:
                                        e.target.value || undefined,
                                    });
                                    setActiveConfigs(() => updated);
                                  }
                                }}
                                className="w-full bg-surface-200 border border-white/10 rounded px-2 py-1.5 text-xs text-white font-mono"
                              />
                            </div>

                            {/* ── Uniqueness / Lineage ── */}
                            <div className="flex items-center gap-4">
                              <label className="flex items-center gap-1.5 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={config.preserve_uniqueness || false}
                                  onChange={(e) => {
                                    const cfg = activeConfigs.get(col.column);
                                    if (cfg) {
                                      const updated = new Map(activeConfigs);
                                      updated.set(col.column, {
                                        ...cfg,
                                        preserve_uniqueness: e.target.checked,
                                      });
                                      setActiveConfigs(() => updated);
                                    }
                                  }}
                                  className="rounded border-white/20 bg-surface-300 text-brand-500 focus:ring-brand-500/30"
                                />
                                <span className="text-[11px] text-white/50">
                                  Preserve Uniqueness
                                </span>
                              </label>

                              {lineageEnabled && (
                                <span className="text-[11px] text-brand-500/40 flex items-center gap-1">
                                  <Link2 size={9} /> Lineage active
                                </span>
                              )}
                            </div>

                            <div>
                              <span className="text-[11px] text-white/50">
                                Samples:{" "}
                              </span>
                              <span className="text-[11px] text-white/50 font-mono">
                                {col.sample_values.slice(0, 3).join(", ")}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            ) : (
              <div className="glass rounded-xl p-8 text-center space-y-3">
                <div className="w-12 h-12 rounded-full bg-amber-500/10 flex items-center justify-center mx-auto">
                  <ArrowRight size={20} className="text-amber-400" />
                </div>
                <h4 className="text-sm font-medium text-amber-400">
                  Pass-Through Sheet
                </h4>
                <p className="text-[11px] text-white/50 max-w-sm mx-auto">
                  <strong className="text-white/50">{activeSheetTab}</strong> is
                  marked as pass-through. It will be included in the output file
                  without any masking applied — useful for reference data,
                  lookup tables, or sheets that don&apos;t contain sensitive
                  information.
                </p>
                <p className="text-[11px] text-white/50">
                  {(
                    sheetProfiles.find((sp) => sp.sheet_name === activeSheetTab)
                      ?.row_count || 0
                  ).toLocaleString()}{" "}
                  rows &middot;{" "}
                  {sheetProfiles.find((sp) => sp.sheet_name === activeSheetTab)
                    ?.columns.length || 0}{" "}
                  columns will pass through unchanged
                </p>
              </div>
            )}

            {/* Bottom action bar */}
            <div className="glass rounded-xl p-4 flex items-center justify-between sticky bottom-4">
              <div className="text-xs text-white/50">
                {isMultiSheet
                  ? `${totalSelectedColumns} columns across ${sheetProfiles.length} sheets will be masked${passThroughSheets.size > 0 ? ` · ${passThroughSheets.size} pass-through` : ""}`
                  : isMultiTable
                    ? `${totalSelectedColumns} columns across ${tableProfiles.length} tables will be masked`
                    : `${columnConfigs.size} of ${activeColumns.length} columns will be masked`}
              </div>
              <button
                onClick={() => setStep(STEP_REVIEW)}
                disabled={
                  totalSelectedColumns === 0 && passThroughSheets.size === 0
                }
                className="px-6 py-2.5 bg-brand-500 hover:bg-brand-600 disabled:opacity-30 disabled:cursor-not-allowed rounded-xl text-sm font-medium text-white transition flex items-center gap-2"
              >
                <ClipboardCheck size={14} /> Review Configuration
              </button>
            </div>
          </div>
        )}

        {/* ════════════════════ REVIEW / SUMMARY STEP ════════════════════ */}
        {step === STEP_REVIEW && (
          <div className="space-y-4">
            <div className="glass rounded-xl p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <ClipboardCheck size={18} className="text-brand-500" />
                  <h3 className="text-sm font-semibold text-white">
                    Masking Configuration Summary
                  </h3>
                </div>
                <button
                  onClick={() => setStep(STEP_CONFIGURE)}
                  className="px-3 py-1.5 bg-surface-200 hover:bg-surface-300 rounded-lg text-xs text-white/55 hover:text-white transition flex items-center gap-1.5"
                >
                  <ArrowLeft size={10} /> Back to Configure
                </button>
              </div>
              <p className="text-[11px] text-white/50 mb-4">
                Review all masking rules before previewing or executing. Verify
                each column&apos;s technique, options, and special settings.
              </p>

              {/* Global settings */}
              <div
                className={`grid grid-cols-2 ${isMultiSheet && passThroughSheets.size > 0 ? "md:grid-cols-5" : "md:grid-cols-4"} gap-3 mb-5`}
              >
                <div className="bg-surface-200 rounded-lg p-3 border border-white/5">
                  <div className="text-[11px] text-white/50 uppercase tracking-wider mb-1">
                    Total Columns
                  </div>
                  <div className="text-lg font-bold text-brand-500">
                    {totalSelectedColumns}
                  </div>
                </div>
                <div className="bg-surface-200 rounded-lg p-3 border border-white/5">
                  <div className="text-[11px] text-white/50 uppercase tracking-wider mb-1">
                    {isMultiTable ? "Tables" : "Sheets"}
                  </div>
                  <div className="text-lg font-bold text-white">
                    {isMultiSheet
                      ? sheetProfiles.length
                      : isMultiTable
                        ? tableProfiles.length
                        : 1}
                  </div>
                </div>
                <div className="bg-surface-200 rounded-lg p-3 border border-white/5">
                  <div className="text-[11px] text-white/50 uppercase tracking-wider mb-1">
                    Lineage
                  </div>
                  <div
                    className={`text-sm font-semibold ${lineageEnabled ? "text-green-400" : "text-white/50"}`}
                  >
                    {lineageEnabled ? "Enabled" : "Disabled"}
                  </div>
                </div>
                <div className="bg-surface-200 rounded-lg p-3 border border-white/5">
                  <div className="text-[11px] text-white/50 uppercase tracking-wider mb-1">
                    Date Links
                  </div>
                  <div
                    className={`text-sm font-semibold ${dateLinks.length > 0 ? "text-amber-400" : "text-white/50"}`}
                  >
                    {dateLinks.length > 0
                      ? `${dateLinks.length} pair${dateLinks.length > 1 ? "s" : ""}`
                      : "None"}
                  </div>
                </div>
                {isMultiSheet && passThroughSheets.size > 0 && (
                  <div className="bg-amber-500/5 rounded-lg p-3 border border-amber-500/15">
                    <div className="text-[11px] text-amber-400/60 uppercase tracking-wider mb-1">
                      Pass-Through
                    </div>
                    <div className="text-lg font-bold text-amber-400">
                      {passThroughSheets.size}
                    </div>
                  </div>
                )}
              </div>

              {/* Per-sheet breakdown */}
              {(() => {
                const sheetsToShow = isMultiSheet
                  ? sheetProfiles.map((sp) => ({
                      name: sp.sheet_name,
                      configs:
                        sheetColumnConfigs.get(sp.sheet_name) || new Map(),
                      rowCount: sp.row_count,
                      colCount: sp.columns.length,
                      isPassThrough: passThroughSheets.has(sp.sheet_name),
                    }))
                  : isMultiTable
                    ? tableProfiles.map((tp) => ({
                        name: tp.table_name,
                        configs:
                          tableColumnConfigs.get(tp.table_name) || new Map(),
                        rowCount: tp.row_count,
                        colCount: tp.columns.length,
                        isPassThrough: false,
                      }))
                    : [
                        {
                          name: "Data",
                          configs: columnConfigs,
                          rowCount: uploadResponse?.row_count || 0,
                          colCount: uploadResponse?.columns?.length || 0,
                          isPassThrough: false,
                        },
                      ];

                return sheetsToShow.map((sheet) => {
                  const configs = Array.from(
                    sheet.configs.values(),
                  ) as MaskingColumnConfig[];

                  // Show pass-through sheet card
                  if (sheet.isPassThrough) {
                    return (
                      <div key={sheet.name} className="mb-4">
                        <div className="flex items-center gap-2 mb-2">
                          <FileSpreadsheet
                            size={12}
                            className="text-amber-400"
                          />
                          <span className="text-xs font-semibold text-white">
                            {sheet.name}
                          </span>
                          <span className="px-1.5 py-0.5 bg-amber-500/15 text-amber-400 rounded text-[11px] font-medium">
                            pass-through
                          </span>
                          <span className="text-[11px] text-white/50">
                            {sheet.rowCount.toLocaleString()} rows &middot;{" "}
                            {sheet.colCount} columns &middot; no masking
                          </span>
                        </div>
                        <div className="bg-amber-500/5 border border-amber-500/10 rounded-lg p-3">
                          <p className="text-[11px] text-white/50">
                            This sheet will be included in the output without
                            any masking applied. All {sheet.colCount} columns
                            will pass through unchanged.
                          </p>
                        </div>
                      </div>
                    );
                  }

                  if (configs.length === 0) return null;

                  // Group configs by technique
                  const byTechnique = new Map<string, MaskingColumnConfig[]>();
                  configs.forEach((cfg) => {
                    const list = byTechnique.get(cfg.technique) || [];
                    list.push(cfg);
                    byTechnique.set(cfg.technique, list);
                  });

                  return (
                    <div key={sheet.name} className="mb-4">
                      <div className="flex items-center gap-2 mb-2">
                        {isMultiSheet && (
                          <FileSpreadsheet
                            size={12}
                            className="text-brand-500"
                          />
                        )}
                        {isMultiTable && (
                          <Table2 size={12} className="text-brand-500" />
                        )}
                        <span className="text-xs font-semibold text-white">
                          {sheet.name}
                        </span>
                        <span className="text-[11px] text-white/50">
                          {sheet.rowCount.toLocaleString()} rows &middot;{" "}
                          {sheet.colCount} columns &middot; {configs.length}{" "}
                          masked
                        </span>
                      </div>

                      <div className="overflow-x-auto rounded-lg border border-white/5">
                        <table className="w-full text-left">
                          <thead>
                            <tr className="bg-surface-300/50">
                              <th className="px-3 py-2 text-[11px] text-white/50 uppercase tracking-wider font-medium">
                                #
                              </th>
                              <th className="px-3 py-2 text-[11px] text-white/50 uppercase tracking-wider font-medium">
                                Column
                              </th>
                              <th className="px-3 py-2 text-[11px] text-white/50 uppercase tracking-wider font-medium">
                                Technique
                              </th>
                              <th className="px-3 py-2 text-[11px] text-white/50 uppercase tracking-wider font-medium">
                                Options
                              </th>
                              <th className="px-3 py-2 text-[11px] text-white/50 uppercase tracking-wider font-medium">
                                Flags
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {configs.map((cfg, idx) => {
                              const techInfo = techniques.find(
                                (t) => t.id === cfg.technique,
                              );
                              const hasFilter = !!cfg.filter_expression;
                              const hasUniqueness = !!cfg.preserve_uniqueness;

                              return (
                                <tr
                                  key={cfg.column}
                                  className={`border-t border-white/5 ${idx % 2 === 0 ? "bg-surface-200/30" : ""}`}
                                >
                                  <td className="px-3 py-2 text-[11px] text-white/50 font-mono">
                                    {idx + 1}
                                  </td>
                                  <td className="px-3 py-2">
                                    <span className="text-xs font-mono text-white">
                                      {cfg.column}
                                    </span>
                                  </td>
                                  <td className="px-3 py-2">
                                    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-brand-500/10 text-brand-500 rounded text-[11px] font-medium">
                                      {techInfo &&
                                        TECHNIQUE_ICONS[techInfo.icon]}
                                      {techInfo?.label || cfg.technique}
                                    </span>
                                  </td>
                                  <td className="px-3 py-2">
                                    {Object.keys(cfg.options || {}).length >
                                    0 ? (
                                      <div className="flex flex-wrap gap-1">
                                        {Object.entries(cfg.options || {}).map(
                                          ([k, v]) => (
                                            <span
                                              key={k}
                                              className="px-1.5 py-0.5 bg-surface-300 rounded text-[11px] text-white/55 font-mono"
                                            >
                                              {k}: {String(v)}
                                            </span>
                                          ),
                                        )}
                                      </div>
                                    ) : (
                                      <span className="text-[11px] text-white/50">
                                        defaults
                                      </span>
                                    )}
                                  </td>
                                  <td className="px-3 py-2">
                                    <div className="flex gap-1">
                                      {hasFilter && (
                                        <span className="px-1.5 py-0.5 bg-purple-500/10 text-purple-400 rounded text-[11px]">
                                          filtered
                                        </span>
                                      )}
                                      {hasUniqueness && (
                                        <span className="px-1.5 py-0.5 bg-green-500/10 text-green-400 rounded text-[11px]">
                                          unique
                                        </span>
                                      )}
                                      {lineageEnabled && (
                                        <span className="px-1.5 py-0.5 bg-blue-500/10 text-blue-400 rounded text-[11px]">
                                          lineage
                                        </span>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>

                      {/* Technique distribution summary */}
                      <div className="flex flex-wrap gap-2 mt-2">
                        {Array.from(byTechnique.entries()).map(
                          ([techId, cols]) => {
                            const techInfo = techniques.find(
                              (t) => t.id === techId,
                            );
                            return (
                              <span
                                key={techId}
                                className="inline-flex items-center gap-1 px-2 py-1 bg-surface-200 border border-white/5 rounded-lg text-[11px] text-white/55"
                              >
                                {techInfo && TECHNIQUE_ICONS[techInfo.icon]}
                                {techInfo?.label || techId}:{" "}
                                <span className="text-white font-semibold">
                                  {cols.length}
                                </span>
                              </span>
                            );
                          },
                        )}
                      </div>
                    </div>
                  );
                });
              })()}

              {/* Date links summary */}
              {dateLinks.length > 0 && (
                <div className="mt-4 bg-amber-500/5 border border-amber-500/15 rounded-lg p-3">
                  <p className="text-[11px] text-amber-400 font-medium flex items-center gap-1.5 mb-2">
                    <CalendarClock size={11} /> Date Link Groups (same shift
                    offset preserves ordering)
                  </p>
                  <div className="space-y-1">
                    {dateLinks.map((link, idx) => (
                      <div
                        key={idx}
                        className="flex items-center gap-2 text-[11px]"
                      >
                        <span className="text-amber-400 font-mono">
                          {link.primary}
                        </span>
                        <span className="text-white/50">&rarr;</span>
                        {link.linked.map((l) => (
                          <span
                            key={l}
                            className="px-1.5 py-0.5 bg-amber-500/10 text-amber-400 rounded font-mono"
                          >
                            {l}
                          </span>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Lineage groups summary */}
              {lineageEnabled && lineageGroups.length > 0 && (
                <div className="mt-3 bg-blue-500/5 border border-blue-500/15 rounded-lg p-3">
                  <p className="text-[11px] text-blue-400 font-medium flex items-center gap-1.5 mb-2">
                    <Link2 size={11} /> Lineage Groups (consistent value
                    mapping)
                  </p>
                  <div className="space-y-1">
                    {lineageGroups.map((group, idx) => (
                      <div key={idx} className="flex flex-wrap gap-1">
                        {group.map((col) => (
                          <span
                            key={col}
                            className="px-1.5 py-0.5 bg-blue-500/10 text-blue-400 rounded text-[11px] font-mono"
                          >
                            {col}
                          </span>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Action buttons */}
            <div className="glass rounded-xl p-4 flex items-center justify-between sticky bottom-4">
              <button
                onClick={() => setStep(STEP_CONFIGURE)}
                className="px-4 py-2 bg-surface-200 hover:bg-surface-300 rounded-lg text-xs text-white/55 hover:text-white transition flex items-center gap-1.5"
              >
                <ArrowLeft size={12} /> Back to Configure
              </button>
              <div className="flex items-center gap-3">
                <span className="text-[11px] text-white/50">
                  {totalSelectedColumns} columns ready
                  {passThroughSheets.size > 0
                    ? ` · ${passThroughSheets.size} pass-through`
                    : ""}
                </span>
                <button
                  onClick={handlePreview}
                  disabled={
                    totalSelectedColumns === 0 && passThroughSheets.size === 0
                  }
                  className="px-6 py-2.5 bg-brand-500 hover:bg-brand-600 disabled:opacity-30 disabled:cursor-not-allowed rounded-xl text-sm font-medium text-white transition flex items-center gap-2"
                >
                  <Eye size={14} /> Preview Masking
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ════════════════════ PREVIEW STEP ════════════════════ */}
        {step === STEP_PREVIEW && !loading && previewData && (
          <div className="space-y-4">
            {/* Info bar */}
            <div className="glass rounded-xl p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Info size={14} className="text-brand-500" />
                <span className="text-xs text-white/50">
                  {(previewData.sheet_name || previewData.table_name) && (
                    <span className="text-brand-500 mr-2">
                      [{previewData.sheet_name || previewData.table_name}]
                    </span>
                  )}
                  Showing{" "}
                  <span className="text-white font-semibold">
                    {previewData.preview_rows}
                  </span>{" "}
                  of{" "}
                  <span className="text-white font-semibold">
                    {previewData.total_rows.toLocaleString()}
                  </span>{" "}
                  rows.
                  <span className="text-brand-500 ml-2">
                    {previewData.columns_affected.length} columns
                  </span>{" "}
                  affected.
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setStep(STEP_REVIEW)}
                  className="px-3 py-1.5 bg-surface-200 hover:bg-surface-300 rounded-lg text-xs text-white/55 hover:text-white transition"
                >
                  Back to Review
                </button>
                <button
                  onClick={handlePreview}
                  className="px-3 py-1.5 bg-surface-200 hover:bg-surface-300 rounded-lg text-xs text-white/55 hover:text-white transition flex items-center gap-1.5"
                >
                  <RefreshCw size={10} /> Refresh
                </button>
              </div>
            </div>

            {/* Multi-sheet: sheet tab switcher for preview */}
            {isMultiSheet && sheetProfiles.length > 1 && (
              <div className="flex items-center gap-1 overflow-x-auto custom-scrollbar pb-1">
                {sheetProfiles.map((sp) => {
                  const hasConfigs =
                    (sheetColumnConfigs.get(sp.sheet_name)?.size || 0) > 0;
                  if (!hasConfigs) return null;
                  return (
                    <button
                      key={sp.sheet_name}
                      onClick={async () => {
                        setActiveSheetTab(sp.sheet_name);
                        if (sessionId) {
                          setLoading(true);
                          try {
                            const configs = Array.from(
                              (
                                sheetColumnConfigs.get(sp.sheet_name) ||
                                new Map()
                              ).values(),
                            );
                            const res = await api.maskingPreview(
                              sessionId,
                              configs,
                              sp.sheet_name,
                              lineageEnabled,
                              lineageGroups,
                              dateLinks,
                            );
                            setPreviewData(res);
                          } catch (err: any) {
                            setError(err.message);
                          }
                          setLoading(false);
                        }
                      }}
                      className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-medium whitespace-nowrap transition-all border ${
                        (previewData.sheet_name || activeSheetTab) ===
                        sp.sheet_name
                          ? "bg-brand-500/10 border-brand-500/30 text-brand-500"
                          : "bg-surface-200 border-white/5 text-white/55 hover:text-white/60"
                      }`}
                    >
                      <FileSpreadsheet size={12} /> {sp.sheet_name}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Multi-table (#603): table tab switcher for preview — mirrors
             * the sheet tab switcher above. */}
            {isMultiTable && tableProfiles.length > 1 && (
              <div className="flex items-center gap-1 overflow-x-auto custom-scrollbar pb-1">
                {tableProfiles.map((tp) => {
                  const hasConfigs =
                    (tableColumnConfigs.get(tp.table_name)?.size || 0) > 0;
                  if (!hasConfigs) return null;
                  return (
                    <button
                      key={tp.table_name}
                      onClick={async () => {
                        setActiveTableTab(tp.table_name);
                        if (sessionId) {
                          setLoading(true);
                          try {
                            const configs = Array.from(
                              (
                                tableColumnConfigs.get(tp.table_name) ||
                                new Map()
                              ).values(),
                            );
                            const res = await api.maskingPreview(
                              sessionId,
                              configs,
                              undefined,
                              lineageEnabled,
                              lineageGroups,
                              dateLinks,
                              tp.table_name,
                            );
                            setPreviewData(res);
                          } catch (err: any) {
                            setError(err.message);
                          }
                          setLoading(false);
                        }
                      }}
                      className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-medium whitespace-nowrap transition-all border ${
                        (previewData.table_name || activeTableTab) ===
                        tp.table_name
                          ? "bg-brand-500/10 border-brand-500/30 text-brand-500"
                          : "bg-surface-200 border-white/5 text-white/55 hover:text-white/60"
                      }`}
                    >
                      <Table2 size={12} /> {tp.table_name}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Before & After comparison */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="glass rounded-xl overflow-hidden">
                <div className="px-4 py-2.5 border-b border-surface-200 flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-red-500" />
                  <h3 className="text-xs font-semibold text-white/50">
                    Original Data
                  </h3>
                </div>
                <div className="overflow-x-auto max-h-[400px] overflow-y-auto custom-scrollbar">
                  <table className="w-full text-[11px]">
                    <thead>
                      <tr className="border-b border-surface-200">
                        {previewData.columns_affected.map((col) => (
                          <th
                            key={col}
                            className="text-left px-3 py-2 font-medium text-red-400/60 whitespace-nowrap"
                          >
                            {col}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {previewData.original.map((row, i) => (
                        <tr key={i} className="border-b border-surface-200/50">
                          {previewData.columns_affected.map((col) => (
                            <td
                              key={col}
                              className="px-3 py-1.5 text-white/50 whitespace-nowrap max-w-[200px] truncate"
                            >
                              {String(row[col] ?? "")}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="glass rounded-xl overflow-hidden">
                <div className="px-4 py-2.5 border-b border-surface-200 flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-green-500" />
                  <h3 className="text-xs font-semibold text-white/50">
                    Masked Data
                  </h3>
                </div>
                <div className="overflow-x-auto max-h-[400px] overflow-y-auto custom-scrollbar">
                  <table className="w-full text-[11px]">
                    <thead>
                      <tr className="border-b border-surface-200">
                        {previewData.columns_affected.map((col) => (
                          <th
                            key={col}
                            className="text-left px-3 py-2 font-medium text-green-400/60 whitespace-nowrap"
                          >
                            {col}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {previewData.masked.map((row, i) => (
                        <tr key={i} className="border-b border-surface-200/50">
                          {previewData.columns_affected.map((col) => (
                            <td
                              key={col}
                              className="px-3 py-1.5 text-brand-500 font-mono whitespace-nowrap max-w-[200px] truncate"
                            >
                              {String(row[col] ?? "")}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Target configuration + execute */}
            <div className="glass rounded-xl p-5 space-y-4">
              <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                <Download size={14} className="text-brand-500" />
                Output Configuration
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div>
                  <label className="block text-[11px] text-white/50 mb-1 uppercase tracking-wider">
                    Target Type
                  </label>
                  <select
                    value={targetType}
                    onChange={(e) => {
                      const next = e.target.value as "file" | "db";
                      setTargetType(next);
                      // Pre-fill from the connection already used to fetch
                      // the source table, so the user isn't retyping the
                      // same host/database/username they just entered (or
                      // that's already saved on the project) a moment ago.
                      // Only when the target form is still untouched
                      // (host empty) — never clobber an edit the user has
                      // already made.
                      if (
                        next === "db" &&
                        sourceType === "db" &&
                        !targetDbParams.host
                      ) {
                        if (projectDbMode === "project" && projectDbConfig) {
                          setTargetDbParams((p) => ({
                            ...p,
                            db_type: projectDbConfig.db_type,
                            host: projectDbConfig.host || "",
                            port: projectDbConfig.port || p.port,
                            database: projectDbConfig.database || "",
                            schema_name:
                              projectDbConfig.schema_name || p.schema_name,
                            username: projectDbConfig.username || "",
                          }));
                          setTargetPrefilledFromProjectSource(true);
                        } else if (projectDbMode === "manual") {
                          setTargetDbParams((p) => ({
                            ...p,
                            ...dbParams,
                            table: p.table,
                          }));
                        }
                      }
                    }}
                    className="w-full bg-surface-200 border border-white/10 rounded-lg px-3 py-2 text-xs text-white"
                  >
                    <option value="file">File Download</option>
                    <option value="db">Write to Database</option>
                  </select>
                </div>
                {targetType === "file" && (
                  <div>
                    <label className="block text-[11px] text-white/50 mb-1 uppercase tracking-wider">
                      Format
                    </label>
                    <select
                      value={targetFormat}
                      onChange={(e) => setTargetFormat(e.target.value)}
                      className="w-full bg-surface-200 border border-white/10 rounded-lg px-3 py-2 text-xs text-white"
                    >
                      <option value="csv">
                        CSV
                        {isMultiSheet
                          ? " (one per sheet)"
                          : isMultiTable
                            ? " (one per table, zipped)"
                            : ""}
                      </option>
                      <option value="xlsx">
                        Excel (.xlsx)
                        {isMultiSheet
                          ? " (multi-sheet)"
                          : isMultiTable
                            ? " (one tab per table)"
                            : ""}
                      </option>
                      <option value="parquet">
                        Parquet
                        {isMultiSheet
                          ? " (one per sheet)"
                          : isMultiTable
                            ? " (combined)"
                            : ""}
                      </option>
                    </select>
                  </div>
                )}
                {targetType === "db" && (
                  <div>
                    <label className="block text-[11px] text-white/50 mb-1 uppercase tracking-wider">
                      Table Strategy
                    </label>
                    <select
                      value={tableStrategy}
                      onChange={(e) => setTableStrategy(e.target.value)}
                      className="w-full bg-surface-200 border border-white/10 rounded-lg px-3 py-2 text-xs text-white"
                    >
                      <option value="create_if_not_exists">
                        Create if not exists
                      </option>
                      <option value="drop_recreate">Drop & Recreate</option>
                      <option value="truncate_reload">Truncate & Reload</option>
                    </select>
                  </div>
                )}
              </div>
              {targetType === "db" && targetPrefilledFromProjectSource && (
                <div className="flex items-start gap-2 rounded-lg px-3 py-2 bg-amber-500/10 border border-amber-500/25">
                  <AlertTriangle
                    size={13}
                    className="mt-0.5 shrink-0 text-amber-400"
                  />
                  <p className="text-[11px] text-amber-300/80">
                    Pre-filled from your project&apos;s saved source connection,
                    which is often a read-only account. If the write fails with
                    a permissions error, use a different account with CREATE
                    TABLE / INSERT access on this schema.
                  </p>
                </div>
              )}
              {targetType === "db" && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-2 border-t border-white/5">
                  <div>
                    <label className="block text-[11px] text-white/50 mb-1">
                      DB Type
                    </label>
                    <select
                      value={targetDbParams.db_type}
                      onChange={(e) =>
                        setTargetDbParams((p) => ({
                          ...p,
                          db_type: e.target.value,
                        }))
                      }
                      className="w-full bg-surface-200 border border-white/10 rounded px-2 py-1.5 text-xs text-white"
                    >
                      <option value="postgresql">PostgreSQL</option>
                      <option value="mysql">MySQL</option>
                      <option value="mssql">SQL Server</option>
                      <option value="oracle">Oracle</option>
                      <option value="snowflake">Snowflake</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-[11px] text-white/50 mb-1">
                      Host
                    </label>
                    <input
                      value={targetDbParams.host}
                      onChange={(e) =>
                        setTargetDbParams((p) => ({
                          ...p,
                          host: e.target.value,
                        }))
                      }
                      className="w-full bg-surface-200 border border-white/10 rounded px-2 py-1.5 text-xs text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] text-white/50 mb-1">
                      Port
                    </label>
                    <input
                      type="number"
                      value={targetDbParams.port}
                      onChange={(e) =>
                        setTargetDbParams((p) => ({
                          ...p,
                          port: parseInt(e.target.value) || 0,
                        }))
                      }
                      className="w-full bg-surface-200 border border-white/10 rounded px-2 py-1.5 text-xs text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] text-white/50 mb-1">
                      Database
                    </label>
                    <input
                      value={targetDbParams.database}
                      onChange={(e) =>
                        setTargetDbParams((p) => ({
                          ...p,
                          database: e.target.value,
                        }))
                      }
                      className="w-full bg-surface-200 border border-white/10 rounded px-2 py-1.5 text-xs text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] text-white/50 mb-1">
                      Schema
                    </label>
                    <input
                      value={targetDbParams.schema_name}
                      onChange={(e) =>
                        setTargetDbParams((p) => ({
                          ...p,
                          schema_name: e.target.value,
                        }))
                      }
                      className="w-full bg-surface-200 border border-white/10 rounded px-2 py-1.5 text-xs text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] text-white/50 mb-1">
                      Username
                    </label>
                    <input
                      value={targetDbParams.username}
                      onChange={(e) =>
                        setTargetDbParams((p) => ({
                          ...p,
                          username: e.target.value,
                        }))
                      }
                      className="w-full bg-surface-200 border border-white/10 rounded px-2 py-1.5 text-xs text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] text-white/50 mb-1">
                      Password
                    </label>
                    <input
                      type="password"
                      value={targetDbParams.password}
                      onChange={(e) =>
                        setTargetDbParams((p) => ({
                          ...p,
                          password: e.target.value,
                        }))
                      }
                      className="w-full bg-surface-200 border border-white/10 rounded px-2 py-1.5 text-xs text-white"
                    />
                  </div>
                  {isMultiTable ? (
                    <div className="col-span-2">
                      <p className="text-[11px] text-white/50 bg-surface-300/50 rounded px-2 py-1.5">
                        Each table will be written to a table of the same name
                        in the schema above.
                      </p>
                    </div>
                  ) : (
                    <div>
                      <label className="block text-[11px] text-white/50 mb-1">
                        Table Name
                      </label>
                      <input
                        value={targetDbParams.table || ""}
                        onChange={(e) =>
                          setTargetDbParams((p) => ({
                            ...p,
                            table: e.target.value,
                          }))
                        }
                        className="w-full bg-surface-200 border border-white/10 rounded px-2 py-1.5 text-xs text-white"
                      />
                    </div>
                  )}
                </div>
              )}
              <div className="flex items-center justify-between pt-2">
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setShowSaveModal(true)}
                    disabled={totalSelectedColumns === 0}
                    className="px-4 py-2.5 bg-surface-200 hover:bg-surface-300 disabled:opacity-30 rounded-xl text-xs text-white/55 hover:text-white transition flex items-center gap-2"
                    title="Save current configuration"
                  >
                    <Save size={14} /> Save Config
                  </button>
                  <button
                    data-testid="masking-open-script-modal"
                    onClick={() => setShowScriptModal(true)}
                    disabled={totalSelectedColumns === 0}
                    className="px-4 py-2.5 bg-purple-500/20 hover:bg-purple-500/30 disabled:opacity-30 rounded-xl text-xs text-purple-300 hover:text-purple-200 transition flex items-center gap-2 border border-purple-500/20"
                    title="Generate deployable script"
                  >
                    <Code size={14} /> Generate Script
                  </button>
                </div>
                <button
                  onClick={handleExecute}
                  className="px-8 py-3 bg-brand-500 hover:bg-brand-600 rounded-xl text-sm font-semibold text-white transition flex items-center gap-2"
                >
                  <Play size={14} /> Execute Masking
                  {isMultiSheet
                    ? ` (${sheetProfiles.length} sheets)`
                    : isMultiTable
                      ? ` (${tableProfiles.length} tables)`
                      : ""}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ════════════════════ EXECUTE / RESULTS STEP ════════════════════ */}
        {step === STEP_EXECUTE && !loading && executeResult && (
          <div className="space-y-4">
            {/* Success banner */}
            <div className="glass rounded-xl p-5 border border-green-500/20">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-green-500/10 flex items-center justify-center">
                  <CheckCircle2 size={20} className="text-green-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-green-400">
                    Masking Complete
                  </h3>
                  <p className="text-xs text-white/55">
                    {executeResult.rows_processed.toLocaleString()} rows
                    processed, {executeResult.columns_masked} columns masked
                    {executeResult.sheets_processed &&
                    executeResult.sheets_processed > 1
                      ? ` across ${executeResult.sheets_processed} sheets`
                      : ""}
                    {executeResult.tables_processed &&
                    executeResult.tables_processed > 1
                      ? ` across ${executeResult.tables_processed} tables`
                      : ""}{" "}
                    — all columns included, masked values replaced in selected
                    columns.
                  </p>
                </div>
              </div>
            </div>

            {/* Consistency warnings (#603 item 3/4) — surfaced, never
             * blocking: the run has already completed successfully above. */}
            {executeResult.consistency_warnings &&
              executeResult.consistency_warnings.length > 0 && (
                <div className="glass rounded-xl p-5 border border-amber-500/25 space-y-2">
                  <div className="flex items-center gap-2">
                    <AlertTriangle size={16} className="text-amber-400" />
                    <h3 className="text-sm font-semibold text-amber-400">
                      {executeResult.consistency_warnings.length} relationship
                      consistency warning
                      {executeResult.consistency_warnings.length > 1 ? "s" : ""}
                    </h3>
                  </div>
                  <p className="text-[11px] text-white/50">
                    These linked columns (detected in the Relationships step)
                    were masked in a way that won&apos;t produce matching values
                    across tables — for example, two FK-linked columns
                    configured with different techniques, a randomized technique
                    (which isn&apos;t guaranteed to repeat the same output
                    across tables), or the same encryption technique with
                    different keys. The run completed anyway; this is
                    information for you to act on, not an error.
                  </p>
                  <ul className="space-y-1.5">
                    {executeResult.consistency_warnings.map((w, idx) => (
                      <li
                        key={idx}
                        className="text-[11px] text-amber-300/90 bg-amber-500/5 border border-amber-500/15 rounded-lg px-3 py-2 font-mono"
                      >
                        {w}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

            {/* Output Options: Download or Write to DB */}
            <div className="glass rounded-xl p-5 space-y-4">
              <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                <Download size={14} className="text-brand-500" />
                Output Options
              </h3>

              {/* Download section */}
              <div className="bg-surface-200 rounded-xl p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-white">
                      {executeResult.is_bundle
                        ? "Download Masked Tables"
                        : "Download Masked File"}
                    </p>
                    <p className="text-[11px] text-white/50">
                      {executeResult.is_bundle
                        ? `A ZIP containing one CSV per table${
                            executeResult.tables_processed
                              ? ` (${executeResult.tables_processed} tables)`
                              : ""
                          }`
                        : "All columns included with masked values in selected columns"}
                    </p>
                  </div>
                  {sessionId && (
                    <div className="flex items-center gap-2">
                      {executeResult.is_bundle ? (
                        // Only CSV is valid here — a multi-table CSV run
                        // produces a ZIP of per-table CSVs, not a
                        // masked_data.xlsx/.parquet the download endpoint
                        // could convert to, so those two buttons would 404.
                        <a
                          href={api.maskingDownloadUrl(sessionId, "csv")}
                          download
                          className="inline-flex items-center gap-2 px-4 py-2 bg-brand-500 hover:bg-brand-600 rounded-lg text-xs font-medium text-white transition"
                        >
                          <Download size={12} /> Download ZIP
                        </a>
                      ) : (
                        <>
                          <a
                            href={api.maskingDownloadUrl(sessionId, "csv")}
                            download
                            className="inline-flex items-center gap-2 px-4 py-2 bg-surface-300 hover:bg-surface-400 rounded-lg text-xs font-medium text-white transition border border-white/10"
                          >
                            <Download size={12} /> CSV
                          </a>
                          <a
                            href={api.maskingDownloadUrl(sessionId, "xlsx")}
                            download
                            className="inline-flex items-center gap-2 px-4 py-2 bg-brand-500 hover:bg-brand-600 rounded-lg text-xs font-medium text-white transition"
                          >
                            <Download size={12} /> Excel
                          </a>
                          <a
                            href={api.maskingDownloadUrl(sessionId, "parquet")}
                            download
                            className="inline-flex items-center gap-2 px-4 py-2 bg-surface-300 hover:bg-surface-400 rounded-lg text-xs font-medium text-white transition border border-white/10"
                          >
                            <Download size={12} /> Parquet
                          </a>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Audit Report */}
            {executeResult.audit && (
              <div className="glass rounded-xl p-5">
                <h3 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
                  <ShieldCheck size={14} className="text-brand-500" />
                  Masking Audit Report
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                  <div className="bg-surface-200 rounded-lg p-3">
                    <span className="text-[11px] text-white/50 block">
                      Total Rows
                    </span>
                    <span className="text-lg font-bold text-white">
                      {executeResult.audit.total_rows.toLocaleString()}
                    </span>
                  </div>
                  <div className="bg-surface-200 rounded-lg p-3">
                    <span className="text-[11px] text-white/50 block">
                      Columns Masked
                    </span>
                    <span className="text-lg font-bold text-brand-500">
                      {executeResult.audit.columns_masked}
                    </span>
                  </div>
                  <div className="bg-surface-200 rounded-lg p-3">
                    <span className="text-[11px] text-white/50 block">
                      Columns Untouched
                    </span>
                    <span className="text-lg font-bold text-white/60">
                      {executeResult.audit.columns_untouched}
                    </span>
                  </div>
                  <div className="bg-surface-200 rounded-lg p-3">
                    <span className="text-[11px] text-white/50 block">
                      Timestamp
                    </span>
                    <span className="text-xs font-mono text-white/60">
                      {new Date(executeResult.audit.timestamp).toLocaleString()}
                    </span>
                  </div>
                </div>

                {/* Per-column details */}
                <div className="space-y-2">
                  {executeResult.audit.masking_details.map((d, idx) => (
                    <div
                      key={`${d.column}-${idx}`}
                      className="flex items-center justify-between bg-surface-200 rounded-lg px-4 py-2.5"
                    >
                      <div className="flex items-center gap-3">
                        <span className="font-mono text-xs text-white">
                          {d.column}
                        </span>
                        <span className="px-2 py-0.5 bg-brand-500/10 text-brand-500 rounded text-[11px] font-medium">
                          {d.technique_label}
                        </span>
                      </div>
                      <span className="text-xs text-white/50">
                        {d.rows_affected.toLocaleString()} /{" "}
                        {d.total_rows.toLocaleString()} rows affected
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center gap-3">
              <div className="flex-1" />
              <button
                onClick={() => {
                  setStep(STEP_CONFIGURE);
                  setExecuteResult(null);
                  setPreviewData(null);
                }}
                className="px-4 py-2 bg-surface-200 hover:bg-surface-300 rounded-xl text-xs text-white/55 hover:text-white transition"
              >
                Re-configure Masking
              </button>
              <button
                onClick={resetAll}
                className="px-4 py-2 bg-surface-200 hover:bg-surface-300 rounded-xl text-xs text-white/55 hover:text-white transition"
              >
                Start New Masking Job
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ════════════════════ SAVE CONFIG MODAL ════════════════════ */}
      {showSaveModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="glass rounded-2xl p-6 w-full max-w-md border border-white/10 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <Save size={18} className="text-brand-500" /> Save Configuration
              </h3>
              <button
                onClick={() => setShowSaveModal(false)}
                className="text-white/50 hover:text-white"
              >
                <X size={18} />
              </button>
            </div>

            {/* Save mode toggle */}
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setSaveMode("new");
                  setSelectedParentConfig(null);
                }}
                className={`flex-1 py-2 px-3 rounded-lg text-xs font-medium transition ${
                  saveMode === "new"
                    ? "bg-brand-500/20 border border-brand-500/40 text-brand-400"
                    : "bg-surface-200 border border-white/5 text-white/55 hover:text-white/60"
                }`}
              >
                Save as New
              </button>
              <button
                onClick={() => {
                  setSaveMode("update");
                  api
                    .maskingConfigList()
                    .then((r) => setExistingConfigsForSave(r.configs))
                    .catch(() => {});
                }}
                className={`flex-1 py-2 px-3 rounded-lg text-xs font-medium transition ${
                  saveMode === "update"
                    ? "bg-purple-500/20 border border-purple-500/40 text-purple-400"
                    : "bg-surface-200 border border-white/5 text-white/55 hover:text-white/60"
                }`}
              >
                Update Existing
              </button>
            </div>

            {/* Select parent config for update mode */}
            {saveMode === "update" && (
              <div>
                <label className="block text-xs text-white/50 mb-1">
                  Select config to update
                </label>
                {existingConfigsForSave.length === 0 ? (
                  <p className="text-[11px] text-white/50 py-2">
                    No saved configurations found.
                  </p>
                ) : (
                  <div className="space-y-1 max-h-32 overflow-y-auto custom-scrollbar">
                    {existingConfigsForSave.map((cfg) => (
                      <button
                        key={cfg.id}
                        onClick={() => {
                          setSelectedParentConfig(cfg);
                          if (!saveConfigName) setSaveConfigName(cfg.name);
                          if (!saveConfigDesc)
                            setSaveConfigDesc(cfg.description || "");
                        }}
                        className={`w-full text-left px-3 py-2 rounded-lg text-xs transition ${
                          selectedParentConfig?.id === cfg.id
                            ? "bg-purple-500/15 border border-purple-500/30 text-purple-300"
                            : "bg-surface-300 border border-white/5 text-white/55 hover:text-white/60"
                        }`}
                      >
                        <span className="font-medium">{cfg.name}</span>
                        {cfg.version && (
                          <span className="ml-1 text-[11px] text-white/50">
                            v{cfg.version}
                          </span>
                        )}
                        <span className="block text-[11px] text-white/50 mt-0.5">
                          {cfg.column_configs.length} columns ·{" "}
                          {new Date(cfg.created_at).toLocaleDateString()}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div>
              <label className="block text-xs text-white/50 mb-1">
                Configuration Name *
              </label>
              <input
                value={saveConfigName}
                onChange={(e) => setSaveConfigName(e.target.value)}
                placeholder="e.g., Customer PII Masking v2"
                className="w-full bg-surface-200 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-white/20"
                autoFocus
              />
            </div>
            <div>
              <label className="block text-xs text-white/50 mb-1">
                Description (optional)
              </label>
              <textarea
                value={saveConfigDesc}
                onChange={(e) => setSaveConfigDesc(e.target.value)}
                placeholder="Brief notes about this configuration..."
                rows={2}
                className="w-full bg-surface-200 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-white/20 resize-none"
              />
            </div>
            <div className="text-[11px] text-white/50">
              {saveMode === "update" && selectedParentConfig
                ? `This will create version ${(selectedParentConfig.version || 1) + 1} based on "${selectedParentConfig.name}".`
                : "This will save the column → technique mappings so you can reload them later on files with the same schema."}
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={() => setShowSaveModal(false)}
                className="px-4 py-2 bg-surface-200 hover:bg-surface-300 rounded-lg text-xs text-white/55 hover:text-white transition"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveConfig}
                disabled={
                  !saveConfigName.trim() ||
                  (saveMode === "update" && !selectedParentConfig)
                }
                className="px-6 py-2 bg-brand-500 hover:bg-brand-600 disabled:opacity-30 rounded-lg text-xs font-medium text-white transition flex items-center gap-2"
              >
                <Save size={12} />{" "}
                {saveMode === "update"
                  ? `Save v${(selectedParentConfig?.version || 1) + 1}`
                  : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ════════════════════ LOAD CONFIG MODAL ════════════════════ */}
      {showLoadModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="glass rounded-2xl p-6 w-full max-w-lg border border-white/10 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <FolderOpen size={18} className="text-brand-500" /> Load
                Configuration
              </h3>
              <button
                onClick={() => setShowLoadModal(false)}
                className="text-white/50 hover:text-white"
              >
                <X size={18} />
              </button>
            </div>
            <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-xl text-xs text-amber-300/80 flex items-start gap-2">
              <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
              <span>
                Please ensure your source data file is uploaded before applying
                a configuration. The column mappings will only apply to columns
                present in the uploaded data.
              </span>
            </div>
            {loadingConfigs ? (
              <div className="py-8 text-center text-white/50 text-sm">
                Loading saved configurations...
              </div>
            ) : savedConfigs.length === 0 ? (
              <div className="py-8 text-center text-white/50 text-sm">
                No saved configurations yet. Configure masking and click "Save"
                to create one.
              </div>
            ) : (
              <div className="space-y-2 max-h-80 overflow-y-auto custom-scrollbar">
                {savedConfigs.map((cfg) => (
                  <div
                    key={cfg.id}
                    className="flex items-center gap-3 bg-surface-200 rounded-xl p-3 hover:bg-surface-300 transition group"
                  >
                    <div
                      className="flex-1 min-w-0 cursor-pointer"
                      onClick={() => handleApplyConfig(cfg)}
                    >
                      <p className="text-sm font-medium text-white truncate">
                        {cfg.name}
                      </p>
                      <p className="text-[11px] text-white/50 truncate">
                        {cfg.is_multi_sheet && cfg.sheet_configs
                          ? `${cfg.sheet_configs.length} sheets · ${cfg.column_configs.length} columns`
                          : cfg.is_multi_table && cfg.table_configs
                            ? `${cfg.table_configs.length} tables · ${cfg.column_configs.length} columns`
                            : `${cfg.column_configs.length} columns`}
                        {" · "}
                        {new Date(cfg.created_at).toLocaleDateString()}{" "}
                        {cfg.description && `· ${cfg.description}`}
                      </p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {cfg.is_multi_sheet && cfg.sheet_configs ? (
                          <>
                            {cfg.sheet_configs.slice(0, 4).map((sc) => (
                              <span
                                key={sc.sheet}
                                className="px-1.5 py-0.5 bg-purple-500/10 text-purple-400 rounded text-[11px] font-mono flex items-center gap-1"
                              >
                                <Sheet size={8} /> {sc.sheet} (
                                {sc.column_configs.length})
                              </span>
                            ))}
                            {cfg.sheet_configs.length > 4 && (
                              <span className="px-1.5 py-0.5 bg-white/5 text-white/50 rounded text-[11px]">
                                +{cfg.sheet_configs.length - 4} more sheets
                              </span>
                            )}
                          </>
                        ) : cfg.is_multi_table && cfg.table_configs ? (
                          <>
                            {cfg.table_configs.slice(0, 4).map((tc) => (
                              <span
                                key={tc.table}
                                className="px-1.5 py-0.5 bg-purple-500/10 text-purple-400 rounded text-[11px] font-mono flex items-center gap-1"
                              >
                                <Table2 size={8} /> {tc.table} (
                                {tc.column_configs.length})
                              </span>
                            ))}
                            {cfg.table_configs.length > 4 && (
                              <span className="px-1.5 py-0.5 bg-white/5 text-white/50 rounded text-[11px]">
                                +{cfg.table_configs.length - 4} more tables
                              </span>
                            )}
                          </>
                        ) : (
                          <>
                            {cfg.column_configs.slice(0, 5).map((cc) => (
                              <span
                                key={cc.column}
                                className="px-1.5 py-0.5 bg-brand-500/10 text-brand-500 rounded text-[11px] font-mono"
                              >
                                {cc.column}
                              </span>
                            ))}
                            {cfg.column_configs.length > 5 && (
                              <span className="px-1.5 py-0.5 bg-white/5 text-white/50 rounded text-[11px]">
                                +{cfg.column_configs.length - 5} more
                              </span>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteConfig(cfg.id);
                      }}
                      className="p-1.5 rounded-lg text-white/50 hover:text-red-400 hover:bg-red-500/10 transition opacity-0 group-hover:opacity-100"
                      title="Delete"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex justify-end pt-2">
              <button
                onClick={() => setShowLoadModal(false)}
                className="px-4 py-2 bg-surface-200 hover:bg-surface-300 rounded-lg text-xs text-white/55 hover:text-white transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ════════════════════ GENERATE SCRIPT MODAL ════════════════════ */}
      {showScriptModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="glass rounded-2xl p-6 w-full max-w-2xl border border-white/10 space-y-4 max-h-[90vh] overflow-y-auto custom-scrollbar">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <FileCode size={18} className="text-purple-400" /> Generate
                Deployable Script
              </h3>
              <button
                onClick={() => {
                  setShowScriptModal(false);
                  setScriptError(null);
                }}
                className="text-white/50 hover:text-white"
              >
                <X size={18} />
              </button>
            </div>

            <p className="text-xs text-white/50">
              Generate a production-ready masking script with error handling,
              logging, validation, tests, and deployment helpers.
            </p>

            {/* Language */}
            <div>
              <label className="block text-xs text-white/55 mb-2">
                Language / Framework
              </label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  {
                    id: "pandas" as const,
                    label: "Python (pandas)",
                    desc: "Standard, single-machine",
                    icon: <FileCode size={16} />,
                  },
                  {
                    id: "pyspark" as const,
                    label: "PySpark",
                    desc: "Distributed, large datasets",
                    icon: <Zap size={16} />,
                  },
                  {
                    id: "snowpark" as const,
                    label: "Snowpark",
                    desc: "Native Snowflake execution",
                    icon: <Snowflake size={16} />,
                  },
                ].map((lang) => (
                  <button
                    key={lang.id}
                    onClick={() => setScriptLanguage(lang.id)}
                    className={`p-3 rounded-xl border text-left transition-all ${
                      scriptLanguage === lang.id
                        ? "bg-purple-500/15 border-purple-500/40 text-purple-300"
                        : "bg-surface-200 border-white/5 text-white/55 hover:text-white/60 hover:border-white/10"
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      {lang.icon}{" "}
                      <span className="text-xs font-medium">{lang.label}</span>
                    </div>
                    <p className="text-[11px] opacity-60">{lang.desc}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Deployment target */}
            <div>
              <label className="block text-xs text-white/55 mb-2">
                Deployment Target
              </label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  {
                    id: "ec2" as const,
                    label: "EC2 / Local",
                    desc: "Server or laptop",
                    icon: <Server size={16} />,
                  },
                  {
                    id: "lambda" as const,
                    label: "AWS Lambda",
                    desc: "Serverless, event-driven",
                    icon: <Cloud size={16} />,
                  },
                  {
                    id: "snowflake" as const,
                    label: "Snowflake",
                    desc: "In-warehouse processing",
                    icon: <Snowflake size={16} />,
                  },
                ].map((tgt) => (
                  <button
                    key={tgt.id}
                    onClick={() => setScriptTarget(tgt.id)}
                    className={`p-3 rounded-xl border text-left transition-all ${
                      scriptTarget === tgt.id
                        ? "bg-purple-500/15 border-purple-500/40 text-purple-300"
                        : "bg-surface-200 border-white/5 text-white/55 hover:text-white/60 hover:border-white/10"
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      {tgt.icon}{" "}
                      <span className="text-xs font-medium">{tgt.label}</span>
                    </div>
                    <p className="text-[11px] opacity-60">{tgt.desc}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Custom Instructions / User Prompt — only available when masking rules are configured */}
            {_collectAllColumnConfigs().length > 0 && uploadResponse ? (
              <div className="bg-surface-200 rounded-xl p-3 space-y-2">
                <p className="text-[11px] font-medium text-cyan-400 flex items-center gap-1.5">
                  <FileText size={12} /> Custom Instructions
                </p>
                <p className="text-[11px] text-white/50">
                  Add any custom notes, requirements, or feedback. These will be
                  embedded as comments in the generated script header and
                  included in the README documentation.
                </p>
                <textarea
                  value={scriptUserInstructions}
                  onChange={(e) => setScriptUserInstructions(e.target.value)}
                  placeholder="e.g., Ensure all PII columns are masked before loading to production. Run on weekdays only via cron schedule. Contact data-eng@acme.com for key rotation..."
                  rows={3}
                  className="w-full bg-surface-300 border border-white/10 rounded-lg px-3 py-2 text-[11px] text-white placeholder:text-white/50 resize-y focus:outline-none focus:border-cyan-500/30 transition"
                />
                {scriptUserInstructions.trim() && (
                  <>
                    {/* AI-Powered Generation toggle */}
                    <div className="flex items-center justify-between bg-surface-300/60 rounded-lg px-3 py-2 border border-white/5">
                      <div className="flex items-center gap-2">
                        <div
                          className={`w-5 h-5 rounded-md flex items-center justify-center ${scriptUseLLM ? "bg-violet-500/20 text-violet-400" : "bg-white/5 text-white/50"}`}
                        >
                          <Sparkles size={11} />
                        </div>
                        <div>
                          <p className="text-[11px] font-medium text-white/80">
                            AI-Powered Generation
                          </p>
                          <p className="text-[11px] text-white/50">
                            Use Claude to generate a fully custom script
                            tailored to your instructions
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          const next = !scriptUseLLM;
                          setScriptUseLLM(next);
                          if (next) fetchTokenEstimate();
                          else setTokenEstimate(null);
                        }}
                        className={`relative w-9 h-5 rounded-full transition-colors ${scriptUseLLM ? "bg-violet-500" : "bg-white/10"}`}
                      >
                        <div
                          className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${scriptUseLLM ? "translate-x-4" : "translate-x-0.5"}`}
                        />
                      </button>
                    </div>
                    {scriptUseLLM ? (
                      <div className="space-y-2">
                        <div className="flex items-center gap-1.5 text-[11px] text-violet-400/80">
                          <Sparkles size={10} /> Claude will generate a fully
                          custom script with tests based on your instructions
                        </div>
                        {/* Token estimate */}
                        {tokenEstimateLoading ? (
                          <div className="flex items-center gap-1.5 text-[11px] text-white/50">
                            <RefreshCw size={10} className="animate-spin" />{" "}
                            Estimating token usage…
                          </div>
                        ) : (
                          tokenEstimate && (
                            <div className="bg-violet-500/10 border border-violet-500/20 rounded-lg px-3 py-2 space-y-1">
                              <p className="text-[11px] font-medium text-violet-300">
                                Estimated Token Usage
                              </p>
                              <div className="grid grid-cols-3 gap-2 text-[11px]">
                                <div>
                                  <span className="text-white/50">Input: </span>
                                  <span className="text-violet-300 font-mono">
                                    {tokenEstimate.estimated_input_tokens.toLocaleString()}
                                  </span>
                                </div>
                                <div>
                                  <span className="text-white/50">
                                    Output:{" "}
                                  </span>
                                  <span className="text-violet-300 font-mono">
                                    ~
                                    {tokenEstimate.estimated_output_tokens.toLocaleString()}
                                  </span>
                                </div>
                                <div>
                                  <span className="text-white/50">Total: </span>
                                  <span className="text-violet-300 font-mono">
                                    ~
                                    {tokenEstimate.estimated_total_tokens.toLocaleString()}
                                  </span>
                                </div>
                              </div>
                              <p className="text-[11px] text-white/50">
                                Model: {tokenEstimate.model} ·{" "}
                                {tokenEstimate.num_columns} col
                                {tokenEstimate.num_columns !== 1
                                  ? "s"
                                  : ""} · {tokenEstimate.num_techniques}{" "}
                                technique
                                {tokenEstimate.num_techniques !== 1 ? "s" : ""}
                              </p>
                            </div>
                          )
                        )}
                      </div>
                    ) : (
                      <div className="flex items-center gap-1.5 text-[11px] text-cyan-400/60">
                        <CheckCircle2 size={10} /> Instructions will be embedded
                        in script &amp; README
                      </div>
                    )}
                  </>
                )}
              </div>
            ) : (
              <div className="bg-surface-200 rounded-xl p-3 space-y-1 opacity-50">
                <p className="text-[11px] font-medium text-white/50 flex items-center gap-1.5">
                  <FileText size={12} /> Custom Instructions
                </p>
                <p className="text-[11px] text-white/50">
                  Configure masking rules for at least one column to unlock
                  Custom Instructions and AI-Powered Generation.
                </p>
              </div>
            )}

            {/* Multi-sheet info badge */}
            {isMultiSheet && (
              <div className="bg-purple-500/10 border border-purple-500/20 rounded-xl p-3">
                <div className="flex items-center gap-2 text-[11px] text-purple-300">
                  <Layers size={12} />
                  <span className="font-medium">Multi-Sheet Mode</span>
                  <span className="text-white/50">·</span>
                  <span className="text-white/50">
                    {selectedSheets.size} sheets,{" "}
                    {_collectAllColumnConfigs().length} columns configured
                  </span>
                </div>
                <p className="text-[11px] text-white/50 mt-1 pl-5">
                  Generated script will process all configured sheets from the
                  Excel file with per-sheet masking rules.
                </p>
              </div>
            )}

            {/* Advanced Options Toggle */}
            <button
              onClick={() => setScriptShowAdvanced(!scriptShowAdvanced)}
              className="w-full flex items-center justify-between px-3 py-2 bg-surface-200 hover:bg-surface-300 rounded-xl text-xs text-white/55 hover:text-white/60 transition"
            >
              <span className="flex items-center gap-2">
                <Settings size={14} /> Advanced Options
              </span>
              <ChevronDown
                size={14}
                className={`transition-transform ${scriptShowAdvanced ? "rotate-180" : ""}`}
              />
            </button>

            {scriptShowAdvanced && (
              <div className="space-y-4 pl-1">
                {/* ── Error Handling ── */}
                <div className="bg-surface-200 rounded-xl p-3 space-y-2">
                  <p className="text-[11px] font-medium text-white/55 flex items-center gap-1.5">
                    <AlertTriangle size={12} /> Error Handling
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => setScriptErrorStrategy("fail_fast")}
                      className={`px-3 py-1.5 rounded-lg text-[11px] transition border ${
                        scriptErrorStrategy === "fail_fast"
                          ? "bg-red-500/15 border-red-500/30 text-red-300"
                          : "bg-surface-300 border-white/5 text-white/50 hover:text-white/50"
                      }`}
                    >
                      Fail Fast
                    </button>
                    <button
                      onClick={() => setScriptErrorStrategy("log_continue")}
                      className={`px-3 py-1.5 rounded-lg text-[11px] transition border ${
                        scriptErrorStrategy === "log_continue"
                          ? "bg-amber-500/15 border-amber-500/30 text-amber-300"
                          : "bg-surface-300 border-white/5 text-white/50 hover:text-white/50"
                      }`}
                    >
                      Log & Continue
                    </button>
                  </div>
                  {scriptErrorStrategy === "log_continue" && (
                    <div className="flex items-center gap-2">
                      <label className="text-[11px] text-white/50 whitespace-nowrap">
                        Max errors (0 = unlimited):
                      </label>
                      <input
                        type="number"
                        min={0}
                        value={scriptMaxErrors}
                        onChange={(e) =>
                          setScriptMaxErrors(
                            Math.max(0, parseInt(e.target.value) || 0),
                          )
                        }
                        className="w-20 bg-surface-300 border border-white/10 rounded px-2 py-1 text-[11px] text-white"
                      />
                    </div>
                  )}
                </div>

                {/* ── Logging ── */}
                <div className="bg-surface-200 rounded-xl p-3 space-y-2">
                  <p className="text-[11px] font-medium text-white/55 flex items-center gap-1.5">
                    <Info size={12} /> Logging & Observability
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-[11px] text-white/50 block mb-1">
                        Log Level
                      </label>
                      <select
                        value={scriptLogLevel}
                        onChange={(e) =>
                          setScriptLogLevel(e.target.value as any)
                        }
                        className="w-full bg-surface-300 border border-white/10 rounded px-2 py-1 text-[11px] text-white"
                      >
                        <option value="DEBUG">DEBUG</option>
                        <option value="INFO">INFO</option>
                        <option value="WARNING">WARNING</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-[11px] text-white/50 block mb-1">
                        Log Output
                      </label>
                      <select
                        value={scriptLogOutput}
                        onChange={(e) =>
                          setScriptLogOutput(e.target.value as any)
                        }
                        className="w-full bg-surface-300 border border-white/10 rounded px-2 py-1 text-[11px] text-white"
                      >
                        <option value="console">Console</option>
                        <option value="file">File</option>
                        <option value="both">Both</option>
                      </select>
                    </div>
                  </div>
                  <label className="flex items-center gap-2 text-[11px] text-white/50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={scriptIncludeSummary}
                      onChange={(e) =>
                        setScriptIncludeSummary(e.target.checked)
                      }
                      className="rounded border-white/20"
                    />
                    Write execution_summary.json after masking
                  </label>
                </div>

                {/* ── Data Validation ── */}
                <div className="bg-surface-200 rounded-xl p-3 space-y-2">
                  <p className="text-[11px] font-medium text-white/55 flex items-center gap-1.5">
                    <ShieldCheck size={12} /> Data Validation
                  </p>
                  <div className="space-y-1">
                    <label className="flex items-center gap-2 text-[11px] text-white/50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={scriptPreValidation}
                        onChange={(e) =>
                          setScriptPreValidation(e.target.checked)
                        }
                        className="rounded border-white/20"
                      />
                      Pre-masking validation (column presence, null %)
                    </label>
                    <label className="flex items-center gap-2 text-[11px] text-white/50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={scriptPostValidation}
                        onChange={(e) =>
                          setScriptPostValidation(e.target.checked)
                        }
                        className="rounded border-white/20"
                      />
                      Post-masking validation (PII leakage spot-check)
                    </label>
                  </div>
                  <div>
                    <label className="text-[11px] text-white/50 block mb-1">
                      Null Handling
                    </label>
                    <div className="grid grid-cols-3 gap-1">
                      {[
                        {
                          id: "skip" as const,
                          label: "Skip",
                          desc: "Leave nulls as-is",
                        },
                        {
                          id: "placeholder" as const,
                          label: "Placeholder",
                          desc: "Fill with N/A",
                        },
                        {
                          id: "error" as const,
                          label: "Error",
                          desc: "Fail on nulls",
                        },
                      ].map((nh) => (
                        <button
                          key={nh.id}
                          onClick={() => setScriptNullHandling(nh.id)}
                          className={`px-2 py-1 rounded text-[11px] transition border ${
                            scriptNullHandling === nh.id
                              ? "bg-brand-500/15 border-brand-500/30 text-brand-400"
                              : "bg-surface-300 border-white/5 text-white/50 hover:text-white/55"
                          }`}
                          title={nh.desc}
                        >
                          {nh.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* ── Testing ── */}
                <div className="bg-surface-200 rounded-xl p-3 space-y-1">
                  <p className="text-[11px] font-medium text-white/55 flex items-center gap-1.5">
                    <TestTube2 size={12} /> Testing
                  </p>
                  <label className="flex items-center gap-2 text-[11px] text-white/50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={scriptIncludeTests}
                      onChange={(e) => {
                        setScriptIncludeTests(e.target.checked);
                        if (e.target.checked) {
                          setScriptIncludeTestRunner(true);
                          setScriptIncludeSampleData(true);
                        }
                      }}
                      className="rounded border-white/20"
                    />
                    Include unit tests (test_masking.py)
                  </label>
                  <label className="flex items-center gap-2 text-[11px] text-white/50 cursor-pointer pl-4">
                    <input
                      type="checkbox"
                      checked={scriptIncludeSampleData}
                      onChange={(e) =>
                        setScriptIncludeSampleData(e.target.checked)
                      }
                      className="rounded border-white/20"
                      disabled={!scriptIncludeTests}
                    />
                    Include sample test data (sample_input.csv)
                  </label>
                  <label className="flex items-center gap-2 text-[11px] text-white/50 cursor-pointer pl-4">
                    <input
                      type="checkbox"
                      checked={scriptIncludeTestRunner}
                      onChange={(e) =>
                        setScriptIncludeTestRunner(e.target.checked)
                      }
                      className="rounded border-white/20"
                      disabled={!scriptIncludeTests}
                    />
                    Include test runner (run_tests.sh)
                  </label>
                </div>

                {/* ── Documentation ── */}
                <div className="bg-surface-200 rounded-xl p-3 space-y-1">
                  <p className="text-[11px] font-medium text-white/55 flex items-center gap-1.5">
                    <FileText size={12} /> Documentation
                  </p>
                  <label className="flex items-center gap-2 text-[11px] text-white/50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={scriptIncludeCodeReview}
                      onChange={(e) =>
                        setScriptIncludeCodeReview(e.target.checked)
                      }
                      className="rounded border-white/20"
                    />
                    Code review checklist (CODE_REVIEW.md)
                  </label>
                  <label className="flex items-center gap-2 text-[11px] text-white/50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={scriptIncludeChangelog}
                      onChange={(e) =>
                        setScriptIncludeChangelog(e.target.checked)
                      }
                      className="rounded border-white/20"
                    />
                    Changelog template (CHANGELOG.md)
                  </label>
                </div>

                {/* ── Production Controls ── */}
                <div className="bg-surface-200 rounded-xl p-3 space-y-2">
                  <p className="text-[11px] font-medium text-purple-400 flex items-center gap-1.5">
                    <Sparkles size={12} /> Production Controls
                  </p>
                  <label className="flex items-center gap-2 text-[11px] text-white/50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={scriptLineageEnabled}
                      onChange={(e) =>
                        setScriptLineageEnabled(e.target.checked)
                      }
                      className="rounded border-white/20"
                    />
                    <Link2 size={10} className="text-brand-500/50" />
                    Masking lineage (deterministic value mapping)
                  </label>
                  <label className="flex items-center gap-2 text-[11px] text-white/50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={scriptIncludeConditionalMasking}
                      onChange={(e) =>
                        setScriptIncludeConditionalMasking(e.target.checked)
                      }
                      className="rounded border-white/20"
                    />
                    <Filter size={10} className="text-amber-500/50" />
                    Conditional masking (filter expressions)
                  </label>
                  <label className="flex items-center gap-2 text-[11px] text-white/50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={scriptIncludeUniqueness}
                      onChange={(e) =>
                        setScriptIncludeUniqueness(e.target.checked)
                      }
                      className="rounded border-white/20"
                    />
                    <ClipboardCheck size={10} className="text-green-500/50" />
                    Uniqueness preservation (dedup after masking)
                  </label>
                  <label className="flex items-center gap-2 text-[11px] text-white/50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={scriptIncludeFormatValidation}
                      onChange={(e) =>
                        setScriptIncludeFormatValidation(e.target.checked)
                      }
                      className="rounded border-white/20"
                    />
                    <ShieldCheck size={10} className="text-blue-500/50" />
                    Format validation (regex checks post-mask)
                  </label>
                  <label className="flex items-center gap-2 text-[11px] text-white/50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={scriptIncludeAuditLog}
                      onChange={(e) =>
                        setScriptIncludeAuditLog(e.target.checked)
                      }
                      className="rounded border-white/20"
                    />
                    <FileText size={10} className="text-orange-500/50" />
                    Audit log (masking_audit.json output)
                  </label>
                </div>
              </div>
            )}

            {/* What's included */}
            <div className="bg-surface-200 rounded-xl p-3">
              <p className="text-[11px] text-white/50 mb-2 font-medium">
                ZIP package will include:
              </p>
              <div className="grid grid-cols-2 gap-1 text-[11px] text-white/50">
                <span>✓ mask.py — Main masking script</span>
                <span>✓ masking_config.json — Config</span>
                <span>✓ requirements.txt — Dependencies</span>
                <span>✓ README.md — Full documentation</span>
                {scriptTarget === "lambda" && (
                  <span>✓ deploy/deploy_lambda.sh</span>
                )}
                {scriptTarget === "snowflake" && (
                  <span>✓ deploy/setup_snowflake.sql</span>
                )}
                {scriptIncludeTests && (
                  <span>✓ test_masking.py — Unit tests</span>
                )}
                {scriptIncludeSampleData && (
                  <span>✓ sample_input.csv — Test data</span>
                )}
                {scriptIncludeTestRunner && (
                  <span>✓ run_tests.sh — Test runner</span>
                )}
                {scriptIncludeCodeReview && (
                  <span>✓ CODE_REVIEW.md — Review checklist</span>
                )}
                {scriptIncludeChangelog && (
                  <span>✓ CHANGELOG.md — Change log</span>
                )}
                {scriptIncludeSummary && (
                  <span>✓ execution_summary.json (on run)</span>
                )}
                {(scriptLineageEnabled || lineageEnabled) && (
                  <span>✓ Lineage mapping (deterministic)</span>
                )}
                {scriptIncludeAuditLog && (
                  <span>✓ masking_audit.json (on run)</span>
                )}
                {scriptUserInstructions.trim() && !scriptUseLLM && (
                  <span>✓ Custom instructions embedded</span>
                )}
                {scriptUserInstructions.trim() && scriptUseLLM && (
                  <span className="text-violet-400/60">
                    ✦ AI-generated script & tests
                  </span>
                )}
                {isMultiSheet && <span>✓ Multi-sheet Excel processing</span>}
              </div>
            </div>

            {scriptError && (
              <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-xs text-red-300 flex items-center gap-2">
                <AlertTriangle size={14} /> {scriptError}
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={() => {
                  setShowScriptModal(false);
                  setScriptError(null);
                }}
                className="px-4 py-2 bg-surface-200 hover:bg-surface-300 rounded-lg text-xs text-white/55 hover:text-white transition"
              >
                Cancel
              </button>
              <button
                data-testid="masking-generate-script-confirm"
                onClick={handleGenerateScript}
                disabled={scriptGenerating}
                className={`px-6 py-2 ${scriptUseLLM && scriptUserInstructions.trim() ? "bg-violet-500 hover:bg-violet-600" : "bg-purple-500 hover:bg-purple-600"} disabled:opacity-50 rounded-lg text-xs font-medium text-white transition flex items-center gap-2`}
              >
                {scriptGenerating ? (
                  <>
                    <RefreshCw size={12} className="animate-spin" />{" "}
                    {scriptUseLLM && scriptUserInstructions.trim()
                      ? "AI Generating…"
                      : "Generating..."}
                  </>
                ) : scriptUseLLM && scriptUserInstructions.trim() ? (
                  <>
                    <Sparkles size={12} /> AI Generate & Download ZIP
                  </>
                ) : (
                  <>
                    <Download size={12} /> Generate & Download ZIP
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Save success toast */}
      {saveSuccess && (
        <div className="fixed bottom-6 right-6 z-50 px-4 py-3 bg-green-500/20 border border-green-500/30 rounded-xl text-sm text-green-400 flex items-center gap-2 backdrop-blur-xl">
          <CheckCircle2 size={16} /> {saveSuccess}
        </div>
      )}

      {/* LLM token usage toast */}
      {lastTokenUsage && (
        <div className="fixed bottom-6 right-6 z-50 px-4 py-3 bg-violet-500/20 border border-violet-500/30 rounded-xl backdrop-blur-xl space-y-1 max-w-sm">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2 text-sm text-violet-300 font-medium">
              <Sparkles size={14} /> AI Script Generated
            </div>
            <button
              onClick={() => setLastTokenUsage(null)}
              className="text-white/50 hover:text-white/50 transition"
            >
              ✕
            </button>
          </div>
          <div className="flex items-center gap-3 text-[11px]">
            <span className="text-white/50">Model:</span>
            <span className="text-violet-300 font-mono text-[11px]">
              {lastTokenUsage.model}
            </span>
          </div>
          <div className="flex items-center gap-4 text-[11px]">
            <div>
              <span className="text-white/50">In: </span>
              <span className="text-violet-300 font-mono">
                {lastTokenUsage.input_tokens.toLocaleString()}
              </span>
            </div>
            <div>
              <span className="text-white/50">Out: </span>
              <span className="text-violet-300 font-mono">
                {lastTokenUsage.output_tokens.toLocaleString()}
              </span>
            </div>
            <div>
              <span className="text-white/50">Total: </span>
              <span className="text-violet-300 font-mono">
                {lastTokenUsage.total_tokens.toLocaleString()}
              </span>
            </div>
          </div>
        </div>
      )}

      <ConnectDatabaseDialog
        open={showSharedConnectDialog}
        onClose={() => setShowSharedConnectDialog(false)}
        title="Connect a Saved Database"
        projectId={projectId}
        agentKey="data_guardian"
        extraField={{
          key: "table",
          label: "Table (optional)",
          value: dbParams.table || "",
          onChange: (v) =>
            setDbParams((p) => ({ ...p, table: v || undefined })),
          placeholder: "Leave blank to list all",
        }}
        testIdPrefix="masking-saved"
        onConnectExisting={handleSharedDialogConnectExisting}
        onConnectNew={handleSharedDialogConnectNew}
      />
    </div>
  );
}
